import React from "react";
import {
  AppBar, Toolbar, Typography, Container, Box, Grid, Card, CardContent, CardActions,
  IconButton, Button, Badge, Drawer, List, ListItem,
  Avatar, Tabs, Tab, TextField, InputAdornment, Snackbar, Alert,
  CircularProgress, Stack, Skeleton, CssBaseline, GlobalStyles,
  Checkbox, FormControlLabel, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { ShoppingCart, Add, Remove, Search, Send, Tune, Close, InfoOutlined, LockOutlined } from "@mui/icons-material";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";

import { explainBackendError, validateOrderPayload, cents, fromc } from "./components/validationHelpers";
import PaymentSheet from "./components/PaymentSheet";
import ComboPickerSheet from "./components/ComboPickerSheet";
import OrderSummary from "./components/OrderSummary";
import ProductInfoModal from "./components/ProductInfoModal";
import BrandedImageFallback from "./components/BrandedImageFallback";
import ClientCheckoutAuthPanel from "./components/ClientCheckoutAuthPanel";
import Config from "./components/ClientConfig";
import OptimizedConfigImage from "./components/OptimizedConfigImage";
import { useDataStore } from "./dataStore";
import { applyEntityTranslation, withLanguageParam } from "./i18n";
import { brandThemeCssVars, DEFAULT_BRAND_THEME, normalizeBrandTheme } from "./themeTokens";
import { useClientPortal } from "./clientPortal/ClientPortalContext";
import { adaptAuthenticatedOrderPayload } from "./clientPortal/orderBenefitAdapter";
import { toSafePaymentRedirectUrl } from "./paymentUrl";
import { withAppBasePath } from "./app/runtimeConfig";

function apiUrl(path) {
  if (!path) return path;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = String(Config.BASE_URL || "").replace(/\/$/, "");
  const cleanPath = String(path).startsWith("/") ? path : `/${path}`;
  return `${base}${cleanPath}`;
}
function getParam(name) {
  return new URLSearchParams(window.location.search).get(name);
}
let tokenFromUrl = getParam("token") || null;

const DEFAULT_BRAND_NAME = "Restaurant";

const ENDPOINTS = {
  location: (loc) => `${Config.API_URL_LOCATIONS}${encodeURIComponent(loc)}`,
  locations: () => `${Config.API_URL_LOCATIONS}list_locations/`,
  priceList: (loc, deliveryMode = false) => `${Config.BASE_API_URL}priceList/?location=${encodeURIComponent(loc)}${deliveryMode ? "&withVAT=true&currency=0" : ""}`,
  promotionList: (loc, deliveryMode = false) => `${Config.BASE_API_URL}promotionList/?location=${encodeURIComponent(loc)}${deliveryMode ? "&withVAT=true&currency=0" : ""}`,
  products: (language) => withLanguageParam(`${Config.API_URL_PRODUCT}list_products/`, language),
  vats: () => `${Config.API_URL_PRODUCT}list_vats/`,
  combos: (language) => withLanguageParam(`${Config.BASE_API_URL}combo/list_combos/`, language),
  categories: (language) => withLanguageParam(`${Config.API_URL_PRODUCT_CATEGORY}`, language),

  startSkiplineSession: () => `${Config.API_URL_ORDERS}startSkiplineSession/`,
  startSkiplineSessionAlt: () => `${Config.BASE_API_URL}order/startSkiplineSession/`,

  createOrder: () => `${Config.API_URL_ORDERS}add/`,
  registerPay: () => `${Config.API_URL_ORDERS}registerPay/`,
  orderPayStatus: (id) => `${Config.API_URL_ORDERS}getPayStatus/?iref=${encodeURIComponent(id)}`,
  cancelOrder: (id) => `${Config.API_URL_ORDERS}cancelOrder/?iref=${encodeURIComponent(id)}`,
  skiplineOptions: () => `${Config.API_URL_ORDERS}skiplineOptions/`,
  skiplineAddressSearch: () => `${Config.API_URL_ORDERS}skiplineAddressSearch/`,
  skiplineResolveDelivery: () => `${Config.API_URL_ORDERS}skiplineResolveDelivery/`,
  geoStructure: () => `${Config.BASE_API_URL}address/getGeoStructure/`,
};

const PICKUP_SERVE_TYPES = new Set([2, 4, 6, 9]);

export function normalizeRomanianPhone(value) {
  const raw = String(value || "").replace(/[\s().-]/g, "");
  if (/^0[237]\d{8}$/.test(raw)) return `+40${raw.slice(1)}`;
  if (/^40[237]\d{8}$/.test(raw)) return `+${raw}`;
  const phone = raw.startsWith("00") ? `+${raw.slice(2)}` : raw;
  if (/^\+40[237]\d{8}$/.test(phone)) return phone;
  if (!phone.startsWith("+40") && /^\+[1-9][0-9]{6,11}$/.test(phone)) return phone;
  return null;
}

function hasFullRecipientName(value) {
  return String(value || "").trim().split(/\s+/).filter(Boolean).length >= 2;
}

export function deliveryCharge(subtotal, fee, threshold) {
  const subtotalC = toCents(subtotal);
  const thresholdC = toCents(threshold);
  const feeC = toCents(fee);
  if (![subtotalC, thresholdC, feeC].every(Number.isFinite) || feeC < 0) {
    return null;
  }
  return {
    fee: subtotalC >= thresholdC ? 0 : fromCents(feeC),
    remaining: thresholdC > 0 ? fromCents(Math.max(0, thresholdC - subtotalC)) : 0,
  };
}

export const ORDER_REQUEST_CONTEXT = Object.freeze({
  guest: "guest",
  client: "client",
});

export function selectOrderRequestContext({
  authenticatedCheckoutEnabled,
  isAuthenticated,
}) {
  return authenticatedCheckoutEnabled && isAuthenticated
    ? ORDER_REQUEST_CONTEXT.client
    : ORDER_REQUEST_CONTEXT.guest;
}

export function extractCreatedOrderId(response, context) {
  if (context === ORDER_REQUEST_CONTEXT.client) {
    return response?.data?.order ?? null;
  }
  return (
    response?.data?.order ??
    response?.order ??
    response?.iref ??
    response?.data?.iref ??
    null
  );
}

export function extractServerOrderTotal(response) {
  const value = response?.data?.totalAmount;
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function extractRedirectPaymentUrl(response, context) {
  let raw;
  if (context === ORDER_REQUEST_CONTEXT.client) {
    raw = response?.data?.payUrl;
  } else {
    raw =
      response?.data?.payUrl ||
      response?.data?.url ||
      response?.data?.paymentUrl ||
      response?.payUrl ||
      response?.url ||
      response?.paymentUrl;
  }
  return toSafePaymentRedirectUrl(String(raw || ""));
}

let skiplineAccessToken = tokenFromUrl;

function getSafeStorage(name) {
  try {
    if (typeof window === "undefined") return null;

    const storage = window[name];
    const testKey = "__skipline_storage_test__";

    storage.setItem(testKey, "1");
    storage.removeItem(testKey);

    return storage;
  } catch (e) {
    return null;
  }
}

function safeStorageGet(name, key) {
  const storage = getSafeStorage(name);
  if (!storage) return null;

  try {
    return storage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeStorageSet(name, key, value) {
  const storage = getSafeStorage(name);
  if (!storage) return;

  try {
    storage.setItem(key, value);
  } catch (e) {
    // Storage can be blocked in embedded/private browser contexts.
  }
}

function safeStorageRemove(name, key) {
  const storage = getSafeStorage(name);
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch (e) {
    // Storage can be blocked in embedded/private browser contexts.
  }
}

function setSkiplineAccessToken(token) {
  skiplineAccessToken = token || null;

  if (token) {
    safeStorageSet("sessionStorage", "skiplineAccessToken", token);
  } else {
    safeStorageRemove("sessionStorage", "skiplineAccessToken");
    if (tokenFromUrl && typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("token");
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    }
    tokenFromUrl = null;
  }
}

function guestTokenExpiryMs(token) {
  try {
    const encoded = String(token || "").split(".")[1];
    if (!encoded || typeof atob !== "function") return null;
    const padded = encoded.replace(/-/g, "+").replace(/_/g, "/")
      .padEnd(Math.ceil(encoded.length / 4) * 4, "=");
    const expiry = Number(JSON.parse(atob(padded)).exp);
    return Number.isFinite(expiry) && expiry > 0 ? expiry * 1000 : null;
  } catch {
    return null;
  }
}

export function authHeaders() {
  const h = { "Content-Type": "application/json" };

  const token =
    skiplineAccessToken ||
    safeStorageGet("sessionStorage", "skiplineAccessToken") ||
    tokenFromUrl;

  if (token) {
    h.Authorization = `Bearer ${token}`;
  }

  return h;
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

function money(n,currency="RON"){ if(typeof n!=="number"||Number.isNaN(n)) return "-"; return new Intl.NumberFormat("ro-RO",{style:"currency",currency,maximumFractionDigits:2}).format(n); }
function ensureSmallImage(path) {
  if (!path) return null;
  const base = Config.BASE_URL;
  const abs = path.startsWith("http") ? path : `${base}${path}`;
  const i = abs.lastIndexOf(".");
  return i === -1 ? abs : `${abs.slice(0, i)}_s${abs.slice(i)}`;
}
function arrify(raw,keys=[]){ if(!raw) return []; if(Array.isArray(raw)) return raw; for(let k of keys){ if(Array.isArray(raw[k])) return raw[k]; } if(Array.isArray(raw.results)) return raw.results; if(Array.isArray(raw.data)) return raw.data; if(Array.isArray(raw.items)) return raw.items; return []; }
function toCents(x){ return Math.round(Number(x)*100); }
function fromCents(c){ return Math.round(c)/100; }

const LEGACY_VAT_RATES_BY_ID = Object.freeze({
  "16ef0e4256": 21,
  "27554c6052": 0,
  "ad0aa21a47": 11,
});

function toVatRate(value) {
  if (value == null || value === "") return null;
  const rate = Number(value);
  return Number.isFinite(rate) && rate >= 0 && rate <= 100 ? rate : null;
}

export function buildVatRateMap(raw) {
  const rates = {};
  arrify(raw, ["VATS", "vats", "vat"]).forEach((item) => {
    const id = item?.iref ?? item?.id;
    const rate = toVatRate(item?.sell ?? item?.rate);
    if (id != null && rate != null) {
      rates[String(id)] = rate;
    }
  });
  return rates;
}

export function normalizeVatDefinition(
  rawVat,
  vatRatesById = {},
  explicitRate = null
) {
  const isObject = rawVat != null && typeof rawVat === "object";
  const rawId = isObject ? (rawVat.iref ?? rawVat.id) : rawVat;
  const vatId = rawId == null || rawId === "" ? null : String(rawId);
  const embeddedRate = isObject
    ? toVatRate(rawVat.sell ?? rawVat.rate)
    : null;
  const vatRate =
    embeddedRate ??
    toVatRate(explicitRate) ??
    (vatId != null ? toVatRate(vatRatesById?.[vatId]) : null) ??
    (vatId != null ? toVatRate(LEGACY_VAT_RATES_BY_ID[vatId]) : null);

  return { vatId, vatRate };
}

function buildEffectiveMaps(pl, promoRaw) {
  const promoObj = promoRaw?.promotionList || promoRaw || {};
  const promoByProductId = {};
  (arrify(promoObj, ["productPromotion_list"]) || []).forEach(l => {
    if (l?.isActive !== false && l?.product && l?.value != null &&
        l.value !== "" && Number.isFinite(Number(l.value))) {
      promoByProductId[l.product] = Number(l.value);
    }
  });
  const promoByComboId = {};
  (arrify(promoObj, ["comboPromotion_list"]) || []).forEach(l => {
    if (l?.isActive !== false && l?.productCombo && l?.value != null &&
        l.value !== "" && Number.isFinite(Number(l.value))) {
      promoByComboId[l.productCombo] = Number(l.value);
    }
  });

  const effectiveProd = { ...(pl?.priceByProductId || {}) };
  Object.keys(promoByProductId).forEach(pid => { effectiveProd[pid] = promoByProductId[pid]; });

  const effectiveCombo = { ...(pl?.priceByComboId || {}) };
  Object.keys(promoByComboId).forEach(cid => { effectiveCombo[cid] = promoByComboId[cid]; });

  return { promoByProductId, promoByComboId, effectiveProd, effectiveCombo };
}

function parseHMS(hms) { // "HH:MM:SS" -> minutes from midnight
  if (!hms) return null;
  const [H, M, S] = hms.split(":").map(Number);
  return (H * 60) + (M || 0) + Math.floor((S || 0) / 60);
}

function fmtHM(mins) { // 780 -> "13:00"
  if (mins == null) return "--:--";
  let h = Math.floor(mins / 60);
  let m = mins % 60;
  return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
}

// Map JS getDay() (0..6, Sunday=0) to API day (1..7, Monday..Sunday)
function apiWeekdayFromDate(d = new Date()) {
  const js = d.getDay();        // 0..6
  return js === 0 ? 7 : js;     // 1..7
}

function evalLocationOpen(info, now = new Date(), t = (key, fallback) => fallback || key) {
  if (!info) return { openNow:false, reason:t("skipline.noLocationInfo") };

  // status & serveType
  const allowedServe = PICKUP_SERVE_TYPES;
  if (info.status !== 1) return { openNow:false, reason:t("skipline.inactiveLocation") };
  if (!allowedServe.has(info.serveType)) return { openNow:false, reason:t("skipline.unsupportedServeType") };

  const day = apiWeekdayFromDate(now);
  const schedules = Array.isArray(info.locationSchedule_location) ? info.locationSchedule_location : [];
  const todayIntervals = schedules.filter(x => x.day === day);

  if (!todayIntervals.length) return { openNow:false, reason:t("skipline.noTodaySchedule") };

  const openDelta = Number(info.orderOpenDeltaMin || 0);
  const closeDelta = Number(info.orderCloseDeltaMin || 0);
  const nowMins = now.getHours() * 60 + now.getMinutes();

  // Check any interval open
  let openNow = false;
  let todayOpen = null;
  let todayClose = null;
  for (const it of todayIntervals) {
    const start = parseHMS(it.interval_start);
    const end   = parseHMS(it.interval_end);
    if (start == null || end == null) continue;

    const adjStart = Math.max(0, start + openDelta);
    const adjEnd   = Math.max(adjStart, end + closeDelta); // closeDelta is less than 0

    // store the window (first interval for display)
    if (todayOpen == null || adjStart < todayOpen) todayOpen = adjStart;
    if (todayClose == null || adjEnd   > todayClose) todayClose = adjEnd;

    if (nowMins >= adjStart && nowMins < adjEnd) {
      openNow = true;
    }
  }

  const reason = openNow
    ? ""
    : t("skipline.closedSchedule")
      .replace("{open}", fmtHM(todayOpen))
      .replace("{close}", fmtHM(todayClose));

  return { openNow, todayOpen, todayClose, reason };
}

const NET_TIMEOUT_MS = 12000;
const withTimeout = (p, ms=NET_TIMEOUT_MS) =>
  Promise.race([p, new Promise((_,rej)=>setTimeout(()=>rej(new Error("Timeout")), ms))]);

class ApiClient {
  async get(url, { publicRequest = false } = {}) {
    const fullUrl = apiUrl(url);

    try {
      const r = await withTimeout(fetch(fullUrl, {
        headers: publicRequest ? {} : authHeaders(),
      }));
      const text = await r.text();

      if (!r.ok) throw new Error(`${r.status}: ${text.slice(0, 200)}`);

      try {
        return JSON.parse(text);
      } catch {
        throw new Error(`Invalid JSON from ${fullUrl}: ${text.slice(0, 200)}`);
      }
    } catch {
      return null;
    }
  }

  async post(url, body) {
    const fullUrl = apiUrl(url);

    const r = await fetch(fullUrl, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    });

    const text = await r.text();

    if (!r.ok) {
      let data = null;
      try { data = JSON.parse(text); } catch { /* Server returned plain text. */ }
      const error = new Error(data?.detail || data?.message || "Request failed");
      error.status = r.status;
      error.code = data?.code || "";
      throw error;
    }

    try {
      return JSON.parse(text);
    } catch {
      throw new Error(`Invalid JSON from ${fullUrl}: ${text.slice(0, 200)}`);
    }
  }

  async postPublic(url, body = {}) {
    const fullUrl = apiUrl(url);

    const r = await fetch(fullUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const text = await r.text();

    if (!r.ok) {
      let data = null;
      try { data = JSON.parse(text); } catch { /* Server returned plain text. */ }
      const error = new Error(data?.detail || data?.message || "Request failed");
      error.status = r.status;
      error.code = data?.code || "";
      throw error;
    }

    try {
      return JSON.parse(text);
    } catch {
      throw new Error(`Invalid JSON from ${fullUrl}: ${text.slice(0, 200)}`);
    }
  }
}

const PAGE_BG = "var(--brand-bg, #fffaf3)";
const SURFACE = "var(--brand-surface, rgba(255,255,255,0.94))";
const BORDER = "rgba(124,45,18,0.10)";
const BRAND = "var(--brand-text, #431407)";
const ACCENT = "var(--brand-primary, #F7B500)";
const CTA = "var(--brand-cta, #0f766e)";

const focusRingSx = {
  "&:focus-visible": {
    outline: `3px solid ${CTA}`,
    outlineOffset: 3,
  },
};

const standaloneGlobalStyles = {
  "html, body": {
    touchAction: "manipulation",
    backgroundColor: PAGE_BG,
  },
  body: {
    // Horizontal clipping must not create a body scrollport that traps sticky controls.
    overflowX: "visible",
    "@supports (overflow-x: clip)": {
      overflowX: "clip",
    },
  },
  "button, [role='button'], .MuiButtonBase-root": {
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
  },
  "button:focus-visible, [role='button']:focus-visible, a:focus-visible, [tabindex='0']:focus-visible": {
    outline: `3px solid ${CTA}`,
    outlineOffset: 3,
  },
  img: {
    maxWidth: "100%",
  },
  "@media (prefers-reduced-motion: reduce)": {
    "html:focus-within": {
      scrollBehavior: "auto",
    },
    "*, *::before, *::after": {
      animationDuration: "0.01ms !important",
      animationIterationCount: "1 !important",
      scrollBehavior: "auto !important",
      transitionDuration: "0.01ms !important",
    },
  },
};

function createSkiplineTheme(brandTheme = DEFAULT_BRAND_THEME) {
  const c = normalizeBrandTheme(brandTheme);

  return createTheme({
    palette: {
      primary: { main: c.primary, contrastText: c.onPrimary },
      secondary: { main: c.cta, contrastText: c.onCta },
      background: { default: c.background, paper: c.surface },
      text: { primary: c.text, secondary: c.muted },
    },
    shape: { borderRadius: 14 },
    typography: {
      fontFamily:
        "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif",
      button: { textTransform: "none" },
    },
    components: {
      MuiButtonBase: {
        styleOverrides: {
          root: {
            "&.Mui-focusVisible": {
              outline: `3px solid ${c.cta}`,
              outlineOffset: 3,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            minHeight: 44,
            borderRadius: 12,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            minWidth: 44,
            minHeight: 44,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 44,
          },
        },
      },
    },
  });
}

const appBarSx = {
  bgcolor: PAGE_BG,
  color: BRAND,
  borderBottom: "1px solid",
  borderColor: "rgba(67,20,7,0.10)",
  backdropFilter: "blur(18px)",
  boxShadow: "0 10px 34px rgba(33,20,12,0.08)",
};

const brandMarkSx = {
  width: 42,
  height: 42,
  position: "relative",
  borderRadius: "12px",
  bgcolor:
    "color-mix(in srgb, var(--brand-primary, #f7b500) 18%, var(--brand-surface, #fff))",
  border: "1px solid rgba(124,45,18,0.10)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  flexShrink: 0,
};

const shellCardSx = {
  borderRadius: "24px",
  border: "1px solid",
  borderColor: BORDER,
  boxShadow: "0 20px 48px rgba(33,20,12,0.10)",
  overflow: "hidden",
  bgcolor: SURFACE,
};

const compactCardSx = {
  borderRadius: "16px",
  border: "1px solid",
  borderColor: BORDER,
  boxShadow: "0 14px 34px rgba(33,20,12,0.08)",
  overflow: "hidden",
  bgcolor: SURFACE,
};

const pillButtonSx = {
  borderRadius: "12px",
  fontWeight: 900,
  textTransform: "none",
  ...focusRingSx,
};

export class SkipTheLinePage extends React.Component {
  constructor(props){
    super(props);
    this.state = {
      location: safeStorageGet("localStorage", "selectedSkiplineLocation") || null,
      orderMode: "pickup",
      deliveryEnabled: false,
      deliveryOptionsLoading: true,
      deliveryEstimateMin: null,
      deliveryEstimateMax: null,
      deliveryQuery: "",
      deliverySearchResults: [],
      deliverySearching: false,
      deliveryBusy: false,
      deliveryError: "",
      deliveryUnavailable: false,
      deliveryCandidate: null,
      deliverySavedAddress: null,
      deliveryAddresses: [],
      deliveryAddressesLoading: false,
      deliveryAddress: null,
      deliveryResolution: null,
      deliveryEditingSaved: null,
      deliveryCountyId: "",
      deliveryCityId: "",
      deliveryCounties: [],
      deliveryCities: [],
      deliveryNeedsCitySelection: false,
      deliverySaveAddress: false,
      deliveryLastSavedDetails: "",
      deliveryBlock: "",
      deliveryEntrance: "",
      deliveryFloor: "",
      deliveryApartment: "",
      deliveryIntercom: "",
      deliveryCourierNotes: "",
      recipientName: "",
      recipientPhone: "",
      deliveryCheckoutConflict: false,
      deliveryOrderOutcomeUnknown: false,
      deliveryGuestSessionExpired: false,
      deliveryClientIdentityMismatch: false,
      locationList: [],
      selectingLocation: false,
      expandedCounty: null,
      locationSearch: "",
      locationInfo: null,
      locationOpenNow: false,
      locationReason: "",   // explanation if blocked
      loading: true, error: "", priceList:null,
      promoList: null,                        // raw promo list
      promoByProductId: {},                   // productId -> promo price (RON)
      promoByComboId: {},                     // comboId   -> promo price (RON) if present
      effectiveProd: {},                      // productId -> price used (RON)
      effectiveCombo: {},                     // comboId   -> price used (RON)
      vatRatesById: {},                       // VAT iref -> sell percentage
      priceByProductId: {},   // base price list
      priceByComboId:   {},   // base price list
      productsAll:[], products:[], combos:[],
      categories:[], categoriesRaw:[], subcategories:[],
      view:"name", activeCategory:"All", search:"",
      cartOpen:false, cartProducts:{}, cartCombos:{},
      locationChangeConfirmation: null,
      pickerOpen:false, pickerCombo:null,
      infoProduct:null,
      customerName:"",
      guestEmail:"",
      placing:false, orderLocked:false, lastOrderId:null, lastOrderGross:null,
      lastOrderContext:null,
      paymentSheetOpen:false, paymentChecking:false, paymentMessage:"", payUrl:"", payIframeError:false,
      paymentRegistering:false, paymentRegistrationFailed:false,
      clientSessionRequired:false,
      paymentProvider: "PayU",
      paymentProviderConfirmed: false,
      summaryOpen:false,
    };
    this.api = props.api || new ApiClient();
    this.pollTimer = null;
    this.orderAddInFlight = false;
    this.registerPayPromise = null;
    this.pendingCreatedOrder = null;
    this.deliverySearchTimer = null;
    this.deliverySearchSequence = 0;
    this.deliveryResolutionSequence = 0;
    this.pendingDeliveryOrderContext = null;
    this.pendingDeliveryOrderOwnerId = null;
    this.suspendedClientOrderOwnerId = null;
    this.suspendedClientPayment = null;
    this.suspendedClientUnknownOwnerId = null;
  }

  getBrandName = () => {
    const configuredName = String(this.props.brandName || "").trim();
    if (configuredName) return configuredName;
    return DEFAULT_BRAND_NAME;
  };

  t = (key, fallback = "") => {
    return this.props.t ? this.props.t(key, fallback) : fallback || key;
  };

  tr = (key, replacements = {}, fallback = "") => {
    let text = this.t(key, fallback);
    Object.keys(replacements).forEach((name) => {
      text = text.replaceAll(`{${name}}`, replacements[name]);
    });
    return text;
  };

  getBrandNameUpper = () => this.getBrandName().toLocaleUpperCase("ro-RO");

  getBrandInitial = () => {
    const brandName = this.getBrandName();
    return brandName ? brandName.charAt(0).toLocaleUpperCase("ro-RO") : "";
  };

  getCategoryIdForName = (name, categoriesRaw = this.state.categoriesRaw) => {
    if (!name || name === "All" || name === "Combos") return null;

    const category = (categoriesRaw || []).find(
      (item) => String(item?.name || "") === String(name)
    );

    return category?.iref || category?.id || null;
  };

  getCategoryNameForId = (id, categoriesRaw = this.state.categoriesRaw) => {
    if (!id) return null;

    const category = (categoriesRaw || []).find(
      (item) => String(item?.iref || item?.id || "") === String(id)
    );

    return category?.name || null;
  };

  resolveActiveCategoryAfterReload = (activeCategory, previousCategoriesRaw, nextCategoriesRaw) => {
    if (activeCategory === "All" || activeCategory === "Combos") return activeCategory;

    const previousCategoryId = this.getCategoryIdForName(activeCategory, previousCategoriesRaw);
    const nextCategoryName = this.getCategoryNameForId(previousCategoryId, nextCategoriesRaw);

    if (nextCategoryName) return nextCategoryName;

    const stillAvailable = (nextCategoriesRaw || []).some(
      (category) => String(category?.name || "") === String(activeCategory)
    );

    return stillAvailable ? activeCategory : "All";
  };

  renderLanguageSwitcher = (sx = {}) => {
    const languages = this.props.languages || [];
    if (languages.length < 2) return null;

    const selectedLanguage =
      languages.find((language) => String(language.iref) === String(this.props.selectedLanguageIref)) ||
      languages[0];
    const nextLanguage =
      languages.find((language) => String(language.iref) !== String(selectedLanguage?.iref)) ||
      selectedLanguage;
    const selectedCode = String(
      selectedLanguage?.countryShortName ||
      this.props.languageCode ||
      ""
    ).toUpperCase();

    const handleMobileToggle = () => {
      if (!nextLanguage?.iref || String(nextLanguage.iref) === String(selectedLanguage?.iref)) return;
      this.props.setSelectedLanguageIref?.(nextLanguage.iref);
    };

    return (
      <>
        <Button
          size="small"
          onClick={handleMobileToggle}
          aria-label={this.t("language.change")}
          sx={{
            display: { xs: "inline-flex", sm: "none" },
            minWidth: 44,
            minHeight: 44,
            px: 1,
            borderRadius: "12px",
            color: "secondary.contrastText",
            bgcolor: "secondary.main",
            border: "1px solid",
            borderColor: "secondary.main",
            flexShrink: 0,
            fontSize: 12,
            fontWeight: 950,
            lineHeight: 1,
            "&:hover": {
              bgcolor: "secondary.dark",
              borderColor: "secondary.dark",
            },
            ...sx,
          }}
        >
          {selectedCode}
        </Button>

        <Stack
          role="group"
          direction="row"
          spacing={0.25}
          aria-label={this.t("language.change")}
          sx={{
            display: { xs: "none", sm: "flex" },
            p: 0.25,
            borderRadius: "8px",
            border: "1px solid",
            borderColor: BORDER,
            bgcolor: "rgba(255,255,255,0.72)",
            flexShrink: 0,
            ...sx,
          }}
        >
          {languages.map((language) => {
            const code = String(language.countryShortName || "").toUpperCase();
            const active = String(language.iref) === String(this.props.selectedLanguageIref);

            return (
              <Button
                key={language.iref}
                size="small"
                onClick={() => this.props.setSelectedLanguageIref?.(language.iref)}
                aria-pressed={active}
                aria-label={`${this.t("language.change")} ${code}`}
                sx={{
                  minWidth: 44,
                  minHeight: 40,
                  px: 0.65,
                  borderRadius: "10px",
                  color: active ? "secondary.contrastText" : BRAND,
                  bgcolor: active ? "secondary.main" : "transparent",
                  fontSize: 12,
                  fontWeight: 950,
                  lineHeight: 1,
                  "&:hover": {
                    bgcolor: active ? "secondary.main" : "rgba(15,118,110,0.07)",
                  },
                }}
              >
                {code}
              </Button>
            );
          })}
        </Stack>
      </>
    );
  };

  renderBrandMark = (sx = {}) => {
    const { logoUrl } = this.props;
    const fallbackInitial = this.getBrandInitial();

    return (
      <Box sx={{ ...brandMarkSx, ...sx }}>
        <Typography
          aria-hidden="true"
          sx={{
            fontWeight: 900,
            color: "var(--brand-accent, #9a3412)",
            fontSize: 18,
            visibility: fallbackInitial ? "visible" : "hidden",
          }}
        >
          {fallbackInitial || "R"}
        </Typography>
        {logoUrl ? (
          <Box
            component={OptimizedConfigImage}
            src={logoUrl}
            alt={this.getBrandName()}
            decoding="async"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
            sx={{
              position: "absolute",
              inset: 0,
              m: "auto",
              maxHeight: 39,
              maxWidth: 39,
              width: "auto",
              display: "block",
            }}
          />
        ) : null}
      </Box>
    );
  };

  activateCardFromKeyboard = (event, action) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    action();
  };

  ensurePublicSession = async (loc = this.state.location) => {
    const existing =
      skiplineAccessToken ||
      safeStorageGet("sessionStorage", "skiplineAccessToken") ||
      tokenFromUrl;

    if (existing) {
      const expiryMs = guestTokenExpiryMs(existing);
      if (expiryMs !== null && expiryMs - Date.now() <= 60000) {
        const protectedOrder = Boolean(this.pendingCreatedOrder || this.state.lastOrderId ||
          this.state.deliveryOrderOutcomeUnknown || this.state.orderLocked ||
          this.state.paymentSheetOpen || this.state.paymentChecking || this.state.paymentRegistering);
        if (protectedOrder) {
          if (expiryMs <= Date.now()) {
            const message = this.t("skipline.guestSessionExpiredStop");
            const error = new Error(message);
            error.code = "GUEST_SKIPLINE_SESSION_EXPIRED";
            error.status = 401;
            this.stopPolling();
            this.setState({ deliveryGuestSessionExpired: true, error: message,
              paymentMessage: message, paymentChecking: false });
            throw error;
          }
          setSkiplineAccessToken(existing);
          return existing;
        }
        setSkiplineAccessToken(null);
      } else {
        setSkiplineAccessToken(existing);
        return existing;
      }
    }

    const body = {
      location: loc,
      source: "skipline_public",
    };

    let res = null;

    try {
      res = await this.api.postPublic(ENDPOINTS.startSkiplineSession(), body);
    } catch (firstErr) {
      res = await this.api.postPublic(ENDPOINTS.startSkiplineSessionAlt(), body);
    }

    const token =
      res?.access ||
      res?.data?.access ||
      res?.token ||
      res?.data?.token;

    if (!token) {
      throw new Error(this.t("skipline.loadingInit"));
    }

    setSkiplineAccessToken(token);
    return token;
  };

  loadPaymentProviderConfig = async () => {
    try {
      const cfg = await this.api.get(
        `${Config.API_URL_CONFIG_FRONT}?key=ORDER_PAYMENT_PROVIDER`
      );

      const rawProvider =
        cfg?.platform?.find((x) => x?.key === "ORDER_PAYMENT_PROVIDER")?.value ||
        cfg?.platform?.[0]?.value;
      if (!rawProvider) throw new Error("Payment provider is unavailable");

      const provider = String(rawProvider).toLowerCase().trim();
      if (!["payu", "bt", "stripe"].includes(provider)) {
        throw new Error("Payment provider is unsupported");
      }

      const paymentProvider =
        provider === "stripe"
          ? "Stripe"
          : provider === "bt"
            ? "BT"
            : "PayU";

      this.setState({ paymentProvider, paymentProviderConfirmed: true });
    } catch (e) {
      this.setState({
        paymentProvider: "PayU",
        paymentProviderConfirmed: false,
      });
    }
  };

  loadSkiplineOptions = async () => {
    try {
      const options = await this.api.get(ENDPOINTS.skiplineOptions(), { publicRequest: true });
      this.setState({
        deliveryEnabled: options?.enabled === true,
        deliveryOptionsLoading: false,
        deliveryEstimateMin: Number.isFinite(Number(options?.estimateMinMinutes))
          ? Number(options.estimateMinMinutes) : null,
        deliveryEstimateMax: Number.isFinite(Number(options?.estimateMaxMinutes))
          ? Number(options.estimateMaxMinutes) : null,
      });
    } catch {
      this.setState({ deliveryEnabled: false, deliveryOptionsLoading: false,
        deliveryEstimateMin: null, deliveryEstimateMax: null });
    }
  };

  loadDeliveryAddresses = async () => {
    if (!this.props.clientPortalAuthenticated || !this.props.loadSkiplineDeliveryAddresses) return;
    const clientId = this.props.clientProfile?.id;
    this.setState({ deliveryAddressesLoading: true });
    try {
      const response = await this.props.loadSkiplineDeliveryAddresses();
      if (this.state.orderMode === "delivery" && this.props.clientPortalAuthenticated && this.props.clientProfile?.id === clientId) {
        this.setState({
          deliveryAddresses: Array.isArray(response?.addresses) ? response.addresses : [],
          deliveryAddressesLoading: false,
        });
      }
    } catch (error) {
      if (this.state.orderMode === "delivery" && this.props.clientPortalAuthenticated && this.props.clientProfile?.id === clientId) {
        this.setState({
          deliveryAddresses: [], deliveryAddressesLoading: false,
          deliveryError: error?.status === 401
            ? this.t("skipline.deliverySessionRequired")
            : this.t("skipline.deliveryAddressesUnavailable"),
        });
      }
    }
  };

  loadDeliveryCounties = async () => {
    try {
      const response = await this.api.get(ENDPOINTS.geoStructure(), { publicRequest: true });
      const romanianCountry = Object.values(response || {}).find((country) =>
        String(country?.name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() === "romania");
      if (!romanianCountry) throw new Error("Romanian counties unavailable");
      const counties = Object.entries(romanianCountry)
        .filter(([id, county]) => id !== "name" && county && typeof county === "object")
        .map(([id, county]) => ({
          iref: id, name: county.name,
          cities: Object.entries(county)
            .filter(([cityId]) => cityId !== "name")
            .map(([cityId, name]) => ({ iref: cityId, name })),
        })).sort((a, b) => a.name.localeCompare(b.name, "ro"));
      this.setState({ deliveryCounties: counties });
      if (this.state.deliveryCountyId) this.loadDeliveryCities(this.state.deliveryCountyId, counties);
    } catch {
      this.setState({ deliveryError: this.t("skipline.deliveryCitiesUnavailable") });
    }
  };

  loadDeliveryCities = (countyId, counties = this.state.deliveryCounties) => {
    if (!countyId) {
      this.setState({ deliveryCities: [], deliveryCityId: "" });
      return;
    }
    const county = counties.find((entry) => String(entry.iref) === String(countyId));
    this.setState({ deliveryCities: county?.cities || [] });
  };

  switchOrderMode = (mode, skipConfirmation = false) => {
    if (this.state.deliveryOrderOutcomeUnknown) return;
    if (mode === this.state.orderMode || (mode === "delivery" && !this.state.deliveryEnabled)) return;
    const hasCart = Object.keys(this.state.cartProducts).length || Object.keys(this.state.cartCombos).length;
    if (hasCart && !skipConfirmation && !window.confirm(this.t("skipline.modeChangeConfirm"))) return;
    this.deliverySearchSequence += 1;
    this.deliveryResolutionSequence += 1;
    if (this.deliverySearchTimer) clearTimeout(this.deliverySearchTimer);
    safeStorageRemove("localStorage", "selectedSkiplineLocation");
    this.setState({
      orderMode: mode, location: null, locationInfo: null, priceList: null,
      products: [], combos: [], cartProducts: {}, cartCombos: {},
      deliveryQuery: "", deliverySearchResults: [], deliveryCandidate: null,
      deliverySavedAddress: null, deliveryAddresses: [],
      deliveryAddress: null, deliveryResolution: null,
      deliveryEditingSaved: null, deliverySaveAddress: false,
      deliveryLastSavedDetails: "", deliveryBlock: "", deliveryEntrance: "",
      deliveryFloor: "", deliveryApartment: "", deliveryIntercom: "",
      deliveryCourierNotes: "", deliveryCountyId: "", deliveryCityId: "",
      deliveryError: "", deliveryUnavailable: false, deliveryCheckoutConflict: false,
      view: "name", activeCategory: "All", search: "",
      recipientName: this.props.clientPortalAuthenticated
        ? this.props.clientProfile?.displayName || "" : "",
      recipientPhone: this.props.clientPortalAuthenticated
        ? this.props.clientProfile?.phone || "" : "",
    });
    if (mode === "delivery" && this.props.clientPortalAuthenticated) this.loadDeliveryAddresses();
  };

  changeDeliveryQuery = (value) => {
    this.deliverySearchSequence += 1;
    this.deliveryResolutionSequence += 1;
    if (this.deliverySearchTimer) clearTimeout(this.deliverySearchTimer);
    this.setState({
      deliveryQuery: value, deliverySearchResults: [], deliverySearching: false,
      deliveryCandidate: null, deliveryAddress: null, deliveryResolution: null,
      deliveryError: "", deliveryUnavailable: false,
    });
    if (String(value).trim().length < 4) return;
    const sequence = this.deliverySearchSequence;
    this.deliverySearchTimer = setTimeout(() => this.searchDeliveryAddress(value, sequence), 350);
  };

  searchDeliveryAddress = async (query, sequence = this.deliverySearchSequence) => {
    this.setState({ deliverySearching: true, deliveryError: "" });
    try {
      const response = await this.api.postPublic(ENDPOINTS.skiplineAddressSearch(), { query: String(query).trim() });
      if (sequence !== this.deliverySearchSequence) return;
      if (!Array.isArray(response?.results)) throw new Error("Malformed search response");
      const results = response.results.filter((item) =>
        item && item.street && item.number && (item.candidateToken || item.selectionToken));
      this.setState({ deliverySearchResults: results, deliverySearching: false });
    } catch (error) {
      if (sequence !== this.deliverySearchSequence) return;
      this.setState({
        deliverySearchResults: [], deliverySearching: false,
        deliveryError: this.t(error?.status == null || error?.status === 0 || error?.status === 429 || error?.status >= 500
          ? "skipline.deliveryTemporaryUnavailable" : "skipline.deliverySearchFailed"),
      });
    }
  };

  selectDeliveryCandidate = (candidate) => {
    const candidateToken = candidate?.candidateToken || candidate?.selectionToken;
    if (!candidate?.street || !candidate?.number || !candidateToken) return;
    this.deliverySearchSequence += 1;
    this.deliveryResolutionSequence += 1;
    if (this.deliverySearchTimer) clearTimeout(this.deliverySearchTimer);
    this.setState({
      deliveryCandidate: { ...candidate, candidateToken },
      deliverySavedAddress: null, deliverySearchResults: [],
      deliveryQuery: candidate.label || `${candidate.street} ${candidate.number}`,
      deliveryAddress: null, deliveryResolution: null,
      deliveryCountyId: "", deliveryCityId: "", deliveryNeedsCitySelection: false,
      deliveryLastSavedDetails: "",
      ...(this.state.deliveryEditingSaved ? {} : {
        deliveryBlock: "", deliveryEntrance: "", deliveryFloor: "",
        deliveryApartment: "", deliveryIntercom: "", deliveryCourierNotes: "",
      }),
      deliveryError: "", deliveryUnavailable: false,
    }, () => this.resolveDeliveryAddress());
  };

  selectSavedDeliveryAddress = (card) => {
    if (!this.props.clientPortalAuthenticated || !card) return;
    if (card.requiresRevalidation === true || card.validCoordinates !== true) {
      this.editSavedDeliveryAddress(card);
      this.setState({ deliveryError: this.t("skipline.deliveryRevalidateAddress") });
      return;
    }
    this.deliveryResolutionSequence += 1;
    this.setState({
      deliveryCandidate: null, deliverySavedAddress: card,
      deliveryEditingSaved: null, deliverySaveAddress: false,
      deliveryAddress: null, deliveryResolution: null,
      deliveryQuery: "", deliverySearchResults: [],
      deliveryEntrance: card.entrance || "",
      deliveryFloor: card.floor || "",
      deliveryApartment: card.local || "",
      deliveryBlock: card.block || "",
      deliveryIntercom: card.intercom || "",
      deliveryCourierNotes: "",
      deliveryError: "", deliveryUnavailable: false,
    }, () => this.resolveDeliveryAddress());
  };

  editSavedDeliveryAddress = (card) => {
    if (!this.props.clientPortalAuthenticated || !card) return;
    this.deliveryResolutionSequence += 1;
    this.setState({
      deliveryEditingSaved: card, deliverySavedAddress: null,
      deliveryCandidate: null, deliveryAddress: null, deliveryResolution: null,
      deliveryQuery: [card.street, card.number].filter(Boolean).join(" "),
      deliverySearchResults: [], deliverySaveAddress: true,
      deliveryLastSavedDetails: "",
      deliveryEntrance: card.entrance || "",
      deliveryFloor: card.floor || "",
      deliveryApartment: card.local || "",
      deliveryBlock: card.block || "",
      deliveryIntercom: card.intercom || "",
      deliveryCourierNotes: "",
      deliveryError: "",
    }, () => this.changeDeliveryQuery(this.state.deliveryQuery));
  };

  deleteSavedDeliveryAddress = async (card) => {
    if (!this.props.clientPortalAuthenticated || card?.kind === "primary") return;
    if (!window.confirm(this.t("skipline.deliveryDeleteConfirm"))) return;
    this.setState({ deliveryBusy: true, deliveryError: "" });
    try {
      await this.props.deleteSkiplineDeliveryAddress({ kind: card.kind, id: card.id, updateKey: card.updateKey });
      const selected = [this.state.deliverySavedAddress, this.state.deliveryEditingSaved]
        .some((address) => address?.kind === card.kind && String(address?.id) === String(card.id));
      if (selected) {
        this.deliveryResolutionSequence += 1;
        this.setState({
          deliverySavedAddress: null, deliveryEditingSaved: null,
          deliveryCandidate: null, deliveryAddress: null, deliveryResolution: null,
          deliveryCountyId: "", deliveryCityId: "", deliveryQuery: "",
          deliverySaveAddress: false,
        });
      }
      await this.loadDeliveryAddresses();
      this.setState({ deliveryBusy: false });
    } catch (error) {
      this.setState({
        deliveryBusy: false,
        deliveryError: this.t(error?.status === 401
          ? "skipline.deliverySessionRequired" : "skipline.deliveryDeleteFailed"),
      });
    }
  };

  resolveDeliveryAddress = async (overrides = {}) => {
    const saved = overrides.savedAddress || this.state.deliverySavedAddress;
    const candidateToken = saved ? null : this.state.deliveryCandidate?.candidateToken;
    if (!candidateToken && !saved) return;
    const savedAddress = saved ? { kind: saved.kind, id: saved.id, updateKey: saved.updateKey } : undefined;
    const requestSequence = ++this.deliveryResolutionSequence;
    const body = {
      ...(candidateToken ? { candidateToken } : {}),
      ...(savedAddress ? { savedAddress } : {}),
      ...(overrides.countyId || this.state.deliveryCountyId ? { countyId: overrides.countyId || this.state.deliveryCountyId } : {}),
      ...(overrides.cityId || this.state.deliveryCityId ? { cityId: overrides.cityId || this.state.deliveryCityId } : {}),
    };
    this.setState({ deliveryBusy: true, deliveryError: "", deliveryUnavailable: false });
    try {
      const response = this.props.clientPortalAuthenticated
        ? await this.props.resolveAuthenticatedSkiplineDelivery(body)
        : await this.api.postPublic(ENDPOINTS.skiplineResolveDelivery(), body);
      if (requestSequence !== this.deliveryResolutionSequence) return null;
      const address = response?.address;
      const location = response?.location;
      if (!response?.resolutionToken || !location?.iref || !address?.street || !address?.number || !address?.countyId || !address?.cityId) {
        throw new Error("Malformed delivery resolution");
      }
      if (!Number.isFinite(Number(response.deliveryFee)) || !Number.isFinite(Number(response.freeDeliveryThreshold))) {
        throw new Error("Malformed delivery fee");
      }
      this.setState({
        deliveryBusy: false, deliveryAddress: address, deliveryResolution: response,
        deliveryCountyId: String(address.countyId), deliveryCityId: String(address.cityId),
        deliveryNeedsCitySelection: false, deliveryUnavailable: false,
      });
      if (!this.state.deliveryCounties.length) this.loadDeliveryCounties();
      this.loadDeliveryCities(address.countyId);
      return response;
    } catch (error) {
      if (requestSequence !== this.deliveryResolutionSequence) return null;
      const needsCity = [400, 422].includes(error?.status) &&
        error?.code === "CITY_SELECTION_REQUIRED";
      const unavailable = error?.code === "DELIVERY_UNAVAILABLE_FOR_ADDRESS";
      const needsRevalidation = error?.code === "ADDRESS_REVALIDATION_REQUIRED";
      this.setState({
        deliveryBusy: false, deliveryResolution: null, deliveryAddress: null,
        deliveryNeedsCitySelection: needsCity,
        deliveryUnavailable: unavailable,
        deliveryError: this.t(needsCity
          ? "skipline.deliveryCityMappingRequired"
          : needsRevalidation
            ? "skipline.deliveryRevalidateAddress"
            : error?.code === "INVALID_CITY_SELECTION"
              ? "skipline.deliveryInvalidCitySelection"
              : error?.code === "INVALID_ADDRESS_SELECTION" || error?.code === "ADDRESS_SELECTION_EXPIRED"
                ? "skipline.deliverySelectionExpired"
          : error?.status == null || error?.status === 0 || error?.status === 429 || error?.status >= 500
            ? "skipline.deliveryTemporaryUnavailable"
            : unavailable
              ? "skipline.deliveryUnavailableAddress"
              : "skipline.deliveryResolveFailed"),
      });
      if (needsCity) this.loadDeliveryCounties();
      if (needsRevalidation && saved) this.editSavedDeliveryAddress(saved);
      return null;
    }
  };

  currentDeliverySaveDetails = () => ({
    countyId: this.state.deliveryCountyId,
    cityId: this.state.deliveryCityId,
    block: this.state.deliveryBlock,
    entrance: this.state.deliveryEntrance,
    floor: this.state.deliveryFloor,
    apartment: this.state.deliveryApartment,
    intercom: this.state.deliveryIntercom,
  });

  saveDeliveryCandidate = async () => {
    const candidate = this.state.deliveryCandidate;
    if (!this.props.clientPortalAuthenticated || !candidate?.candidateToken || !this.state.deliveryResolution) return false;
    const saved = this.state.deliveryEditingSaved;
    const details = this.currentDeliverySaveDetails();
    this.setState({ deliveryBusy: true, deliveryError: "" });
    try {
      const response = await this.props.saveSkiplineDeliveryAddress({
        candidateToken: candidate.candidateToken,
        ...(saved ? { kind: saved.kind, id: saved.id, updateKey: saved.updateKey } : {}),
        ...details,
      });
      const card = response?.address;
      if (!card?.kind || !card?.id || !card?.updateKey) throw new Error("Malformed saved address response");
      this.setState({
        deliveryEditingSaved: card, deliverySavedAddress: card,
        deliveryLastSavedDetails: JSON.stringify(details),
        deliverySaveAddress: true, deliveryBusy: false,
      });
      this.loadDeliveryAddresses();
      const previousLocation = this.state.deliveryResolution?.location?.iref;
      const savedResolution = await this.resolveDeliveryAddress({ savedAddress: card });
      if (!savedResolution) return null;
      const locationChanged = previousLocation &&
        String(previousLocation) !== String(savedResolution.location.iref);
      if (locationChanged) this.setState({
        deliveryError: this.t("skipline.deliverySavedLocationChanged"),
      });
      return { address: card, resolution: savedResolution, locationChanged: Boolean(locationChanged) };
    } catch (error) {
      this.setState({
        deliveryBusy: false,
        deliverySaveAddress: Boolean(this.state.deliverySavedAddress),
        deliveryError: this.t(error?.status === 401
          ? "skipline.deliverySessionRequired" : "skipline.deliverySaveFailed"),
      });
      return false;
    }
  };

  changeDeliveryRecipient = (field, value) => {
    this.setState((state) => {
      const recipient = { recipientName: state.recipientName, recipientPhone: state.recipientPhone, [field]: value };
      const corrected = hasFullRecipientName(recipient.recipientName) && normalizeRomanianPhone(recipient.recipientPhone);
      return {
        ...recipient,
        ...(corrected && state.deliveryError === this.t("skipline.deliveryRecipientRequired") ? { deliveryError: "" } : {}),
      };
    });
  };

  continueDelivery = async () => {
    if (this.state.deliveryBusy) return;
    if (this.props.clientPortalAuthenticated && !this.props.authenticatedCheckoutEnabled) {
      this.setState({ deliveryError: this.t("skipline.deliveryAccountCheckoutUnavailable") });
      return;
    }
    if (this.props.authenticatedCheckoutEnabled && this.isClientPortalBusy()) {
      this.setState({ deliveryError: this.t("skipline.clientSessionRestoring") });
      return;
    }
    const { deliveryCandidate, deliverySavedAddress, deliveryResolution, deliveryAddress,
      deliveryCountyId, deliveryCityId, deliverySaveAddress } = this.state;
    if (!deliveryCandidate && !deliverySavedAddress) return;
    if (!deliveryCountyId || !deliveryCityId) {
      this.setState({ deliveryError: this.t("skipline.deliveryCountyCityRequired") });
      return;
    }
    if (!hasFullRecipientName(this.state.recipientName) || !normalizeRomanianPhone(this.state.recipientPhone)) {
      this.setState({ deliveryError: this.t("skipline.deliveryRecipientRequired") });
      return;
    }
    const isClientOrder = this.isClientOrderContext(this.getOrderRequestContext());
    if (isClientOrder ? this.props.clientProfile?.hasOrderEmail !== true : !isValidEmail(this.state.guestEmail)) {
      this.setState({ deliveryError: this.t(isClientOrder
        ? "skipline.clientAccountEmailMissing" : "skipline.enterPaymentEmail") });
      return;
    }
    let resolution = deliveryResolution;
    if (!resolution || String(deliveryAddress?.countyId) !== String(deliveryCountyId) || String(deliveryAddress?.cityId) !== String(deliveryCityId)) {
      resolution = await this.resolveDeliveryAddress({ countyId: deliveryCountyId, cityId: deliveryCityId });
      if (!resolution) return;
    }
    this.setState({ deliveryBusy: true, deliveryError: "" });
    try {
      if (deliverySaveAddress && deliveryCandidate && !this.state.deliverySavedAddress &&
          this.props.clientPortalAuthenticated) {
        const saved = await this.saveDeliveryCandidate();
        if (!saved) return;
        resolution = saved.resolution;
        if (saved.locationChanged) return;
      }
      const hasCart = Object.keys(this.state.cartProducts || {}).length > 0 ||
        Object.keys(this.state.cartCombos || {}).length > 0;
      if (hasCart && this.state.location && String(this.state.location) !== String(resolution.location.iref)) {
        if (!window.confirm(this.t("skipline.deliveryLocationChangedConfirm"))) {
          this.setState({ deliveryBusy: false });
          return;
        }
        this.setState({ cartProducts: {}, cartCombos: {} });
      }
      safeStorageRemove("localStorage", "selectedSkiplineLocation");
      const selectionSequence = this.deliveryResolutionSequence;
      this.setState({
        deliveryResolution: resolution, loading: true, deliveryBusy: true,
        deliveryCheckoutConflict: false,
      });
      await this.ensurePublicSession(resolution.location.iref);
      await this.loadPaymentProviderConfig();
      const menuReady = await this.bootstrap(resolution.location.iref, resolution.priceListId);
      if (menuReady && this.state.orderMode === "delivery" &&
          selectionSequence === this.deliveryResolutionSequence) {
        this.setState({ location: resolution.location.iref, view: "home" });
      } else if (!menuReady && selectionSequence === this.deliveryResolutionSequence) {
        this.setState({ deliveryError: this.state.deliveryError || this.state.error || this.t("skipline.loadingMenu") });
      }
      this.setState({ deliveryBusy: false, loading: false });
    } catch (error) {
      this.setState({
        deliveryBusy: false, loading: false,
        deliveryError: this.t(error?.status === 401
          ? "skipline.deliverySessionRequired" : "skipline.deliverySaveFailed"),
      });
    }
  };

  async componentDidMount() {
    if (this.props.clientPortalAuthenticated) setSkiplineAccessToken(null);
    this.loadSkiplineOptions();
    if (this.props.clientPortalAuthenticated) this.setState({
      recipientName: this.props.clientProfile?.displayName || "",
      recipientPhone: this.props.clientProfile?.phone || "",
    });
    try {
      this.setState({ selectingLocation: true });

      const resp = await this.api.get(ENDPOINTS.locations(), { publicRequest: true }) || {};
      const locationList = (Array.isArray(resp.locations) ? resp.locations : [])
        .filter((loc) => PICKUP_SERVE_TYPES.has(Number(loc?.serveType)));
      const restoredLocation = this.state.location;
      const pickupRestored = restoredLocation && locationList.some((loc) =>
        String(loc?.iref ?? loc?.id) === String(restoredLocation));
      if (restoredLocation && !pickupRestored) {
        safeStorageRemove("localStorage", "selectedSkiplineLocation");
      }

      locationList.sort((a, b) => (a.name || "").localeCompare(b.name || "", "ro"));

      const countyNames = [
        ...new Set(locationList.map((loc) => this.getCountyName(loc))),
      ];

      this.setState(
        {
          locationList,
          location: pickupRestored ? restoredLocation : null,
          selectingLocation: false,
          loading: false,
          expandedCounty: countyNames.length === 1 ? countyNames[0] : null,
        },
        async () => {
          if (this.state.location) {
            try {
              this.setState({ loading: true });

              await this.ensurePublicSession(this.state.location);
              await this.loadPaymentProviderConfig();
              await this.bootstrap(this.state.location);
            } catch {
              this.setState({
                loading: false,
                error: this.t("skipline.loadingMenu"),
              });
            }
          }
        }
      );
    } catch {
      this.setState({
        loading: false,
        selectingLocation: false,
        error: this.t("skipline.loadingInit"),
      });
    }
  }

  stopUncertainClientDeliveryForIdentityChange = () => {
    this.deliverySearchSequence += 1;
    this.deliveryResolutionSequence += 1;
    if (this.deliverySearchTimer) clearTimeout(this.deliverySearchTimer);
    this.stopPolling();
    setSkiplineAccessToken(null);
    this.pendingDeliveryOrderContext = null;
    this.pendingDeliveryOrderOwnerId = null;
    this.suspendedClientUnknownOwnerId = null;
    this.setState({
      deliveryClientIdentityMismatch: true,
      deliveryOrderOutcomeUnknown: true,
      clientSessionRequired: false, paymentSheetOpen: false, payUrl: "",
      location: null, locationInfo: null, priceList: null, promoList: null,
      productsAll: [], products: [], combos: [], cartProducts: {}, cartCombos: {},
      deliveryAddresses: [], deliverySavedAddress: null, deliveryCandidate: null,
      deliveryEditingSaved: null, deliveryAddress: null, deliveryResolution: null,
      deliveryQuery: "", deliverySearchResults: [], deliveryCountyId: "", deliveryCityId: "",
      deliveryBlock: "", deliveryEntrance: "", deliveryFloor: "", deliveryApartment: "",
      deliveryIntercom: "", deliveryCourierNotes: "", recipientName: "", recipientPhone: "",
      lastOrderId: null, lastOrderContext: null, lastOrderGross: null,
      deliveryError: this.t("skipline.deliveryIdentityChangedStop"),
      error: this.t("skipline.deliveryIdentityChangedStop"),
      view: "name", cartOpen: false, placing: false, loading: false,
    });
  };

  componentDidUpdate(prevProps) {
    const confirmation = this.state.locationChangeConfirmation;
    if (confirmation && (this.locationChangeBlocked() ||
        confirmation.orderMode !== this.state.orderMode || confirmation.location !== this.state.location)) {
      this.cancelLocationChange();
    }
    if (prevProps.selectedLanguageIref !== this.props.selectedLanguageIref && this.state.location) {
      this.bootstrap(this.state.location);
    }
    const previousClientId = prevProps.clientProfile?.id || null;
    const currentClientId = this.props.clientProfile?.id || null;
    if (prevProps.clientPortalAuthenticated !== this.props.clientPortalAuthenticated || previousClientId !== currentClientId) {
      if (this.state.locationChangeConfirmation) this.setState({ locationChangeConfirmation: null });
      if (this.suspendedClientOrderOwnerId && this.props.clientPortalAuthenticated) {
        const sameClient = String(currentClientId) === String(this.suspendedClientOrderOwnerId);
        const suspendedPayment = this.suspendedClientPayment;
        this.suspendedClientOrderOwnerId = null;
        this.suspendedClientPayment = null;
        setSkiplineAccessToken(null);
        if (sameClient) {
          this.setState({
            clientSessionRequired: false, deliveryError: "", error: "",
            payUrl: suspendedPayment?.payUrl || "",
            paymentRegistrationFailed: !suspendedPayment?.payUrl,
          }, () => {
            if (suspendedPayment?.payUrl) this.checkOrderStatus();
            else this.retryPaymentRegistration();
          });
          return;
        }
      }
      if (this.suspendedClientUnknownOwnerId && this.props.clientPortalAuthenticated) {
        const sameClient = String(currentClientId) === String(this.suspendedClientUnknownOwnerId);
        this.suspendedClientUnknownOwnerId = null;
        setSkiplineAccessToken(null);
        if (!sameClient) {
          this.stopUncertainClientDeliveryForIdentityChange();
          return;
        }
        this.setState({
          clientSessionRequired: false, paymentSheetOpen: false,
          deliveryError: "",
          error: this.state.deliveryOrderOutcomeUnknown && !this.orderAddInFlight
            ? this.t("skipline.orderAddUncertain") : "",
        });
        return;
      }
      const uncertainClientDelivery = this.state.orderMode === "delivery" &&
        this.pendingDeliveryOrderContext === ORDER_REQUEST_CONTEXT.client &&
        Boolean(this.pendingDeliveryOrderOwnerId) &&
        (this.state.deliveryOrderOutcomeUnknown || this.orderAddInFlight || this.state.placing);
      if (uncertainClientDelivery && prevProps.clientPortalAuthenticated &&
          (!this.props.clientPortalAuthenticated ||
            String(currentClientId) !== String(this.pendingDeliveryOrderOwnerId))) {
        if (this.props.clientPortalAuthenticated) {
          this.stopUncertainClientDeliveryForIdentityChange();
        } else {
          this.suspendedClientUnknownOwnerId = this.pendingDeliveryOrderOwnerId;
          setSkiplineAccessToken(null);
          this.stopPolling();
          this.setState({
            clientSessionRequired: true, paymentSheetOpen: true,
            payUrl: "", payIframeError: true,
            deliveryOrderOutcomeUnknown: true,
            deliveryAddresses: [], deliveryAddressesLoading: false,
            paymentMessage: this.t("skipline.clientSessionRequired"),
            error: this.t("skipline.clientSessionRequired"),
          });
        }
        return;
      }
      const knownClientOrderInterrupted = prevProps.clientPortalAuthenticated &&
        !this.props.clientPortalAuthenticated && previousClientId &&
        this.state.lastOrderContext === ORDER_REQUEST_CONTEXT.client &&
        Boolean(this.state.lastOrderId) && !this.state.summaryOpen;
      if (knownClientOrderInterrupted) {
        this.suspendedClientOrderOwnerId = previousClientId;
        this.suspendedClientPayment = { payUrl: this.state.payUrl };
        setSkiplineAccessToken(null);
        this.stopPolling();
        this.setState({
          clientSessionRequired: true,
          paymentSheetOpen: true,
          paymentRegistrationFailed: true,
          paymentChecking: false,
          paymentRegistering: false,
          payIframeError: true,
          payUrl: "",
          paymentMessage: this.t("skipline.clientSessionRequired"),
          error: this.t("skipline.clientSessionRequired"),
        });
        return;
      }
      const guestPaymentInterrupted = this.state.lastOrderContext === ORDER_REQUEST_CONTEXT.guest &&
        Boolean(this.state.lastOrderId || this.pendingCreatedOrder || this.state.deliveryOrderOutcomeUnknown);
      this.deliverySearchSequence += 1;
      this.deliveryResolutionSequence += 1;
      if (this.deliverySearchTimer) clearTimeout(this.deliverySearchTimer);
      setSkiplineAccessToken(null);
      if (guestPaymentInterrupted) this.stopPolling();
      if (this.state.orderMode === "delivery") {
        this.stopPolling();
        this.pendingCreatedOrder = null;
      }
      this.setState({
        deliveryAddresses: [], deliverySavedAddress: null,
        deliveryCandidate: null, deliveryAddress: null, deliveryResolution: null,
        deliverySaveAddress: false, deliveryEditingSaved: null,
        deliveryQuery: "", deliverySearchResults: [],
        deliveryCountyId: "", deliveryCityId: "", deliveryBlock: "",
        deliveryEntrance: "", deliveryFloor: "", deliveryApartment: "",
        deliveryIntercom: "", deliveryCourierNotes: "", deliveryLastSavedDetails: "",
        deliveryCheckoutConflict: false,
        recipientName: this.props.clientPortalAuthenticated
          ? this.props.clientProfile?.displayName || "" : "",
        recipientPhone: this.props.clientPortalAuthenticated
          ? this.props.clientProfile?.phone || "" : "",
        deliveryError: this.state.orderMode === "delivery"
          ? this.t(guestPaymentInterrupted
            ? "skipline.deliveryGuestSessionExpired" : "skipline.deliverySessionChanged") : "",
        ...(guestPaymentInterrupted ? {
          error: this.t("skipline.guestPaymentSessionInterrupted"),
          payUrl: "", paymentSheetOpen: false, paymentChecking: false,
          deliveryGuestSessionExpired: true,
          deliveryOrderOutcomeUnknown: true,
        } : {}),
        ...(this.state.orderMode === "delivery" ? {
          location: null, locationInfo: null, priceList: null,
          products: [], combos: [], cartProducts: {}, cartCombos: {}, view: "name",
          payUrl: "", paymentSheetOpen: false, summaryOpen: false,
          lastOrderId: null, lastOrderContext: null, lastOrderGross: null,
          orderLocked: false, paymentRegistering: false,
        } : {}),
      });
      if (this.props.clientPortalAuthenticated && this.state.orderMode === "delivery") {
        this.loadDeliveryAddresses();
      }
    }
  }

  componentWillUnmount(){
    this.stopPolling();
    this.deliverySearchSequence += 1;
    this.deliveryResolutionSequence += 1;
    if (this.deliverySearchTimer) clearTimeout(this.deliverySearchTimer);
  }

  async bootstrap(loc = this.state.location, expectedDeliveryPriceListId = this.state.deliveryResolution?.priceListId) {
    try{
      const location = loc;
      const deliveryMode = this.state.orderMode === "delivery";
      const languageIref = this.props.selectedLanguageIref || "";
      const previousCategoriesRaw = this.state.categoriesRaw;
      const [locRaw, plRaw, promoRaw, prodsRaw, combosRaw, catsRaw, vatsRaw] = await Promise.all([
        this.api.get(ENDPOINTS.location(location)),
        this.api.get(ENDPOINTS.priceList(location, deliveryMode)),
        this.api.get(ENDPOINTS.promotionList(location, deliveryMode)).catch(()=>null),
        this.api.get(ENDPOINTS.products(languageIref)),
        this.api.get(ENDPOINTS.combos(languageIref)),
        this.api.get(ENDPOINTS.categories(languageIref)),
        this.api.get(ENDPOINTS.vats()).catch(()=>null),
      ]);

      const locInfo = locRaw || null;
      const openEval = this.state.orderMode === "delivery"
        ? { openNow: true, reason: "" }
        : evalLocationOpen(locInfo, new Date(), this.t);
      const pl = this.normalizePriceList(plRaw);
      if (deliveryMode && (!pl.id || !expectedDeliveryPriceListId ||
          String(pl.id) !== String(expectedDeliveryPriceListId))) {
        const hasCart = Object.values(this.state.cartProducts || {}).some((line) => Number(line?.qty) > 0) ||
          Object.values(this.state.cartCombos || {}).some((line) => Number(line?.qty) > 0);
        this.setState({
          loading: false,
          deliveryError: this.t("skipline.deliveryPriceListChanged"),
          ...(hasCart ? {
            deliveryCheckoutConflict: true,
            locationOpenNow: false,
            locationReason: this.t("skipline.deliveryPriceListChanged"),
          } : {
            location: null, locationInfo: null, priceList: null,
            deliveryResolution: null, view: "name",
          }),
        });
        return false;
      }
      const promoList = promoRaw?.promotionList || promoRaw || null;
      const vatRatesById = buildVatRateMap(vatsRaw);

      const { promoByProductId, promoByComboId, effectiveProd, effectiveCombo } =
        buildEffectiveMaps(pl, promoRaw);

      const catArr = arrify(catsRaw,["productCategories"]).map((category) =>
        applyEntityTranslation(category, "pct_product", this.props.selectedLanguage, this.props.defaultLanguage)
      );
      const subArr = [];
      const catMap = new Map(catArr.map(c=>[c.iref||c.id,c]));

      const productsAll = this.normalizeProducts(
        arrify(prodsRaw,["products","data","items"]).map((product) =>
          applyEntityTranslation(product, "pt_product", this.props.selectedLanguage, this.props.defaultLanguage)
        ),
        pl,
        catMap,
        effectiveProd,
        promoByProductId,
        vatRatesById
      );
      const products = productsAll.filter(p=>p.showStandalone &&
        (this.state.orderMode !== "delivery" || p.isDeliverable === true));

      const combos = this.normalizeCombos(
        arrify(combosRaw, ["productCombos", "combos", "data"]).map((combo) =>
          applyEntityTranslation(combo, "pt_product", this.props.selectedLanguage, this.props.defaultLanguage)
        ),
        pl,
        effectiveCombo,
        promoByProductId,
        promoByComboId,
        catMap,
        productsAll,
        this.state.orderMode === "delivery"
      );

      const apiCats = catArr.filter(c=>c.isForSale!==false)
        .map(c=>({ id:c.iref||c.id, name:c.name })).filter(c=>!!c.name);
      const categories = ["All", ...apiCats.map(c=>c.name), "Combos"];

      await new Promise((resolve) => this.setState((state) => ({
        priceList: pl,
        promoList,
        promoByProductId, promoByComboId,
        effectiveProd, effectiveCombo,
        vatRatesById,
        productsAll, products, combos, categories,
        categoriesRaw: catArr, subcategories: subArr,
        activeCategory: this.resolveActiveCategoryAfterReload(
          state.activeCategory,
          previousCategoriesRaw,
          catArr
        ),
        locationInfo: locInfo,
        locationOpenNow: openEval.openNow,
        locationReason: openEval.reason,
        loading: false,
      }), resolve));
      return true;
    }catch(e){
      this.setState({ error:String(e.message||e), loading:false });
      return false;
    }
  }

  selectLocation = async (locIref) => {
    if (this.state.deliveryOrderOutcomeUnknown) return;
    if (!locIref) return;

    if (this.state.orderMode === "pickup") safeStorageSet("localStorage", "selectedSkiplineLocation", locIref);

    this.setState({
      location: locIref,
      loading: true,
      error: "",
      cartProducts: {},
      cartCombos: {},
      deliveryCheckoutConflict: false,
      infoProduct: null,
      view: "name",
      activeCategory: "All",
      search: "",
    });
    try {
      await this.ensurePublicSession(locIref);
      await this.loadPaymentProviderConfig();
      await this.bootstrap(locIref);
    } catch {
      this.setState({
        loading: false,
        error: this.t("skipline.loadingSelectedLocation"),
      });
    }
  };

  locationChangeBlocked = () => Boolean(
    this.state.orderLocked || this.state.deliveryOrderOutcomeUnknown ||
    this.state.placing || this.orderAddInFlight || this.state.paymentRegistering
  );

  changeLocation = () => {
    if (this.locationChangeBlocked()) return;
    const hasItems =
      Object.keys(this.state.cartProducts || {}).length > 0 ||
      Object.keys(this.state.cartCombos || {}).length > 0;

    if (hasItems) {
      this.setState({ locationChangeConfirmation: {
        orderMode: this.state.orderMode,
        location: this.state.location,
      } });
      return;
    }
    this.resetLocationSelection();
  };

  cancelLocationChange = () => this.setState({ locationChangeConfirmation: null });

  confirmLocationChange = () => {
    const confirmation = this.state.locationChangeConfirmation;
    if (!confirmation) return;
    this.cancelLocationChange();
    if (this.locationChangeBlocked() || confirmation.orderMode !== this.state.orderMode ||
        confirmation.location !== this.state.location) return;
    this.resetLocationSelection();
  };

  resetLocationSelection = () => {
    if (this.locationChangeBlocked()) return;

    this.deliveryResolutionSequence += 1;

    safeStorageRemove("localStorage", "selectedSkiplineLocation");
    this.setState({
      location: null,
      locationInfo: null,
      priceList: null,
      promoList: null,
      vatRatesById: {},
      productsAll: [],
      products: [],
      combos: [],
      cartProducts: {},
      cartCombos: {},
      cartOpen: false,
      pickerOpen: false,
      pickerCombo: null,
      locationChangeConfirmation: null,
      infoProduct: null,
      view: "name",
      activeCategory: "All",
      search: "",
      orderLocked: false,
      lastOrderId: null,
      lastOrderGross: null,
      lastOrderContext: null,
      payUrl: "",
      paymentProviderConfirmed: false,
      paymentRegistrationFailed: false,
      paymentRegistering: false,
      clientSessionRequired: false,
      deliveryCandidate: null,
      deliverySavedAddress: null,
      deliveryAddress: null,
      deliveryResolution: null,
      deliveryQuery: "", deliverySearchResults: [],
      deliveryCountyId: "", deliveryCityId: "", deliveryBlock: "",
      deliveryEntrance: "", deliveryFloor: "", deliveryApartment: "",
      deliveryIntercom: "", deliveryCourierNotes: "", deliveryLastSavedDetails: "",
      deliveryCheckoutConflict: false,
      deliveryError: "",
    });
    this.pendingCreatedOrder = null;
  };

  renderLocationChangeDialog = () => {
    const confirmation = this.state.locationChangeConfirmation;
    const isDelivery = confirmation?.orderMode === "delivery";
    return (
      <Dialog open={Boolean(confirmation)} onClose={this.cancelLocationChange} fullWidth maxWidth="xs"
        aria-labelledby="skipline-change-selection-title" aria-describedby="skipline-change-selection-warning">
        <DialogTitle id="skipline-change-selection-title" sx={{ fontWeight: 900 }}>
          {this.t(isDelivery ? "skipline.confirmAddressChangeTitle" : "skipline.confirmLocationChangeTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="skipline-change-selection-warning" sx={{ color: "text.primary", lineHeight: 1.6 }}>
            {this.t(isDelivery ? "skipline.addressChangeConfirm" : "skipline.locationChangeConfirm")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: "wrap" }}>
          <Button autoFocus onClick={this.cancelLocationChange} sx={{ color: "text.primary", fontWeight: 700 }}>
            {this.t("skipline.keepCart")}</Button>
          <Button variant="contained" onClick={this.confirmLocationChange} disabled={this.locationChangeBlocked()}>
            {this.t("skipline.clearCartAndChange")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  formatAddress = (address) => {
    if (!address) return "";
    if (typeof address === "string") return address;
    return [
      address.street,
      address.number,
      address.local,
    ].filter(Boolean).join(", ");
  };

  getCityName = (loc) => {
    const city = loc?.address?.city;
    if (!city) return this.t("skipline.otherCity");
    if (typeof city === "string") return city;
    return city.name || city.iref || this.t("skipline.otherCity");
  };

  getCountyName = (loc) => {
    return loc?.countyName || "Alt județ";
  };

  groupLocationsByCounty = () => {
    const q = String(this.state.locationSearch || "").trim().toLowerCase();
    const groups = {};
    (this.state.locationList || []).forEach((loc) => {
      const countyName = this.getCountyName(loc);
      const cityName = this.getCityName ? this.getCityName(loc) : loc?.address?.city;
      const addressText = this.formatAddress(loc.address);
      const haystack = [
        countyName,
        cityName,
        loc.name,
        addressText,
      ].filter(Boolean).join(" ").toLowerCase();
      if (q && !haystack.includes(q)) {
        return;
      }
      if (!groups[countyName]) {
        groups[countyName] = [];
      }
      groups[countyName].push(loc);
    });

    return Object.keys(groups)
      .sort((a, b) => a.localeCompare(b, "ro"))
      .map((countyName) => ({
        countyName,
        locations: groups[countyName].sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", "ro")
        ),
      }));
  };

  renderOrderModeTabs = () => {
    if (!this.state.deliveryEnabled) return null;
    return (
      <Tabs
        value={this.state.orderMode}
        onChange={(_, mode) => this.switchOrderMode(mode)}
        variant="fullWidth"
        aria-label={this.t("skipline.orderMode")}
        sx={{ border: "1px solid", borderColor: BORDER, borderRadius: 2,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 800, minHeight: 44 },
          "& .Mui-selected": { bgcolor: "rgba(247,181,0,0.12)" } }}
      >
        <Tab value="pickup" label={this.t("skipline.pickupTab")} disabled={this.state.deliveryBusy} />
        <Tab value="delivery" label={this.t("skipline.deliveryTab")} disabled={this.state.deliveryBusy} />
      </Tabs>
    );
  };

  renderSelectionLegalNotice = () => (
    <Typography variant="caption" color="text.secondary"
      sx={{ display: "block", mt: 1.25, lineHeight: 1.4 }}>
      {this.tr("skipline.termsPrefix", {
        brand: this.getBrandName(), provider: this.state.paymentProvider,
      })}{" "}
      <Box component="a" href={withAppBasePath("/terms")} target="_blank" rel="noopener noreferrer"
        sx={{ color: "primary.main", fontWeight: 700, textDecoration: "none",
          "&:hover": { textDecoration: "underline" } }}>
        {this.t("skipline.terms")}
      </Box>
      {" "}{this.t("skipline.and")}{" "}
      <Box component="a" href={withAppBasePath("/privacy")} target="_blank" rel="noopener noreferrer"
        sx={{ color: "primary.main", fontWeight: 700, textDecoration: "none",
          "&:hover": { textDecoration: "underline" } }}>
        {this.t("skipline.privacy")}
      </Box>
      .
    </Typography>
  );

  renderSelectionHeader = () => {
    const brandNameUpper = this.getBrandNameUpper();
    const deliveryError = this.state.orderMode === "delivery" ? this.state.deliveryError : "";
    return (
      <AppBar position="sticky" elevation={0} sx={appBarSx}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, gap: 1 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            {this.renderBrandMark()}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap sx={{ fontWeight: 900, letterSpacing: 0.3,
                lineHeight: 1.1, visibility: brandNameUpper ? "visible" : "hidden" }}>
                {brandNameUpper || DEFAULT_BRAND_NAME.toLocaleUpperCase("ro-RO")}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", mt: 0.25 }}>
                {this.t(this.state.orderMode === "delivery" ? "skipline.deliveryTagline" : "skipline.tagline")}
              </Typography>
            </Box>
          </Stack>
          {this.renderLanguageSwitcher()}
        </Toolbar>
        {this.state.deliveryEnabled ? (
          <Container maxWidth="sm" sx={{ pb: 1 }}>
            {this.renderOrderModeTabs()}
          </Container>
        ) : null}
        {deliveryError ? (
          <Container maxWidth="sm" sx={{ pb: 1 }}>
            <Alert severity={this.state.deliveryUnavailable ? "warning" : "error"}
              action={<Button size="small" onClick={() => this.switchOrderMode("pickup")}>
                {this.t("skipline.switchPickup")}</Button>}>
              {deliveryError}
            </Alert>
          </Container>
        ) : null}
      </AppBar>
    );
  };

  renderDeliverySelect = () => {
    if (this.state.deliveryClientIdentityMismatch) {
      return <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">{this.t("skipline.deliveryIdentityChangedStop")}</Alert>
      </Container>;
    }
    if (this.state.deliveryGuestSessionExpired) {
      return <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">{this.t("skipline.deliveryGuestSessionExpired")}</Alert>
      </Container>;
    }
    const {
      deliveryQuery, deliverySearchResults, deliverySearching, deliveryError,
      deliveryAddresses, deliveryAddressesLoading, deliveryCandidate,
      deliverySavedAddress, deliveryAddress, deliveryResolution,
      deliveryBusy, deliveryCountyId, deliveryCityId, deliveryCounties,
      deliveryCities, deliveryNeedsCitySelection, guestEmail, recipientName, recipientPhone,
    } = this.state;
    const selected = deliveryCandidate || deliverySavedAddress;
    const address = deliveryAddress || deliveryCandidate || deliverySavedAddress;
    const isClient = this.props.clientPortalAuthenticated === true;
    const clientPortalBusy = this.props.authenticatedCheckoutEnabled && this.isClientPortalBusy();
    const recipientNameValid = hasFullRecipientName(recipientName);
    const recipientPhoneValid = Boolean(normalizeRomanianPhone(recipientPhone));
    const paymentEmailValid = isClient
      ? this.props.authenticatedCheckoutEnabled && this.props.clientProfile?.hasOrderEmail === true
      : isValidEmail(guestEmail);
    const paymentEmailError = !isClient && !paymentEmailValid;
    const continueDisabled = deliveryBusy || clientPortalBusy || !deliveryResolution ||
      !deliveryCountyId || !deliveryCityId || !address?.street || !address?.number ||
      !recipientNameValid || !recipientPhoneValid || !paymentEmailValid;
    const options = (values, selectedId, fallbackName) => {
      const mapped = (values || []).map((value) => ({
        id: String(value.iref ?? value.id ?? ""), name: value.name || value.label || "",
      })).filter((item) => item.id);
      if (selectedId && !mapped.some((item) => item.id === String(selectedId))) {
        mapped.unshift({ id: String(selectedId), name: fallbackName || String(selectedId) });
      }
      return mapped;
    };
    const countyOptions = options(deliveryCounties, deliveryCountyId, deliveryAddress?.countyName);
    const cityOptions = options(deliveryCities, deliveryCityId, deliveryAddress?.cityName);
    const fields = [
      ["deliveryBlock", "skipline.deliveryBlock", 80],
      ["deliveryEntrance", "skipline.deliveryEntrance", 32],
      ["deliveryFloor", "skipline.deliveryFloor", 16],
      ["deliveryApartment", "skipline.deliveryApartment", 128],
      ["deliveryIntercom", "skipline.deliveryIntercom", 80],
    ];
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: PAGE_BG, display: "flex", flexDirection: "column",
        "@supports (height: 100dvh)": { minHeight: "100dvh" } }}>
        {this.renderSelectionHeader()}
        <Container maxWidth="sm" sx={{ flex: 1, pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          {isClient ? (
            <Card sx={{ ...compactCardSx, mb: 2 }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
                <Typography variant="h6" fontWeight={900} gutterBottom>{this.t("skipline.savedAddresses")}</Typography>
                {deliveryAddressesLoading ? <CircularProgress size={24} /> : null}
                <Stack spacing={1}>
                  {deliveryAddresses.map((card) => (
                    <Box key={`${card.kind}-${card.id}`} sx={{ p: 1.5, border: "1px solid", borderColor: BORDER,
                      borderRadius: 2, bgcolor: deliverySavedAddress?.id === card.id ? "rgba(247,181,0,0.12)" : "#fff" }}>
                      <Button fullWidth disabled={deliveryBusy} onClick={() => this.selectSavedDeliveryAddress(card)}
                        sx={{ textAlign: "left", justifyContent: "flex-start", color: BRAND }}>
                        <Stack alignItems="flex-start">
                          <Typography fontWeight={800}>{[card.street, card.number].filter(Boolean).join(" ")}</Typography>
                          <Typography variant="caption">{[card.cityName, card.countyName].filter(Boolean).join(", ")}</Typography>
                          {card.requiresRevalidation === true || card.validCoordinates !== true ? <Typography variant="caption" color="error">
                            {this.t("skipline.deliveryRevalidateAddress")}</Typography> : null}
                        </Stack>
                      </Button>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" disabled={deliveryBusy} onClick={() => this.editSavedDeliveryAddress(card)}>{this.t("skipline.editAddress")}</Button>
                        {card.kind !== "primary" ? <Button size="small" color="error"
                          onClick={() => this.deleteSavedDeliveryAddress(card)} disabled={deliveryBusy}>
                          {this.t("skipline.deleteAddress")}</Button> : null}
                      </Stack>
                    </Box>
                  ))}
                  <Button variant="outlined" disabled={deliveryBusy} onClick={() => this.setState({
                    deliveryCandidate: null, deliverySavedAddress: null, deliveryEditingSaved: null,
                    deliveryAddress: null, deliveryResolution: null, deliveryQuery: "",
                    deliverySearchResults: [], deliverySaveAddress: false, deliveryError: "",
                    deliveryCountyId: "", deliveryCityId: "",
                  })}>{this.t("skipline.addAddress")}</Button>
                </Stack>
              </CardContent>
            </Card>
          ) : null}
          <Card sx={compactCardSx}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } },
              "& .MuiOutlinedInput-root": { minHeight: 44 },
              "& .MuiFormHelperText-root": { fontSize: 12, lineHeight: 1.5, mx: 0 } }}>
              <Typography id="skipline-delivery-address-heading" component="h1" variant="h5"
                sx={{ fontWeight: 900, letterSpacing: -0.3, mb: 2 }}>{this.t("skipline.deliveryAddress")}</Typography>
              {!deliverySavedAddress ? (
                <>
                  <TextField fullWidth size="small" disabled={deliveryBusy} label={this.t("skipline.searchDeliveryAddress")}
                    value={deliveryQuery} onChange={(event) => this.changeDeliveryQuery(event.target.value)}
                    helperText={this.t("skipline.deliveryPreciseAddressHelp")}
                    placeholder={this.t("skipline.deliveryAddressExample")}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                    autoComplete="street-address" />
                  {deliverySearching ? <CircularProgress size={22} sx={{ mt: 1 }} /> : null}
                  {deliverySearchResults.length > 0 ? <Stack role="listbox" spacing={0.5} sx={{ mt: 1 }}>
                    {deliverySearchResults.map((candidate) => (
                      <Button key={candidate.candidateToken || candidate.selectionToken} role="option"
                        onClick={() => this.selectDeliveryCandidate(candidate)}
                        sx={{ justifyContent: "flex-start", textAlign: "left" }}>{candidate.label}</Button>
                    ))}
                  </Stack> : null}
                </>
              ) : null}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5,
                fontSize: 12, lineHeight: 1.5, "& a": { color: "inherit" } }}>
                {this.t("skipline.geoapifyAttribution")}{" "}
                <a href="https://www.geoapify.com/" target="_blank" rel="noopener noreferrer">Geoapify</a>
                {" · "}
                <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
                  © OpenStreetMap contributors
                </a>
              </Typography>
              {selected ? (
                <Box component="form" id="skipline-delivery-address-form" noValidate aria-labelledby="skipline-delivery-address-heading"
                  aria-busy={deliveryBusy} sx={{ mt: 2 }}
                  onSubmit={(event) => { event.preventDefault(); this.continueDelivery(); }}>
                  <Stack component="fieldset" disabled={deliveryBusy} spacing={1.5}
                    sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(88px, 0.3fr)", gap: 1.5 }}>
                      <TextField fullWidth size="small" label={this.t("skipline.deliveryStreet")}
                        value={address?.street || ""} InputProps={{ readOnly: true }} required error={!address?.street} />
                      <TextField fullWidth size="small" label={this.t("skipline.deliveryNumber")}
                        value={address?.number || ""} InputProps={{ readOnly: true }} required error={!address?.number} />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5 }}>
                      <TextField select fullWidth size="small" required disabled={Boolean(deliverySavedAddress)}
                        label={this.t("skipline.deliveryCounty")}
                        error={!deliveryCountyId}
                        value={deliveryCountyId} onChange={(event) => {
                          const countyId = event.target.value;
                          this.setState({ deliveryCountyId: countyId, deliveryCityId: "", deliveryResolution: null });
                          this.loadDeliveryCities(countyId);
                        }}>
                        {countyOptions.map((county) => <MenuItem key={county.id} value={county.id}>{county.name}</MenuItem>)}
                      </TextField>
                      <TextField select fullWidth size="small" required disabled={Boolean(deliverySavedAddress)}
                        label={this.t("skipline.deliveryCity")}
                        error={!deliveryCityId}
                        value={deliveryCityId} onChange={(event) => this.setState({
                          deliveryCityId: event.target.value, deliveryResolution: null,
                        })}>
                        {cityOptions.map((city) => <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>)}
                      </TextField>
                    </Box>
                    {deliveryNeedsCitySelection ? <Alert severity="info">{this.t("skipline.deliveryCityMappingRequired")}</Alert> : null}
                    {deliveryCountyId && deliveryCityId && !deliveryResolution ? (
                      <Button variant="outlined" onClick={() => this.resolveDeliveryAddress()} disabled={deliveryBusy}>
                        {this.t("skipline.findDeliveryLocation")}</Button>
                    ) : null}
                    {deliveryResolution ? <Alert severity="success">
                      <Typography fontWeight={900}>{deliveryResolution.location.name}</Typography>
                      <Typography variant="body2">{this.t("skipline.closestDeliveryLocation")}</Typography>
                      {Number.isFinite(Number(deliveryResolution.estimateMinMinutes)) && Number.isFinite(Number(deliveryResolution.estimateMaxMinutes)) ? (
                        <Typography variant="body2">{this.tr("skipline.deliveryEstimate", {
                          min: deliveryResolution.estimateMinMinutes, max: deliveryResolution.estimateMaxMinutes,
                        })}</Typography>
                      ) : null}
                    </Alert> : null}
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, pt: 1.5, borderTop: "1px solid", borderColor: BORDER }}>
                      {this.t("skipline.deliveryDetails")}</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
                      {fields.map(([stateKey, label, maxLength]) => <TextField key={stateKey} fullWidth size="small"
                        label={this.t(label)} value={this.state[stateKey]}
                        inputProps={{ maxLength }}
                        onChange={(event) => this.setState({ [stateKey]: event.target.value })} />)}
                    </Box>
                    <TextField fullWidth size="small" multiline minRows={2} label={this.t("skipline.deliveryCourierNotes")}
                      value={this.state.deliveryCourierNotes} inputProps={{ maxLength: 500 }}
                      onChange={(event) => this.setState({ deliveryCourierNotes: event.target.value })} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, pt: 1.5, borderTop: "1px solid", borderColor: BORDER }}>
                      {this.t("skipline.deliveryRecipient")}</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
                      <TextField fullWidth size="small" required autoComplete="name" label={this.t("skipline.deliveryRecipientName")}
                        value={recipientName} inputProps={{ maxLength: 120 }}
                        error={!recipientNameValid}
                        helperText={!recipientNameValid ? this.t("skipline.deliveryInvalidName") : ""}
                        onChange={(event) => this.changeDeliveryRecipient("recipientName", event.target.value)} />
                      <TextField fullWidth size="small" required type="tel" autoComplete="tel" label={this.t("skipline.deliveryRecipientPhone")}
                        value={recipientPhone} inputProps={{ maxLength: 20 }}
                        error={!recipientPhoneValid}
                        helperText={!recipientPhoneValid ? this.t("skipline.deliveryInvalidPhone") : ""}
                        onChange={(event) => this.changeDeliveryRecipient("recipientPhone", event.target.value)} />
                    </Box>
                    {clientPortalBusy ? (
                      <Alert severity="info">{this.t("skipline.clientSessionRestoring")}</Alert>
                    ) : isClient ? (
                      <Alert severity={this.props.clientProfile?.hasOrderEmail === true ? "info" : "error"}>
                        {this.t(this.props.clientProfile?.hasOrderEmail === true
                          ? "skipline.clientAccountEmailReady" : "skipline.clientAccountEmailMissing")}
                      </Alert>
                    ) : (
                      <TextField fullWidth size="small" required type="email" autoComplete="email"
                        label={this.t("skipline.paymentEmail")} value={guestEmail}
                        inputProps={{ maxLength: 128, "aria-label": this.t("skipline.paymentEmail") }}
                        onChange={(event) => this.setState({
                          guestEmail: event.target.value,
                          ...(isValidEmail(event.target.value) && deliveryError === this.t("skipline.enterPaymentEmail") ? { deliveryError: "" } : {}),
                        })}
                        error={paymentEmailError}
                        helperText={paymentEmailError
                          ? this.t("skipline.enterPaymentEmail") : this.t("skipline.emailHelp")} />
                    )}
                    {isClient && deliveryCandidate && !deliverySavedAddress && !this.state.deliveryEditingSaved ? (
                      <FormControlLabel control={<Checkbox checked={this.state.deliverySaveAddress}
                        disabled={deliveryBusy || !deliveryResolution}
                        onChange={(event) => {
                          if (event.target.checked) this.saveDeliveryCandidate();
                          else this.setState({ deliverySaveAddress: false });
                        }} />}
                        label={this.t("skipline.saveDeliveryAddress")} />
                    ) : null}
                    {isClient && deliveryCandidate && deliverySavedAddress ? (
                      <Alert severity="success">{this.t("skipline.deliveryAddressSaved")}</Alert>
                    ) : null}
                    {isClient && this.state.deliveryEditingSaved && !deliverySavedAddress ? (
                      <Typography variant="caption">{this.t("skipline.deliveryEditSavesChanges")}</Typography>
                    ) : null}
                  </Stack>
                </Box>
              ) : null}
              {!selected && !isClient ? (
                <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 2, pt: 1.5,
                  borderTop: "1px solid", borderColor: BORDER, color: "text.secondary" }}>
                  <LockOutlined sx={{ fontSize: 16, mt: 0.15 }} />
                  <Typography variant="caption" sx={{ fontSize: 12, lineHeight: 1.5 }}>
                    {this.t("skipline.deliveryGuestAddressHelp")}</Typography>
                </Stack>
              ) : null}
              {this.renderSelectionLegalNotice()}
            </CardContent>
          </Card>
        </Container>
        {selected ? (
          <Box component="footer" sx={{ position: "sticky", bottom: 0, flexShrink: 0,
            zIndex: (theme) => theme.zIndex.appBar - 1, bgcolor: SURFACE,
            borderTop: "1px solid", borderColor: BORDER, pt: 1,
            pb: "calc(8px + env(safe-area-inset-bottom, 0px))" }}>
            <Container maxWidth="sm">
              <Button fullWidth variant="contained" size="large" type="submit"
                form="skipline-delivery-address-form" disabled={continueDisabled}
                startIcon={deliveryBusy ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ minHeight: 48, fontWeight: 900 }}>
                {this.t("skipline.continueMenu")}
              </Button>
            </Container>
          </Box>
        ) : null}
      </Box>
    );
  };

  toggleCounty = (countyName) => {
    this.setState((prev) => ({
      expandedCounty: prev.expandedCounty === countyName ? null : countyName,
    }));
  };

  productById = (id) => (this.state.productsAll || []).find(p => p.id === id) || null;

  normalizePriceList(raw){
    const obj = raw?.priceList || raw || {};
    const id = obj?.iref || obj?.id;
    const withVAT = !!obj?.withVAT;
    const currency = obj?.currency===0?"RON":"RON";
    const prodLines = arrify(obj,["productPrice_list","productPrices"]).map(l=>({ product: typeof l.product==='object'?(l.product?.iref||l.product?.id):l.product, value:Number(l.value), isActive:l?.isActive!==false })).filter(l=>l.product && Number.isFinite(l.value));
    const comboLines = arrify(obj,["productCombo_list","comboPrices"]).map(l=>({ combo: typeof l.productCombo==='object'?(l.productCombo?.iref||l.productCombo?.id):l.productCombo, value:Number(l.value), isActive:l?.isActive!==false })).filter(l=>l.combo && Number.isFinite(l.value));
    const priceByProductId = {}; prodLines.forEach(l=>{ if(l.isActive) priceByProductId[l.product]=l.value; });
    const priceByComboId = {}; comboLines.forEach(l=>{ if(l.isActive) priceByComboId[l.combo]=l.value; });
    return { id, withVAT, currency, priceByProductId, priceByComboId };
  }

  // add params: effectiveProd, promoByProductId
  normalizeProducts(arr, pl, catMap, effectiveProd, promoByProductId, vatRatesById = {}){
    return (arr||[]).map(p=>{
      const id = p.iref || p.id || p.slug || p.ean || p.sku;
      const name = p.name || "";
      const categoryId = p.pCategory || p.category || null;
      const subcategoryId = p.pSubCategory || null;
      const { vatId, vatRate } = normalizeVatDefinition(
        p.vat,
        vatRatesById,
        p.vatRate ?? p.vatSell
      );
      const status = p.status;
      const isForSale = p.isForSale ?? p.isSellable ?? true;
      const isSoldAlone = p.isSoldAlone ?? true;
      const productImages = p.productImage_product || [];
      const imgMain = productImages.find(i=>i.isMain) || productImages[0];
      const imageSmallUrl = ensureSmallImage(imgMain?.image);
      const categoryName = catMap.get(categoryId)?.name || null;

      const basePrice = pl.priceByProductId[id];
      const effPrice  = effectiveProd ? (typeof effectiveProd === 'function' ? effectiveProd(id) : effectiveProd[id]) : basePrice;
      const isPromo   = promoByProductId ? (promoByProductId[id] != null) : false;

      const hasPrice = (effPrice != null); // effective price drives sellability
      const sellable = status===2 && isForSale===true;
      const showStandalone = sellable && hasPrice && isSoldAlone!==false;

      return {
        ...p,
        id,name,categoryId,subcategoryId,categoryName,vatId,vatRate,status,isForSale,isSoldAlone,
        sellable,hasPrice,showStandalone,imageSmallUrl,
        isDeliverable: p.isDeliverable === true,
        basePrice, effPrice, isPromo
      };
    });
  }

  // add params: effectiveCombo, promoByProductId
  normalizeCombos(arr, pl, effectiveCombo, promoByProductId, promoByComboId = {}, catMap = new Map(), productsAll = [], deliveryMode = false) {
    const asKey = (v) => String(v);
    const hasKey = (obj, k) =>
      Object.prototype.hasOwnProperty.call(obj || {}, asKey(k));
    const getMaybeMap = (m, k) =>
      m?.get ? m.get(asKey(k)) : m?.[asKey(k)];

    const priceByComboId = pl?.priceByComboId || {};
    const priceByProductId = pl?.priceByProductId || {};

    return (arr || []).map((c) => {
      const id = asKey(c.iref ?? c.id);
      const name = c.name || "";
      const shortName = c.shortName || name;
      const categoryId = c.pCategory || null;
      const subcategoryId = c.pSubCategory || null;
      const categoryName = catMap.get(categoryId)?.name || null;

      const basePricePL = priceByComboId[id];
      const ecValue = effectiveCombo ? getMaybeMap(effectiveCombo, id) : undefined;
      const basePriceEff = ecValue ?? basePricePL;

      const basePriceFromPL = basePricePL;
      const isComboPromo =
        typeof basePriceEff === "number" &&
        (basePriceFromPL == null || basePriceEff !== basePriceFromPL);

      const comboImages = c.comboImage_productCombo || [];
      const imgMain = comboImages.find(i=>i.isMain) || comboImages[0];
      const imageSmallUrl = ensureSmallImage(imgMain?.image);

      const cats = (c.comboCategory_productCombo || []).map((k) => ({
        id: asKey(k.iref ?? k.id),
        name: k.name,
        categoryId: k.productCategory?.iref || k.productCategory || null,
        subcategoryId: k.productSubCategory?.iref || k.productSubCategory || null,
        priceType: k.priceType ?? 0,
        priceValueC: toCents(k.priceValue || 0),
      }));

      const fixed = (c.combo_productComboProduct || []).map((p) => ({
        id: asKey(p.iref ?? p.id),
        name: p.name,
        productId: asKey(p.product?.iref ?? p.product),
        priceType: p.priceType ?? 0,
        priceValueC: toCents(p.priceValue || 0),
      }));

      const sellable = c.status === 2 && (c.isForSale ?? true) === true;

      const inPL = hasKey(priceByComboId, id);
      const inPromo = hasKey(promoByComboId, id);

      const allFixedPriced = fixed.every((fi) =>
        fi.priceType === 0 || fi.priceType === 1
          ? hasKey(priceByProductId, fi.productId) ||
            hasKey(promoByProductId, fi.productId)
          : true
      );

      const isDeliverable = c.isDeliverable === true;
      const deliverableById = new Map(productsAll.map((product) => [String(product.id), product]));
      const allFixedDeliverable = fixed.every((item) => deliverableById.get(String(item.productId))?.isDeliverable === true);
      const allOptionsDeliverable = cats.every((category) => productsAll.some((product) => {
        if (product.isDeliverable !== true || product.sellable !== true || product.hasPrice !== true) return false;
        if (category.subcategoryId) return String(product.subcategoryId) === String(category.subcategoryId);
        return !category.categoryId || String(product.categoryId) === String(category.categoryId);
      }));
      const show = sellable && (inPL || inPromo) && allFixedPriced &&
        (!deliveryMode || (isDeliverable && allFixedDeliverable && allOptionsDeliverable));

      if (!show) return null;

      const priceToUse = ecValue ?? basePricePL;

      return {
        id,
        name,
        shortName,
        categoryId,
        subcategoryId,
        categoryName,
        basePriceC: toCents(priceToUse ?? 0),
        basePricePL: basePriceFromPL ?? null,
        isComboPromo,
        isDeliverable,
        imageSmallUrl,
        categories: cats,
        fixedItems: fixed,
      };
    }).filter(Boolean);
  }

  qtyForProduct = (id)=> (this.state.cartProducts[id]?.qty || 0);
  goHome = () => this.state.orderLocked ? null : this.setState({ view:"home", activeCategory:"All", search:"" });
  openCategory = (name) => this.state.orderLocked ? null : this.setState({ activeCategory: name, view:"browse" });

  ensureNameOrWarn = () => {
    /* const n = (this.state.customerName || "").trim();

    if (n.length < 2) {
      this.setState({
        view: "name",
        error: "Introdu numele pentru a continua.",
      });
      return false;
    } */

    return true;
  };

  getEffectiveMaps = (pl, promo) => {
    const promoByProductId = {};
    const promoByComboId   = {}; // future-proof if backend exposes combo promo lines
    const plist = promo?.promotionList || promo || {};
    const prodPromoLines = Array.isArray(plist.productPromotion_list) ? plist.productPromotion_list : [];
    prodPromoLines.forEach(x => {
      if (x?.isActive !== false && x?.product && Number.isFinite(Number(x.value))) {
        promoByProductId[x.product] = Number(x.value);
      }
    });
    // if you later expose comboPromotion_list, you can fill promoByComboId here

    const effectiveProd = { ...pl.priceByProductId };
    Object.keys(promoByProductId).forEach(pid => { effectiveProd[pid] = promoByProductId[pid]; });

    const effectiveCombo = { ...pl.priceByComboId };
    Object.keys(promoByComboId).forEach(cid => { effectiveCombo[cid] = promoByComboId[cid]; });

    return { promoByProductId, promoByComboId, effectiveProd, effectiveCombo };
  };

  priceSourceForCombo = (comboId) =>
    (this.state.promoByComboId[comboId] != null ? "promo" :
    this.state.priceList?.priceByComboId?.[comboId] != null ? "price" : null);

  // ——— price maps shortcuts
  getPriceContext = () => ({
    plByProd: this.state.priceList?.priceByProductId || {},
    priceListId: this.state.priceList?.id || null,
    promoByProd: this.state.promoByProductId || {},
    promoListId: this.state.promoList?.iref || null,
    // Optional: preload of other pricelists if you have them
    priceByProductByList: this.state.priceByProductByList || {}, // { [plId]: { [productId]: priceRON } }
  });

  getPriceFromList = (productId, listId) => {
    if (!listId) return null;
    const { priceByProductByList } = this.getPriceContext();
    const m = priceByProductByList[listId];
    return (m && m[productId] != null) ? Number(m[productId]) : null;
  };

  // Decide where a product’s price comes from given the combo slot type (0|1|2)
  priceSourceForProduct = (productId, slotType) => {
    const { plByProd, priceListId, promoByProd, promoListId } = this.getPriceContext();
    const hasPL    = (plByProd[productId] != null) || (this.getPriceFromList(productId, priceListId) != null);
    const hasPromo = !!promoListId && (promoByProd[productId] != null);

    if (slotType === 1) return hasPromo ? "promo" : (hasPL ? "pl" : null);
    if (slotType === 0) return hasPL ? "pl" : (hasPromo ? "promo" : null);   // anchor: PL first, else promo
    return hasPL ? "pl" : (hasPromo ? "promo" : null);                        // type‑2: prefer PL
  };

  // Which list id to annotate on a component (PL id or promo iref)
  priceListIdForProduct = (productId, slotType) => {
    const { priceListId, promoListId } = this.getPriceContext();
    const src = this.priceSourceForProduct(productId, slotType);
    return src === "promo" ? (promoListId || null) : (priceListId || null);
  };

  // *** The anchor used for type‑0 allocation (per product) ***
  anchorForType0 = (productId) => {
    const { plByProd, promoByProd } = this.getPriceContext();
    const src = this.priceSourceForProduct(productId, 0);

    if (src === "promo") {
      return Number(promoByProd[productId] ?? 0);
    }

    // src === "pl": try that product’s own PL id (if different), else current PL
    const compPLId = this.priceListIdForProduct(productId, 0);
    const vFromCompPL = this.getPriceFromList(productId, compPLId);
    if (vFromCompPL != null) return Number(vFromCompPL);

    const vCur = plByProd[productId];
    if (vCur != null) return Number(vCur);

    // final fallback
    return Number(promoByProd[productId] ?? 0);
  };

  addProduct = (p) => {
    if (this.state.orderLocked || this.state.deliveryOrderOutcomeUnknown) return;
    if (this.state.orderMode === "delivery" && p?.isDeliverable !== true) return;
    if (!this.ensureNameOrWarn()) return;
    const effUnit = (this.state.promoByProductId?.[p.id] ?? this.state.priceList?.priceByProductId?.[p.id] ?? 0);
    const unitC = toCents(effUnit);
    const priceSource =
      (this.state.promoByProductId?.[p.id] != null) ? 'promo'
      : (this.state.priceList?.priceByProductId?.[p.id] != null) ? 'price'
      : null;

    this.setState(({ cartProducts }) => ({
      cartProducts: {
        ...cartProducts,
        [p.id]: {
          id: p.id,
          name: p.name,
          imageSmallUrl: p.imageSmallUrl,
          unitPriceC: unitC,
          qty: (cartProducts[p.id]?.qty || 0) + 1,
          vatId: p.vatId,
          vatRate: p.vatRate,
          priceSource,
        },
      },
    }));
  };

  incProduct = (id)=> {
    if (this.state.orderLocked || this.state.deliveryOrderOutcomeUnknown) return null;
    return this.setState(({cartProducts})=>({ cartProducts:{...cartProducts,[id]:{...cartProducts[id], qty:(cartProducts[id]?.qty||0)+1}} }));
  };
  decProduct = (id)=>{
    if (this.state.orderLocked || this.state.deliveryOrderOutcomeUnknown) return;
    this.setState(({cartProducts})=>{ const line=cartProducts[id]; if(!line) return {cartProducts}; const next={...cartProducts}; if(line.qty<=1) delete next[id]; else next[id]={...line, qty: line.qty-1}; return {cartProducts: next}; });
  };

  openComboPicker = (combo)=> { if (this.state.orderLocked || this.state.deliveryOrderOutcomeUnknown) return; if (this.state.orderMode === "delivery" && combo?.isDeliverable !== true) return; if (!this.ensureNameOrWarn()) return; this.setState({ pickerOpen:true, pickerCombo:combo }); };
  closeComboPicker = ()=> this.setState({ pickerOpen:false, pickerCombo:null });

  confirmComboFromPicker = (selections, selectionDetails, meta) => {
    if (this.state.deliveryOrderOutcomeUnknown) return;
    const { pickerCombo } = this.state;
    if (this.state.orderMode === "delivery" &&
      (pickerCombo?.isDeliverable !== true || selectionDetails.some((item) => this.productById(item.productId)?.isDeliverable !== true))) return;
    // attach fixed items + vatId from product catalog
    const fixedItems = (pickerCombo.fixedItems || []).map(fi => {
      const prod = this.productById(fi.productId);
      return {
        name: fi.name,
        productId: fi.productId,
        priceType: fi.priceType,        // 0/1/2
        priceValueC: fi.priceValueC,    // cents
        vatId: prod?.vatId || prod?.vat || null,
        vatRate: prod?.vatRate ?? null,
      };
    });

    const key = pickerCombo.id + '|' + Object.values(selections).map(x => (x.id || x)).sort().join(',');

    this.setState(({ cartCombos }) => ({
      cartCombos: {
        ...cartCombos,
        [key]: {
          id: pickerCombo.id,
          name: pickerCombo.name,
          imageSmallUrl: pickerCombo.imageSmallUrl,
          qty: (cartCombos[key]?.qty || 0) + 1,
          basePriceC: pickerCombo.basePriceC,
          selections,
          selectionDetails,
          fixedItems,
          priceSource: meta?.isComboPromo ? 'promo' : 'price', // 👈
        },
      },
      pickerOpen: false,
      pickerCombo: null,
    }));
  };

  incCombo = (key)=> {
    if (this.state.orderLocked || this.state.deliveryOrderOutcomeUnknown) return null;
    return this.setState(({cartCombos})=>({ cartCombos:{...cartCombos,[key]:{...cartCombos[key], qty: cartCombos[key].qty+1}} }));
  };
  decCombo = (key)=>{
    if (this.state.orderLocked || this.state.deliveryOrderOutcomeUnknown) return;
    this.setState(({cartCombos})=>{ const line=cartCombos[key]; if(!line) return {cartCombos}; const next={...cartCombos}; if(line.qty<=1) delete next[key]; else next[key]={...line, qty: line.qty-1}; return {cartCombos: next}; });
  };
  clearCart = ()=> {
    if (this.state.orderLocked || this.state.deliveryOrderOutcomeUnknown) return null;
    return this.setState({ cartProducts:{}, cartCombos:{} });
  };

  vatRate = (vatId, explicitRate = null) =>
    normalizeVatDefinition(
      vatId,
      this.state.vatRatesById,
      explicitRate
    ).vatRate;

  requireVatRate = (item) => {
    const rate = this.vatRate(
      item?.vatId ?? item?.vat,
      item?.vatRate
    );
    if (rate == null) {
      throw new Error(this.t("skipline.vatUnavailable"));
    }
    return rate;
  };

  round2(n) {
    return Math.round(Number(n) * 100) / 100;
  }

  allocType0PerUnitRON(basePriceRON, type0, priceList) {
    const P0 = type0.map(s => {
      const plv = Number(this.state.priceList?.priceByProductId?.[s.productId] ?? 0);
      if (plv > 0) return plv;
      const pv = Number(this.state.promoByProductId?.[s.productId] ?? 0);
      return pv > 0 ? pv : 0;
    });
    const S0 = P0.reduce((a, b) => a + b, 0);

    if (!type0?.length) return [];
    if (S0 <= 0) {
      // Even split if no standalone prices available
      const each = this.round2(basePriceRON / type0.length);
      const out = Array(type0.length).fill(each);
      const drift = this.round2(basePriceRON - out.reduce((a, b) => this.round2(a + b), 0));
      // add/pad the remainder (+/-0.01) to the first item
      if (Math.abs(drift) >= 0.01) out[0] = this.round2(out[0] + drift);
      return out;
    }

    // Proportional round per component
    const raw = P0.map(p => this.round2(basePriceRON * (p / S0)));
    let sum = this.round2(raw.reduce((a, b) => this.round2(a + b), 0));
    let rem = this.round2(basePriceRON - sum); // can be -0.01, 0, or +0.01

    if (Math.abs(rem) >= 0.01) {
      // give/take the cent to the product with the largest P0 (most significant)
      const idx = P0.indexOf(Math.max(...P0));
      raw[idx] = this.round2(raw[idx] + rem);
    }
    return raw;
  }

  computeTotals(){
    const { cartProducts, cartCombos } = this.state;
    let totalGrossC = 0, totalVatC = 0;

    // ----- products -----
    Object.values(cartProducts).forEach(l => {
      const unit = l.unitPriceC;                // cents
      const qty  = l.qty;
      const rate = this.vatRate(l.vatId, l.vatRate) ?? 0; // %
      const lineC = unit * qty;
      const vatC  = Math.round(lineC * rate / (100 + rate));
      totalGrossC += lineC;
      totalVatC   += vatC;
    });

    // ----- combos -----
    Object.values(cartCombos).forEach(line => {
      const { basePriceC, qty, selectionDetails = [], fixedItems = [] } = line;

      const allItems = [
        ...selectionDetails,
        ...fixedItems.map(fi => ({
          productId: fi.productId,
          priceType: fi.priceType,
          priceValueC: fi.priceValueC,
          vatId: fi.vatId,
          vatRate: fi.vatRate,
          name: fi.name,
        })),
      ];

      const type1 = allItems.filter(s => s.priceType === 1);
      const type2 = allItems.filter(s => s.priceType === 2);
      const type0 = allItems.filter(s => s.priceType === 0);

      // per‑unit Σ(type‑1) from effective product prices (promo > list), cents
      const effProdMap = this.state.effectiveProd || {};
      const v1 = type1.map(s => s.productId
        ? toCents(((typeof effProdMap === "function") ? effProdMap(s.productId) : effProdMap[s.productId]) || 0)
        : 0
      );
      const sum1 = v1.reduce((a, b) => a + b, 0);

      // per‑unit Σ(type‑2) from set values, cents
      const v2 = type2.map(s => s.priceValueC || 0);
      const sum2 = v2.reduce((a, b) => a + b, 0);

      // per‑unit full line value
      const unitLineC = (basePriceC || 0) + sum1 + sum2;

      // allocate BASE ONLY (basePriceC) to type‑0 using pricelist proportions (RON per unit)
      const P0 = type0.map(s => this.anchorForType0(s.productId));
      const S0 = P0.reduce((a, b) => a + b, 0);
      const baseUnitRON = fromCents(basePriceC || 0);

      let alloc0UnitRON = [];
      if (type0.length) {
        if (S0 <= 0) {
          const each = Math.round((baseUnitRON / type0.length) * 100) / 100;
          alloc0UnitRON = Array(type0.length).fill(each);
          const drift = Math.round((baseUnitRON - alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0)) * 100)/100;
          if (Math.abs(drift) >= 0.01) alloc0UnitRON[0] = Math.round((alloc0UnitRON[0] + drift) * 100)/100;
        } else {
          alloc0UnitRON = P0.map(p => Math.round((baseUnitRON * (p / S0)) * 100) / 100);
          let sum = Math.round(alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0) * 100)/100;
          let rem = Math.round((baseUnitRON - sum) * 100) / 100;
          if (Math.abs(rem) >= 0.01) {
            const idx = P0.indexOf(Math.max(...P0));
            alloc0UnitRON[idx] = Math.round((alloc0UnitRON[idx] + rem) * 100) / 100;
          }
        }
      }

      // VAT per unit from each component
      let unitVatC = 0;
      type0.forEach((s, i) => {
        const rate = this.vatRate(s.vatId, s.vatRate) ?? 0;
        unitVatC += Math.round(toCents(alloc0UnitRON[i]) * rate / (100 + rate));
      });
      type1.forEach((s, i) => {
        const rate = this.vatRate(s.vatId, s.vatRate) ?? 0;
        unitVatC += Math.round(v1[i] * rate / (100 + rate));
      });
      type2.forEach((s, i) => {
        const rate = this.vatRate(s.vatId, s.vatRate) ?? 0;
        unitVatC += Math.round(v2[i] * rate / (100 + rate));
      });

      totalGrossC += unitLineC * qty;
      totalVatC   += unitVatC * qty;
    });

    const merchandiseGross = fromCents(totalGrossC);
    const resolution = this.state.orderMode === "delivery" ? this.state.deliveryResolution : null;
    const charge = resolution
      ? deliveryCharge(merchandiseGross, resolution.deliveryFee, resolution.freeDeliveryThreshold)
      : null;
    const deliveryFee = charge?.fee || 0;
    const deliveryFeeVat = deliveryFee > 0 ? Number(resolution?.deliveryFeeVat || 0) : 0;
    return {
      merchandiseGross,
      deliveryFee,
      freeDeliveryThreshold: resolution ? Number(resolution.freeDeliveryThreshold) : 0,
      amountRemainingForFreeDelivery: charge?.remaining || 0,
      totalGross: fromCents(totalGrossC + cents(deliveryFee)),
      totalVat: fromCents(totalVatC + cents(deliveryFeeVat)),
      totalNet: fromCents(totalGrossC - totalVatC + cents(deliveryFee) - cents(deliveryFeeVat)),
    };
  }

  buildOrderPayload(
    orderContext = ORDER_REQUEST_CONTEXT.guest
  ) {
    const { priceList, cartProducts, cartCombos, guestEmail, promoList } = this.state;
    const deliveryMode = this.state.orderMode === "delivery";
    if (deliveryMode) {
      if (!this.state.deliveryResolution?.resolutionToken ||
          String(this.state.deliveryResolution?.location?.iref) !== String(this.state.location)) {
        throw new Error(this.t("skipline.deliveryResolveFailed"));
      }
      if (!hasFullRecipientName(this.state.recipientName) || !normalizeRomanianPhone(this.state.recipientPhone)) {
        throw new Error(this.t("skipline.deliveryRecipientRequired"));
      }
      if (Object.values(cartProducts).some((line) => this.productById(line.id)?.isDeliverable !== true) ||
          Object.values(cartCombos).some((line) => this.state.combos.find((combo) => combo.id === line.id)?.isDeliverable !== true ||
            [...(line.selectionDetails || []), ...(line.fixedItems || [])].some((item) => this.productById(item.productId)?.isDeliverable !== true))) {
        throw new Error(this.t("skipline.deliveryProductUnavailable"));
      }
    }

    const orderProductEntry_order = [];
    const orderProductPromotionEntry_order = [];

    Object.values(cartProducts).forEach((l) => {
      const unitC = l.unitPriceC;
      const qty = l.qty || 1;
      const rate = this.requireVatRate(l);

      const lineC = unitC * qty;
      const lineVat = Math.round(lineC * rate / (100 + rate));

      const common = {
        value: fromCents(lineC),
        quantity: qty,
        vat: fromCents(lineVat),
      };

      const isPromoProduct =
        !!(promoList?.iref) &&
        this.state.promoByProductId?.[l.id] != null;

      if (isPromoProduct) {
        orderProductPromotionEntry_order.push({
          ...common,
          ProductPromotion: {
            promotionList: this.state.promoList.iref,
            product: l.id,
            value: fromCents(unitC),
          },
        });
      } else {
        orderProductEntry_order.push({
          ...common,
          ProductPrice: {
            priceList: priceList.id,
            product: l.id,
            value: fromCents(unitC),
          },
        });
      }
    });

    const orderComboEntry_order = [];
    const orderComboPromotionEntry_order = [];

    Object.values(cartCombos).forEach((line) => {
      const { basePriceC, qty = 1, selectionDetails = [], id, fixedItems = [] } = line;

      const allItems = [
        ...selectionDetails,
        ...fixedItems.map((fi) => {
          const prod = this.productById(fi.productId);
          return {
            productId: fi.productId,
            priceType: fi.priceType,
            priceValueC: fi.priceValueC,
            vatId: prod?.vatId || prod?.vat || null,
            vatRate: prod?.vatRate ?? fi.vatRate ?? null,
            name: fi.name,
          };
        }),
      ];

      const type1 = allItems.filter((s) => s.priceType === 1);
      const type2 = allItems.filter((s) => s.priceType === 2);
      const type0 = allItems.filter((s) => s.priceType === 0);

      const effProdMap = this.state.effectiveProd || {};
      const v1 = type1.map((s) =>
        s.productId
          ? toCents(
              ((typeof effProdMap === "function")
                ? effProdMap(s.productId)
                : effProdMap[s.productId]) || 0
            )
          : 0
      );

      const sum1 = v1.reduce((a, b) => a + b, 0);

      const v2 = type2.map((s) => s.priceValueC || 0);
      const sum2 = v2.reduce((a, b) => a + b, 0);

      const unitLineC = (basePriceC || 0) + sum1 + sum2;
      const unitLineRON = fromCents(unitLineC);

      const P0 = type0.map(s => this.anchorForType0(s.productId));
      const S0 = P0.reduce((a, b) => a + b, 0);
      const baseUnitRON = fromCents(basePriceC || 0);

      let alloc0UnitRON = [];

      if (type0.length) {
        if (S0 <= 0) {
          const each = Math.round((baseUnitRON / type0.length) * 100) / 100;
          alloc0UnitRON = Array(type0.length).fill(each);

          const drift =
            Math.round(
              (
                baseUnitRON -
                alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0)
              ) * 100
            ) / 100;

          if (Math.abs(drift) >= 0.01) {
            alloc0UnitRON[0] = Math.round((alloc0UnitRON[0] + drift) * 100) / 100;
          }
        } else {
          alloc0UnitRON = P0.map(p => Math.round((baseUnitRON * (p / S0)) * 100) / 100);

          let sum =
            Math.round(
              alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0) * 100
            ) / 100;

          let rem = Math.round((baseUnitRON - sum) * 100) / 100;

          if (Math.abs(rem) >= 0.01) {
            const idx = P0.indexOf(Math.max(...P0));
            alloc0UnitRON[idx] = Math.round((alloc0UnitRON[idx] + rem) * 100) / 100;
          }
        }
      }

      const components = [];

      type1.forEach((s, i) => {
        const perUnitRON = fromCents(v1[i]);
        const valRON = Math.round(perUnitRON * qty * 100) / 100;
        const rate = this.requireVatRate(s);
        const vVat = Math.round((valRON * rate / (100 + rate)) * 100) / 100;

        components.push({
          value: valRON,
          quantity: qty,
          vat: vVat,
          product: s.productId,
          priceType: 1,
          priceList: this.priceListIdForProduct(s.productId, 1),
        });
      });

      type2.forEach((s, i) => {
        const perUnitRON = fromCents(v2[i]);
        const valRON = Math.round(perUnitRON * qty * 100) / 100;
        const rate = this.requireVatRate(s);
        const vVat = Math.round((valRON * rate / (100 + rate)) * 100) / 100;

        components.push({
          value: valRON,
          quantity: qty,
          vat: vVat,
          product: s.productId,
          priceType: 2,
          priceList: this.priceListIdForProduct(s.productId, 2),
        });
      });

      type0.forEach((s, i) => {
        const valRON = Math.round(alloc0UnitRON[i] * qty * 100) / 100;
        const rate = this.requireVatRate(s);
        const vVat = Math.round((valRON * rate / (100 + rate)) * 100) / 100;

        components.push({
          value: valRON,
          quantity: qty,
          vat: vVat,
          product: s.productId,
          priceType: 0,
          priceList: this.priceListIdForProduct(s.productId, 0),
        });
      });

      const lineValueRON = Math.round(unitLineRON * qty * 100) / 100;
      const lineVatRON =
        Math.round(
          components.reduce((a, b) => a + Number(b.vat || 0), 0) * 100
        ) / 100;

      const lineCommon = {
        value: lineValueRON,
        quantity: qty,
        vat: lineVatRON,
      };

      const usePromoCombo =
        !!(promoList?.iref) &&
        this.state.promoByComboId?.[id] != null;

      if (usePromoCombo) {
        orderComboPromotionEntry_order.push({
          ...lineCommon,
          ComboPromotion: {
            promotionList: this.state.promoList.iref,
            productCombo: id,
            value: fromCents(basePriceC),
          },
          OrderComboPromotionProductsEntry_OrderComboPromotionEntry: components,
        });
      } else {
        orderComboEntry_order.push({
          ...lineCommon,
          ComboPrice: {
            priceList: this.state.priceList.id,
            productCombo: id,
            value: fromCents(basePriceC),
          },
          OrderComboProductsEntry_OrderComboEntry: components,
        });
      }
    });

    const allLines = [
      ...orderProductEntry_order,
      ...orderProductPromotionEntry_order,
      ...orderComboEntry_order,
      ...orderComboPromotionEntry_order,
    ];

    const grossC = allLines.reduce((a, x) => a + cents(x.value), 0);
    const vatC = allLines.reduce((a, x) => a + cents(x.vat), 0);
    const charge = deliveryMode
      ? deliveryCharge(fromc(grossC), this.state.deliveryResolution.deliveryFee,
        this.state.deliveryResolution.freeDeliveryThreshold)
      : { fee: 0, remaining: 0 };
    if (!charge) throw new Error(this.t("skipline.deliveryFeeUnavailable"));
    const deliveryFeeVat = charge.fee > 0
      ? Number(this.state.deliveryResolution.deliveryFeeVat) : 0;
    if (!Number.isFinite(deliveryFeeVat) || deliveryFeeVat < 0 || deliveryFeeVat > charge.fee) {
      throw new Error(this.t("skipline.deliveryFeeUnavailable"));
    }
    const totalGrossC = grossC + cents(charge.fee);
    const totalVatC = vatC + cents(deliveryFeeVat);

    const cleanEmail = String(guestEmail || "").trim().toLowerCase();

    const brandName = this.getBrandName();
    const descriptionBase =
      `Comanda ${this.state.locationInfo?.name || brandName}`;

    const payload = {
      type: 1,
      status: 0,
      deliveryType: deliveryMode ? 1 : 0,
      description: this.isClientOrderContext(orderContext)
        ? descriptionBase
        : `${descriptionBase} - ${cleanEmail}`,
      currency: 0,
      location: this.state.location,

      numberOfLines:
        orderProductEntry_order.length +
        orderProductPromotionEntry_order.length +
        orderComboEntry_order.length +
        orderComboPromotionEntry_order.length,

      totalAmount: fromc(totalGrossC),
      totalAmountWithoutVAT: fromc(totalGrossC - totalVatC),
      discountValue: 0,
      discountValueWithoutVAT: 0,

      transaction_order: [
        {
          type: 0,
          status: 0,
          platform: 2,
          amount: fromc(totalGrossC),
        },
      ],

      orderProductEntry_order,
      orderComboEntry_order,
      orderProductPromotionEntry_order,
      orderComboPromotionEntry_order,
    };

    if (deliveryMode) {
      payload.skiplineDelivery = {
        resolutionToken: this.state.deliveryResolution.resolutionToken,
        recipientName: String(this.state.recipientName).trim(),
        phone: normalizeRomanianPhone(this.state.recipientPhone),
        block: String(this.state.deliveryBlock || "").trim(),
        entrance: String(this.state.deliveryEntrance || "").trim(),
        floor: String(this.state.deliveryFloor || "").trim(),
        apartment: String(this.state.deliveryApartment || "").trim(),
        intercom: String(this.state.deliveryIntercom || "").trim(),
        courierNotes: String(this.state.deliveryCourierNotes || "").trim(),
      };
      if (charge.fee > 0) {
        if (!this.state.deliveryResolution.deliveryFeeProductId || !this.state.deliveryResolution.priceListId) {
          throw new Error(this.t("skipline.deliveryFeeUnavailable"));
        }
        payload.deliveryTax = {
          orderProductEntry_order: [{
            value: charge.fee,
            quantity: 1,
            vat: deliveryFeeVat,
            ProductPrice: {
              priceList: this.state.deliveryResolution.priceListId,
              product: this.state.deliveryResolution.deliveryFeeProductId,
              value: Number(this.state.deliveryResolution.deliveryFee),
            },
          }],
        };
      }
    }

    const v = validateOrderPayload(payload, this.state.effectiveProd || {});
    if (!v.ok) {
      throw new Error(v.errors.join(" · "));
    }

    return payload;
  }

  openPayU = () => {
    const payUrl = toSafePaymentRedirectUrl(this.state.payUrl);
    if (!payUrl) {
      this.setState({ error: this.t("skipline.noPaymentLink") });
      return;
    }
    window.open(payUrl, "_blank", "noopener,noreferrer");
    this.setState({
      paymentChecking: true,
      paymentMessage: this.tr("payment.cardOnlyProvider", { provider: this.state.paymentProvider }),
    }, this.startPolling);
  };

  startPolling = () => { this.stopPolling(); this.pollTimer = setInterval(this.checkOrderStatus, 5000); };
  stopPolling = () => { if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; } };

  isClientOrderContext = (context) =>
    context === ORDER_REQUEST_CONTEXT.client;

  getOrderRequestContext = () =>
    selectOrderRequestContext({
      authenticatedCheckoutEnabled:
        this.props.authenticatedCheckoutEnabled,
      isAuthenticated: this.props.clientPortalAuthenticated,
    });

  isClientPortalBusy = () =>
    Boolean(
      this.props.clientPortalBusy ??
        this.props.clientPortalRestoring
    );

  createOrderForContext = async (context, payload) => {
    if (this.isClientOrderContext(context)) {
      return this.props.addAuthenticatedOrder(payload);
    }
    await this.ensurePublicSession();
    return this.api.post(ENDPOINTS.createOrder(), payload);
  };

  registerPayForContext = async (context, orderId, guestEmail) => {
    if (this.isClientOrderContext(context)) {
      return this.props.registerAuthenticatedPay(orderId);
    }
    await this.ensurePublicSession();
    return this.api.post(ENDPOINTS.registerPay(), {
      orderId,
      order: orderId,
      iref: orderId,
      email: guestEmail,
      guestEmail,
      checkoutToken: this.guestCheckoutTokenForOrder(orderId),
    });
  };

  guestCheckoutTokenForOrder = (orderId) => {
    const order = this.pendingCreatedOrder;
    if (order?.context === ORDER_REQUEST_CONTEXT.guest &&
        String(order.id) === String(orderId) &&
        typeof order.checkoutToken === "string" && order.checkoutToken.length > 0) {
      return order.checkoutToken;
    }
    throw Object.assign(new Error(this.t("skipline.guestSessionExpiredStop")), {
      status: 401, code: "GUEST_SKIPLINE_SESSION_EXPIRED",
    });
  };

  registerKnownOrder = async (orderId, context, guestEmail) => {
    if (this.registerPayPromise) return this.registerPayPromise;

    const pending = (async () => {
      this.setState({
        placing: true,
        paymentRegistering: true,
        paymentRegistrationFailed: false,
        clientSessionRequired: false,
        error: "",
      });
      try {
        const payRes = await this.registerPayForContext(
          context,
          orderId,
          guestEmail
        );
        const payUrl = extractRedirectPaymentUrl(payRes, context);
        if (!payUrl) throw new Error(this.t("skipline.noPaymentLink"));

        this.setState({
          placing: false,
          paymentRegistering: false,
          paymentRegistrationFailed: false,
          payUrl,
          paymentSheetOpen: true,
          paymentMessage: this.t("skipline.orderRegistered"),
          paymentChecking: false,
          payIframeError: true,
          cartOpen: false,
        });
        return true;
      } catch (error) {
        const clientSessionRequired =
          this.isClientOrderContext(context) && error?.status === 401;
        const guestSessionExpired = !this.isClientOrderContext(context) &&
          (error?.status === 401 || error?.code === "GUEST_SKIPLINE_SESSION_EXPIRED");
        if (guestSessionExpired) this.stopPolling();
        this.setState({
          placing: false,
          paymentRegistering: false,
          paymentRegistrationFailed: true,
          clientSessionRequired,
          payUrl: "",
          paymentSheetOpen: true,
          paymentChecking: false,
          payIframeError: true,
          deliveryGuestSessionExpired: guestSessionExpired,
          paymentMessage: clientSessionRequired
            ? this.t("skipline.clientSessionRequired")
            : guestSessionExpired
              ? this.t("skipline.guestSessionExpiredStop")
              : this.t("skipline.paymentRegistrationFailed"),
          error: clientSessionRequired
            ? this.t("skipline.clientSessionRequired")
            : guestSessionExpired
              ? this.t("skipline.guestSessionExpiredStop")
              : this.t("skipline.paymentRegistrationFailed"),
        });
        return false;
      }
    })();

    this.registerPayPromise = pending;
    try {
      return await pending;
    } finally {
      if (this.registerPayPromise === pending) {
        this.registerPayPromise = null;
      }
    }
  };

  retryPaymentRegistration = async () => {
    const order = this.pendingCreatedOrder;
    if (!order || this.state.paymentRegistering) return;
    await this.registerKnownOrder(order.id, order.context, order.guestEmail);
  };

  restoreClientSession = async (credentials) => {
    const result = await this.props.loginClient(credentials);
    if (!result?.ok) return result;
    if (this.suspendedClientOrderOwnerId || this.suspendedClientUnknownOwnerId) return result;
    const hasKnownOrder = Boolean(this.pendingCreatedOrder);
    this.setState({
      clientSessionRequired: false,
      ...(hasKnownOrder ? {} : {
        paymentSheetOpen: false,
        error: "",
      }),
    });
    this.props.refreshAfterOrderOutcome();
    if (this.state.paymentRegistrationFailed) {
      this.retryPaymentRegistration();
    }
    return result;
  };

  logoutClientBeforeOrder = () => {
    if (
      this.state.orderLocked ||
      this.state.deliveryOrderOutcomeUnknown ||
      this.state.placing ||
      this.orderAddInFlight ||
      !this.props.logoutClient
    ) {
      return;
    }
    this.setState({
      clientSessionRequired: false,
      error: "",
    });
    setSkiplineAccessToken(null);
    Promise.resolve(this.props.logoutClient()).catch(() => {
      // The context has already completed the authoritative local logout.
    });
  };

  retryDeliveryEligibility = async () => {
    const previousLocation = this.state.location;
    const previousResolution = this.state.deliveryResolution;
    const previousPriceListId = this.state.priceList?.id;
    const previousPromoListId = this.state.promoList?.iref || null;
    const previousEffectiveProd = this.state.effectiveProd || {};
    const previousEffectiveCombo = this.state.effectiveCombo || {};
    const response = await this.resolveDeliveryAddress();
    if (!response) return;
    if (String(response.location.iref) !== String(previousLocation)) {
      if (!window.confirm(this.t("skipline.deliveryLocationChangedConfirm"))) {
        this.setState({ deliveryCheckoutConflict: true });
        return;
      }
      this.setState({ cartProducts: {}, cartCombos: {}, deliveryCheckoutConflict: false,
        location: response.location.iref, view: "name", loading: true });
      const menuReady = await this.bootstrap(response.location.iref, response.priceListId);
      if (!menuReady) return;
      return;
    }
    const menuReady = await this.bootstrap(response.location.iref, response.priceListId);
    if (!menuReady) return;
    const quoteFields = ["priceListId", "deliveryFee", "deliveryFeeVat",
      "deliveryFeeProductId", "freeDeliveryThreshold"];
    const sameQuote = quoteFields.every((field) =>
      String(previousResolution?.[field] ?? "") === String(response?.[field] ?? ""));
    const sameMenu = String(previousPriceListId ?? "") === String(this.state.priceList?.id ?? "") &&
      String(previousPromoListId ?? "") === String(this.state.promoList?.iref ?? "");
    const sameEffectivePrice = (before, after, id) =>
      String(before?.[id] ?? "") === String(after?.[id] ?? "");
    const productsMatch = Object.values(this.state.cartProducts || {}).every((line) => {
      const product = this.productById(line.id);
      const source = this.state.promoByProductId?.[line.id] != null ? "promo" : "price";
      return product?.isDeliverable === true && line.priceSource === source &&
        toCents(this.state.effectiveProd?.[line.id]) === line.unitPriceC &&
        sameEffectivePrice(previousEffectiveProd, this.state.effectiveProd, line.id);
    });
    const combosMatch = Object.values(this.state.cartCombos || {}).every((line) => {
      const combo = this.state.combos.find((item) => String(item.id) === String(line.id));
      const components = [...(line.selectionDetails || []), ...(line.fixedItems || [])];
      return combo?.isDeliverable === true && combo.basePriceC === line.basePriceC &&
        sameEffectivePrice(previousEffectiveCombo, this.state.effectiveCombo, line.id) &&
        components.every((item) => this.productById(item.productId)?.isDeliverable === true &&
          sameEffectivePrice(previousEffectiveProd, this.state.effectiveProd, item.productId));
    });
    if (!sameQuote || !sameMenu || !productsMatch || !combosMatch) {
      this.setState({ deliveryCheckoutConflict: true, locationOpenNow: false,
        locationReason: this.t("skipline.deliveryCheckoutConflict") });
      return;
    }
    this.setState({ deliveryCheckoutConflict: false, error: "" });
  };

  clearCartAndChooseDeliveryAddress = () => {
    if (this.state.deliveryOrderOutcomeUnknown) return;
    this.deliveryResolutionSequence += 1;
    this.setState({
      cartProducts: {}, cartCombos: {}, location: null, locationInfo: null,
      priceList: null, products: [], combos: [], deliveryCandidate: null,
      deliverySavedAddress: null, deliveryAddress: null, deliveryResolution: null,
      deliveryQuery: "", deliverySearchResults: [],
      deliveryCountyId: "", deliveryCityId: "", deliveryBlock: "",
      deliveryEntrance: "", deliveryFloor: "", deliveryApartment: "",
      deliveryIntercom: "", deliveryCourierNotes: "", deliveryLastSavedDetails: "",
      deliveryCheckoutConflict: false, view: "name", cartOpen: false,
    });
  };

  checkout = async () => {
    if (this.state.placing || this.orderAddInFlight) return;
    if (this.state.deliveryOrderOutcomeUnknown) {
      this.setState({ error: this.t("skipline.orderAddUncertain") });
      return;
    }
    if (this.state.deliveryGuestSessionExpired) return;
    if (this.state.deliveryClientIdentityMismatch) return;
    if (this.suspendedClientUnknownOwnerId && !this.props.clientPortalAuthenticated) return;
    if (this.state.deliveryCheckoutConflict && !this.state.deliveryOrderOutcomeUnknown) {
      this.setState({ error: this.t("skipline.deliveryCheckoutConflict") });
      return;
    }
    if (this.state.orderMode === "delivery" && this.props.clientPortalAuthenticated &&
        !this.props.authenticatedCheckoutEnabled) {
      this.setState({ error: this.t("skipline.deliveryAccountCheckoutUnavailable") });
      return;
    }

    if (
      this.props.authenticatedCheckoutEnabled &&
      this.isClientPortalBusy()
    ) {
      this.setState({ error: this.t("skipline.clientSessionRestoring") });
      return;
    }

    const cartQty =
      Object.values(this.state.cartProducts || {}).reduce((a, l) => a + (l?.qty || 0), 0) +
      Object.values(this.state.cartCombos || {}).reduce((a, l) => a + (l?.qty || 0), 0);

    if (cartQty === 0) {
      this.setState({ error: this.t("skipline.addItems") });
      return;
    }

    const orderContext = this.getOrderRequestContext();
    const isClientOrder = this.isClientOrderContext(orderContext);
    const guestEmail = isClientOrder
      ? ""
      : String(this.state.guestEmail || "").trim().toLowerCase();

    if (
      isClientOrder &&
      this.props.clientProfile?.hasOrderEmail !== true
    ) {
      this.setState({
        error: this.t("skipline.clientAccountEmailMissing"),
      });
      return;
    }

    if (!isClientOrder && !isValidEmail(guestEmail)) {
      this.setState({
        view: "name",
        error: this.t("skipline.enterPaymentEmail"),
      });
      return;
    }

    let payload;

    try {
      payload = this.buildOrderPayload(orderContext);
    } catch (err) {
      this.setState({
        error: explainBackendError(err) || String(err?.message || err),
      });
      return;
    }

    const totalLines =
      (payload.orderProductEntry_order?.length || 0) +
      (payload.orderProductPromotionEntry_order?.length || 0) +
      (payload.orderComboEntry_order?.length || 0) +
      (payload.orderComboPromotionEntry_order?.length || 0);

    if (totalLines === 0) {
      this.setState({ error: this.t("skipline.addItems") });
      return;
    }

    if (this.state.orderMode === "delivery" && Number(payload.totalAmount) <= 0) {
      this.setState({ error: this.t("skipline.deliveryCardOnlyPositiveTotal") });
      return;
    }

    if (
      isClientOrder &&
      (!this.state.paymentProviderConfirmed ||
        !["PayU", "BT"].includes(this.state.paymentProvider))
    ) {
      this.setState({
        error: this.t("skipline.authenticatedPaymentUnavailable"),
      });
      return;
    }

    if (isClientOrder) {
      try {
        payload = adaptAuthenticatedOrderPayload(payload);
      } catch (error) {
        this.setState({
          error: this.t("skipline.clientBenefitsUnavailable"),
        });
        return;
      }
    }

    if (this.state.orderMode === "delivery") {
      this.pendingDeliveryOrderContext = orderContext;
      this.pendingDeliveryOrderOwnerId = isClientOrder ? this.props.clientProfile?.id || null : null;
    }
    const orderOwnerId = this.state.orderMode === "delivery" && isClientOrder
      ? this.pendingDeliveryOrderOwnerId : null;
    const requestAuthenticated = Boolean(this.props.clientPortalAuthenticated);
    const requestClientId = this.props.clientProfile?.id || null;

    this.orderAddInFlight = true;
    this.setState({ placing: true, error: "" });

    try {
      const res = await this.createOrderForContext(orderContext, payload);
      const orderId = extractCreatedOrderId(res, orderContext);

      if (!orderId) {
        throw new Error(this.t("skipline.orderError"));
      }
      if (orderOwnerId && this.props.clientPortalAuthenticated &&
          String(this.props.clientProfile?.id) !== String(orderOwnerId)) {
        this.stopUncertainClientDeliveryForIdentityChange();
        return;
      }
      if (!isClientOrder && (
        Boolean(this.props.clientPortalAuthenticated) !== requestAuthenticated ||
        String(this.props.clientProfile?.id || null) !== String(requestClientId)
      )) {
        this.setState({ placing: false, deliveryGuestSessionExpired: true,
          deliveryOrderOutcomeUnknown: true,
          error: this.t("skipline.guestPaymentSessionInterrupted") });
        return;
      }

      const totals = this.computeTotals();
      const serverTotal = extractServerOrderTotal(res);
      const lastOrderGross =
        serverTotal ?? totals.totalGross ?? 0;
      this.pendingCreatedOrder = {
        id: orderId,
        context: orderContext,
        guestEmail,
        ...(isClientOrder ? {} : {
          checkoutToken: res?.data?.checkoutToken || res?.checkoutToken || null,
        }),
      };
      this.pendingDeliveryOrderContext = null;
      this.pendingDeliveryOrderOwnerId = null;

      this.setState({
        orderLocked: true,
        deliveryOrderOutcomeUnknown: false,
        deliveryGuestSessionExpired: false,
        lastOrderId: orderId,
        lastOrderGross,
        lastOrderContext: orderContext,
        cartOpen: false,
      });

      if (orderOwnerId && !this.props.clientPortalAuthenticated) {
        this.suspendedClientOrderOwnerId = orderOwnerId;
        this.suspendedClientUnknownOwnerId = null;
        this.suspendedClientPayment = { payUrl: "" };
        this.setState({
          placing: false, clientSessionRequired: true,
          paymentSheetOpen: true, paymentRegistrationFailed: true,
          paymentMessage: this.t("skipline.clientSessionRequired"),
        });
        return;
      }

      if (serverTotal === 0) {
        this.setState({
          placing: false,
          paymentRegistering: false,
          paymentRegistrationFailed: false,
          paymentSheetOpen: false,
          summaryOpen: true,
        });
        if (this.isClientOrderContext(orderContext)) {
          this.props.refreshAfterOrderOutcome();
        }
        return;
      }

      await this.registerKnownOrder(orderId, orderContext, guestEmail);
    } catch (e) {
      if (this.state.deliveryClientIdentityMismatch) return;
      const clientSessionRequired =
        this.isClientOrderContext(orderContext) && e?.status === 401;
      const guestSessionExpired = this.state.orderMode === "delivery" &&
        !this.isClientOrderContext(orderContext) &&
        (e?.status === 401 || e?.code === "GUEST_SKIPLINE_SESSION_EXPIRED");
      const deliveryConflict = this.state.orderMode === "delivery" &&
        e?.status === 409;
      const definitiveValidation = this.state.orderMode === "delivery" &&
        [400, 401, 403, 409, 422].includes(e?.status);
      if (definitiveValidation) {
        this.pendingDeliveryOrderContext = null;
        this.pendingDeliveryOrderOwnerId = null;
      }
      if (clientSessionRequired && this.state.orderMode === "delivery" &&
          this.pendingDeliveryOrderOwnerId) {
        this.suspendedClientUnknownOwnerId = this.pendingDeliveryOrderOwnerId;
      }
      const suspendedClient = isClientOrder && Boolean(this.suspendedClientUnknownOwnerId) &&
        !this.props.clientPortalAuthenticated;
      this.setState({
        placing: false,
        clientSessionRequired: clientSessionRequired || suspendedClient,
        paymentSheetOpen: clientSessionRequired || suspendedClient,
        payIframeError: clientSessionRequired || suspendedClient,
        paymentRegistrationFailed: false,
        deliveryCheckoutConflict: deliveryConflict,
        deliveryOrderOutcomeUnknown: this.state.orderMode === "delivery" &&
          !definitiveValidation,
        deliveryGuestSessionExpired: guestSessionExpired,
        error: clientSessionRequired
          ? this.t("skipline.clientSessionRequired")
          : guestSessionExpired
            ? this.t("skipline.deliveryGuestSessionExpired")
          : deliveryConflict
            ? this.t("skipline.deliveryCheckoutConflict")
            : definitiveValidation
              ? this.t("skipline.deliveryCheckoutValidation")
              : this.t("skipline.orderAddUncertain"),
      });
    } finally {
      this.orderAddInFlight = false;
    }
  };

  checkOrderStatus = async () => {
    const {
      lastOrderId,
      lastOrderContext = ORDER_REQUEST_CONTEXT.guest,
    } = this.state;
    if (!lastOrderId) return;

    try {
      this.setState({ paymentChecking: true });

      let statusRes;
      if (this.isClientOrderContext(lastOrderContext)) {
        statusRes = await this.props.getAuthenticatedPayStatus(lastOrderId);
      } else {
        await this.ensurePublicSession();
        statusRes = await this.api.post(ENDPOINTS.orderPayStatus(lastOrderId), {
          checkoutToken: this.guestCheckoutTokenForOrder(lastOrderId),
        });
      }

      const stRaw =
        statusRes?.data?.orderStatus ??
        statusRes?.data?.status ??
        statusRes?.orderStatus ??
        statusRes?.status;

      const st = typeof stRaw === "string" ? parseInt(stRaw, 10) : stRaw;

      if ([1, 5, 6, 7].includes(st)) {
        this.stopPolling();

        this.setState({
          paymentChecking: false,
          paymentMessage: this.isClientOrderContext(lastOrderContext)
            ? this.t("skipline.paymentSuccess")
            : statusRes?.message || this.t("skipline.paymentSuccess"),
        });
        if (this.isClientOrderContext(lastOrderContext)) {
          this.props.refreshAfterOrderOutcome();
        }

        setTimeout(() => {
          this.setState({
            paymentSheetOpen: false,
            summaryOpen: true,
          });
        }, 400);

        return;
      }

      if (
        st === 0 ||
        st === 4 ||
        (!this.isClientOrderContext(lastOrderContext) && st == null)
      ) {
        this.setState({
          paymentChecking: false,
          paymentMessage:
            this.t("skipline.paymentPending"),
        });
        return;
      }

      if (st === 3) {
        this.stopPolling();

        this.setState({
          paymentChecking: false,
          paymentMessage: this.t("skipline.paymentCanceled"),
        });
        if (this.isClientOrderContext(lastOrderContext)) {
          this.props.refreshAfterOrderOutcome();
        }

        return;
      }

      if ([2, 8].includes(st)) {
        this.stopPolling();
        if (this.isClientOrderContext(lastOrderContext)) {
          this.props.refreshAfterOrderOutcome();
        }
        this.setState({
          paymentChecking: false,
          paymentMessage: this.t("skipline.paymentCheckFailed"),
        });
        return;
      }

      if (!this.isClientOrderContext(lastOrderContext)) {
        this.setState({
          paymentChecking: false,
          paymentMessage: this.tr("skipline.paymentStatus", {
            status: String(stRaw),
          }),
        });
        return;
      }
      this.setState({
        paymentChecking: false,
        paymentMessage: this.t("skipline.paymentCheckFailed"),
      });
    } catch (e) {
      const clientSessionRequired =
        this.isClientOrderContext(lastOrderContext) && e?.status === 401;
      const guestSessionExpired = !this.isClientOrderContext(lastOrderContext) &&
        (e?.status === 401 || e?.code === "GUEST_SKIPLINE_SESSION_EXPIRED");
      if (clientSessionRequired || guestSessionExpired) this.stopPolling();
      this.setState({
        paymentChecking: false,
        clientSessionRequired,
        deliveryGuestSessionExpired: guestSessionExpired,
        paymentMessage: clientSessionRequired
          ? this.t("skipline.clientSessionRequired")
          : guestSessionExpired
            ? this.t("skipline.guestSessionExpiredStop")
            : this.t("skipline.paymentCheckFailed"),
        ...(guestSessionExpired ? { error: this.t("skipline.guestSessionExpiredStop") } : {}),
      });
    }
  };

  cancelCurrentOrder = async () => {
    const {
      lastOrderId,
      lastOrderContext = ORDER_REQUEST_CONTEXT.guest,
    } = this.state;
    if (!lastOrderId) return;

    try {
      // stop polling first
      this.stopPolling();

      // POST to cancel
      if (this.isClientOrderContext(lastOrderContext)) {
        await this.props.cancelAuthenticatedOrder(lastOrderId);
      } else {
        await this.api.post(ENDPOINTS.cancelOrder(lastOrderId), {
          checkoutToken: this.guestCheckoutTokenForOrder(lastOrderId),
        });
      }
      if (this.isClientOrderContext(lastOrderContext)) {
        this.props.refreshAfterOrderOutcome();
      }

      // reset UI so the user can start over
      this.setState({
        paymentSheetOpen: false,
        paymentMessage: "",
        paymentChecking: false,
        payUrl: "",
        orderLocked: false,
        lastOrderId: null,
        lastOrderGross: null,
        lastOrderContext: null,
        paymentRegistrationFailed: false,
        paymentRegistering: false,
        clientSessionRequired: false,
        cartProducts: {},
        cartCombos: {},
        infoProduct: null,
        view: "name",
        activeCategory: "All",
        search: "",
        error: "",
      });
      this.pendingCreatedOrder = null;
    } catch (e) {
      const clientSessionRequired =
        this.isClientOrderContext(lastOrderContext) && e?.status === 401;
      this.setState({
        clientSessionRequired,
        error: clientSessionRequired
          ? this.t("skipline.clientSessionRequired")
          : this.t("skipline.cancelFailed"),
      });
    }
  };

  renderLocationSelect() {
    if (this.state.orderMode === "delivery") return this.renderDeliverySelect();
    const { selectingLocation, locationList, expandedCounty, locationSearch } = this.state;
    const groupedLocations = this.groupLocationsByCounty();
    const hasSearch = String(locationSearch || "").trim().length > 0;

    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: PAGE_BG,
        }}
      >
        {this.renderSelectionHeader()}

        <Container
          maxWidth="sm"
          sx={{
            pt: { xs: 2, sm: 3 },
            pb: { xs: 3, sm: 5 },
          }}
        >
          <Card
            elevation={0}
            sx={{
              ...compactCardSx,
              mb: 2,
            }}
          >
            <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 900,
                  mb: 1,
                }}
              >
                {this.t("skipline.chooseLocation")}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "block",
                  mb: 2,
                }}
              >
                {this.t("skipline.chooseLocationDesc")}
              </Typography>

              <TextField
                fullWidth
                size="small"
                placeholder={this.t("skipline.searchLocation")}
                value={locationSearch}
                onChange={(e) => this.setState({ locationSearch: e.target.value })}
                inputProps={{
                  "aria-label": this.t("skipline.searchLocation"),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: locationSearch ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => this.setState({ locationSearch: "" })}
                        aria-label={this.t("skipline.clearFilter")}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    bgcolor: "#fffaf3",
                    minHeight: 44,
                  },
                }}
              />

              {this.renderSelectionLegalNotice()}
            </Box>
          </Card>

          <Card
            elevation={0}
            sx={{
              ...compactCardSx,
            }}
          >
            <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
              {selectingLocation ? (
                <Box sx={{ py: 4 }}>
                  <Stack spacing={1.25}>
                    {[1, 2, 3].map((x) => (
                      <Skeleton
                        key={x}
                        variant="rounded"
                        height={74}
                        sx={{ borderRadius: "8px" }}
                      />
                    ))}
                  </Stack>
                </Box>
              ) : locationList.length === 0 ? (
                <Alert severity="warning" sx={{ borderRadius: "8px" }}>
                  {this.t("skipline.noLocations")}
                </Alert>
              ) : groupedLocations.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: "8px" }}>
                  {this.t("skipline.noSearchLocations")}
                </Alert>
              ) : (
                <Stack spacing={1.25}>
                  {groupedLocations.map((group) => {
                    const isOpen = hasSearch || expandedCounty === group.countyName;
                    const countyPanelId = `skipline-county-${String(
                      group.countyName
                    ).replace(/[^a-z0-9_-]+/gi, "-")}`;

                    return (
                      <Box
                        key={group.countyName}
                        sx={{
                          border: "1px solid",
                          borderColor: isOpen ? "primary.main" : BORDER,
                          borderRadius: "8px",
                          bgcolor: "background.paper",
                          overflow: "hidden",
                          transition: "border-color 150ms ease, box-shadow 150ms ease",
                          boxShadow: isOpen
                            ? "0 10px 26px rgba(247, 181, 0, 0.14)"
                            : "none",
                        }}
                      >
                        <Button
                          fullWidth
                          onClick={() => this.toggleCounty(group.countyName)}
                          aria-expanded={isOpen}
                          aria-controls={countyPanelId}
                          sx={{
                            justifyContent: "space-between",
                            textAlign: "left",
                            px: 2,
                            py: 1.6,
                            color: "text.primary",
                          }}
                        >
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Box
                              sx={{
                                width: 38,
                                height: 38,
                                borderRadius: "8px",
                                bgcolor: isOpen ? "primary.main" : "#fff7df",
                                color: isOpen ? "primary.contrastText" : "text.secondary",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <LocationOnRoundedIcon fontSize="small" />
                            </Box>

                            <Box>
                              <Typography sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                                {group.countyName}
                              </Typography>

                              <Typography variant="caption" color="text.secondary">
                                {group.locations.length}{" "}
                                {group.locations.length === 1
                                  ? this.t("skipline.locationOne")
                                  : this.t("skipline.locationMany")}
                              </Typography>
                            </Box>
                          </Stack>

                          <Typography
                            sx={{
                              fontWeight: 900,
                              fontSize: 18,
                              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 150ms ease",
                              color: "text.secondary",
                            }}
                          >
                            ▾
                          </Typography>
                        </Button>

                        {isOpen ? (
                          <Box
                            id={countyPanelId}
                            sx={{
                              px: 1.25,
                              pb: 1.25,
                            }}
                          >
                            <Stack spacing={1}>
                              {group.locations.map((loc) => {
                                const addressText = this.formatAddress(loc.address);

                                return (
                                  <Button
                                    key={loc.iref}
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => this.selectLocation(loc.iref)}
                                    sx={{
                                      justifyContent: "space-between",
                                      textAlign: "left",
                                      py: 1.3,
                                      px: 1.5,
                                      borderRadius: "8px",
                                      borderColor: BORDER,
                                      color: "text.primary",
                                      bgcolor: "#fff",
                                      "&:hover": {
                                        borderColor: "primary.main",
                                        bgcolor: "rgba(247, 181, 0, 0.08)",
                                      },
                                    }}
                                  >
                                    <Box sx={{ minWidth: 0, pr: 1 }}>
                                      <Typography
                                        sx={{
                                          fontWeight: 800,
                                          lineHeight: 1.25,
                                        }}
                                      >
                                        {loc.name}
                                      </Typography>

                                      {addressText ? (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            display: "block",
                                            mt: 0.35,
                                            whiteSpace: "normal",
                                            lineHeight: 1.35,
                                          }}
                                        >
                                          {addressText}
                                        </Typography>
                                      ) : null}
                                    </Box>

                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 800,
                                        color: "primary.main",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {this.t("skipline.choose")}
                                    </Typography>
                                  </Button>
                                );
                              })}
                            </Stack>
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Card>
        </Container>
      </Box>
    );
  }

  renderProductCard = (p) => {
    const price = p.effPrice ?? this.state.priceList?.priceByProductId?.[p.id] ?? null;
    const base = p.basePrice;
    const promo = !!p.isPromo;
    const qty = this.qtyForProduct(p.id);
    const locked = this.state.orderLocked || this.state.deliveryOrderOutcomeUnknown;

    return (
      <Card
        elevation={0}
        sx={{
          ...compactCardSx,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderColor: qty > 0 ? "primary.main" : BORDER,
          bgcolor: qty > 0 ? "rgba(255,253,247,0.98)" : "background.paper",
          opacity: locked ? 0.55 : 1,
          boxShadow: qty > 0
            ? "0 18px 42px rgba(247, 181, 0, 0.20)"
            : compactCardSx.boxShadow,
          transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease, background-color 140ms ease",
          "&:hover": locked
            ? {}
            : {
                transform: "translateY(-2px)",
                boxShadow: "0 16px 36px rgba(33,20,12,0.12)",
                borderColor: qty > 0 ? "primary.main" : "rgba(247, 181, 0, 0.45)",
              },
          "&:focus-within": {
            borderColor: qty > 0 ? "primary.main" : "rgba(247, 181, 0, 0.55)",
            boxShadow: "0 0 0 3px rgba(247,181,0,0.16), 0 16px 36px rgba(33,20,12,0.10)",
          },
          "&:hover .skipline-product-media": locked
            ? {}
            : {
                transform: "scale(1.035)",
                filter: "saturate(1.05)",
              },
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: { xs: 132, sm: 148 },
            bgcolor: "rgba(255,250,243,0.94)",
            borderBottom: "1px solid",
            borderColor: "rgba(124,45,18,0.08)",
            overflow: "hidden",
          }}
        >
          <BrandedImageFallback
            className="skipline-product-media"
            icon={<ShoppingCart fontSize="medium" />}
            label={p.imageSmallUrl ? "" : this.t("common.noImage")}
            brandTheme={this.props.brandTheme}
            sx={{
              position: "absolute",
              inset: 0,
              minHeight: 0,
              transition: "transform 180ms ease, filter 180ms ease",
            }}
          />
          {p.imageSmallUrl ? (
            <Box
              component="img"
              className="skipline-product-media"
              src={p.imageSmallUrl}
              alt={p.name}
              loading="lazy"
              decoding="async"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
              sx={{
                position: "absolute",
                inset: 0,
                m: "auto",
                maxHeight: "88%",
                maxWidth: "88%",
                objectFit: "contain",
                transition: "transform 180ms ease, filter 180ms ease",
              }}
            />
          ) : null}

          {promo ? (
            <Box
              sx={{
                position: "absolute",
                left: 8,
                top: 8,
                bgcolor: "error.main",
                color: "#fff",
                px: 1,
                py: 0.35,
                borderRadius: "8px",
                fontSize: 11,
                fontWeight: 900,
                boxShadow: "0 6px 14px rgba(211, 47, 47, 0.24)",
              }}
            >
              {this.t("skipline.promo")}
            </Box>
          ) : null}

          <IconButton
            size="small"
            aria-label={`${this.t("common.productDetails")} ${p.name}`}
            onClick={() => this.setState({ infoProduct: p })}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: "rgba(255,255,255,0.92)",
              color: BRAND,
              border: "1px solid",
              borderColor: BORDER,
              boxShadow: "0 8px 18px rgba(33,20,12,0.12)",
              "&:hover": {
                bgcolor: "#fffaf3",
              },
              ...focusRingSx,
            }}
          >
            <InfoOutlined fontSize="small" />
          </IconButton>

          {qty > 0 && !locked ? (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{
                position: "absolute",
                right: 8,
                bottom: 8,
                bgcolor: "background.paper",
                px: 0.5,
                py: 0.35,
                borderRadius: "8px",
                boxShadow: "0 10px 22px rgba(33,20,12,0.14)",
                border: "1px solid",
                borderColor: qty > 0 ? "primary.main" : BORDER,
              }}
            >
              <IconButton
                color="error"
                size="small"
                onClick={() => this.decProduct(p.id)}
                aria-label={`${this.t("skipline.decreaseQuantity", "Decrease quantity")} ${p.name}`}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  bgcolor: "rgba(211,47,47,0.08)",
                  ...focusRingSx,
                }}
              >
                <Remove fontSize="small" />
              </IconButton>

              <Typography
                sx={{
                  minWidth: 24,
                  textAlign: "center",
                  fontSize: 14,
                  fontWeight: 900,
                }}
                aria-live="polite"
              >
                {qty}
              </Typography>

              <IconButton
                color="primary"
                size="small"
                onClick={() => this.incProduct(p.id)}
                aria-label={`${this.t("skipline.increaseQuantity", "Increase quantity")} ${p.name}`}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  bgcolor: "rgba(247,181,0,0.12)",
                  ...focusRingSx,
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </Stack>
          ) : null}
        </Box>

        <CardContent
          sx={{
            flexGrow: 1,
            p: 1.5,
            pb: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            title={p.name}
            sx={{
              fontWeight: 900,
              lineHeight: 1.25,
              minHeight: 38,
              color: BRAND,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {p.name}
          </Typography>

          {price != null ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="baseline"
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 900,
                  color: promo ? "error.main" : "text.primary",
                }}
              >
                {money(price, this.state.priceList?.currency || "RON")}
              </Typography>

              {promo && base != null ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    textDecoration: "line-through",
                    fontWeight: 700,
                  }}
                >
                  {money(base, this.state.priceList?.currency || "RON")}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </CardContent>

        <CardActions sx={{ p: 1.5, pt: 0 }}>
          <Button
            fullWidth
            variant={qty > 0 ? "outlined" : "contained"}
            size="medium"
            onClick={() => this.addProduct(p)}
            startIcon={<Add />}
            sx={{
              ...pillButtonSx,
              fontWeight: 900,
              py: 0.9,
              minHeight: 46,
              boxShadow: qty > 0 ? "none" : "0 10px 22px rgba(247,181,0,0.16)",
              borderColor: qty > 0 ? "primary.main" : undefined,
            }}
            disabled={locked}
          >
            {this.t("skipline.add")}
          </Button>
        </CardActions>
      </Card>
    );
  };

  renderComboCard = (c)=> {
    const locked = this.state.orderLocked || this.state.deliveryOrderOutcomeUnknown;
    const promo = !!c.isComboPromo;
    return (
      <Card
        variant="outlined"
        sx={{
          ...compactCardSx,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderColor: "secondary.main",
          bgcolor: "rgba(255,253,247,0.98)",
          opacity: locked ? 0.5 : 1,
          boxShadow: "0 16px 38px rgba(15,118,110,0.10)",
          transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
          "&:hover": locked
            ? {}
            : {
                transform: "translateY(-2px)",
                boxShadow: "0 18px 42px rgba(33,20,12,0.12)",
              },
          "&:focus-within": {
            boxShadow: "0 0 0 3px rgba(15,118,110,0.14), 0 16px 36px rgba(33,20,12,0.10)",
          },
          "&:hover .skipline-product-media": locked
            ? {}
            : {
                transform: "scale(1.035)",
                filter: "saturate(1.05)",
              },
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: { xs: 120, sm: 130 },
            bgcolor: "rgba(255,250,243,0.94)",
            borderBottom: "1px solid",
            borderColor: "rgba(124,45,18,0.08)",
            overflow: "hidden",
          }}
        >
          <BrandedImageFallback
            className="skipline-product-media"
            icon={<Tune fontSize="medium" />}
            label={c.imageSmallUrl ? "" : this.t("common.noImage")}
            brandTheme={this.props.brandTheme}
            sx={{
              position: "absolute",
              inset: 0,
              minHeight: 0,
              transition: "transform 180ms ease, filter 180ms ease",
            }}
          />
          {c.imageSmallUrl ? (
            <Box
              component="img"
              className="skipline-product-media"
              src={c.imageSmallUrl}
              alt={c.name}
              loading="lazy"
              decoding="async"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
              sx={{
                position: "absolute",
                inset: 0,
                m: "auto",
                maxHeight: "92%",
                maxWidth: "92%",
                objectFit: "contain",
                transition: "transform 180ms ease, filter 180ms ease",
              }}
            />
          ) : null}
          <Box sx={{ position:'absolute', left:6, top:6, bgcolor:'secondary.main', color:'secondary.contrastText',
                    px:1, py:0.25, borderRadius:"8px", fontSize:12, fontWeight:900 }}>
            {this.t("skipline.combo")}
          </Box>
          {promo && (
            <Box sx={{ position:'absolute', right:6, top:6, bgcolor:'error.main', color:'#fff',
                      px:1, py:0.25, borderRadius:"8px", fontSize:12, fontWeight:900, boxShadow:1 }}>
              {this.t("skipline.promo")}
            </Box>
          )}
        </Box>
        <CardContent sx={{ flexGrow:1, py:1, px:1.25 }}>
          <Typography
            variant="subtitle2"
            title={c.name}
            sx={{
              fontWeight: 900,
              color: BRAND,
              lineHeight: 1.25,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {c.name}
          </Typography>
          <Typography variant="body2" sx={{ mt:0.5, fontWeight: 900 }}>
            {money(fromCents(c.basePriceC), this.state.priceList?.currency||'RON')}+
          </Typography>
        </CardContent>
        <CardActions sx={{ p:1, pt:0 }}>
          <Button fullWidth variant="outlined" size="medium"
                  onClick={()=>this.openComboPicker(c)} startIcon={<Tune />} sx={{ ...pillButtonSx, minHeight: 46, borderColor: "secondary.main" }} disabled={locked}>
            {this.t("skipline.choose")}
          </Button>
        </CardActions>
      </Card>
    );
  };

  // ================================================

  renderNameGate() {
    const { guestEmail, locationInfo, locationOpenNow, locationReason } = this.state;
    const clientOrderActive =
      this.props.authenticatedCheckoutEnabled &&
      this.props.clientPortalAuthenticated;
    const clientPortalBusy =
      this.props.authenticatedCheckoutEnabled &&
      this.isClientPortalBusy();
    const hasOrderEmail =
      this.props.clientProfile?.hasOrderEmail === true;
    const ok =
      !clientPortalBusy &&
      (clientOrderActive || isValidEmail(guestEmail));

    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: PAGE_BG,
        }}
      >
        <AppBar
          position="fixed"
          elevation={0}
          sx={appBarSx}
        >
          <Toolbar sx={{ minHeight: 64 }}>
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{ minWidth: 0, flexGrow: 1 }}
            >
              {this.renderBrandMark({ width: 40, height: 40 })}

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: 0.3,
                    lineHeight: 1.1,
                  }}
                  noWrap
                >
                  {this.getBrandNameUpper()}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.25 }}
                  noWrap
                >
                  {locationInfo?.name || this.t(this.state.orderMode === "delivery"
                    ? "skipline.deliveryTab" : "skipline.tagline")}
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="text"
              size="small"
              startIcon={<LocationOnRoundedIcon fontSize="small" />}
              onClick={this.changeLocation}
              disabled={this.locationChangeBlocked()}
              aria-label={this.t(this.state.orderMode === "delivery" ? "skipline.changeAddress" : "skipline.changeLocation")}
              sx={{
                ml: 1,
                minWidth: 36,
                px: 0.75,
                fontWeight: 800,
                whiteSpace: "nowrap",
                "& .MuiButton-startIcon": {
                  mr: { xs: 0, sm: 0.5 },
                },
              }}
            >
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                {this.t(this.state.orderMode === "delivery" ? "skipline.changeAddress" : "skipline.changeLocation")}
              </Box>
            </Button>

            {this.renderLanguageSwitcher()}
          </Toolbar>
        </AppBar>

        <Container
          maxWidth="sm"
          sx={{
            pt: { xs: 10, sm: 12 },
            pb: { xs: 3, sm: 5 },
          }}
        >
          <Card
            elevation={0}
            sx={{
              ...shellCardSx,
            }}
          >
            <Box
              sx={{
                p: { xs: 2.25, sm: 3 },
                bgcolor: "background.paper",
              }}
            >
              <Box sx={{ textAlign: "center", mb: 2.5 }}>
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: "8px",
                    bgcolor: ACCENT,
                    color: "#111111",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                    boxShadow: "0 12px 26px rgba(247,181,0,0.22)",
                  }}
                >
                  <ShoppingCart fontSize="medium" />
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    mb: 0.75,
                    letterSpacing: -0.2,
                  }}
                >
                  {this.t("skipline.startOrder")}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.55,
                    maxWidth: 420,
                    mx: "auto",
                  }}
                >
                  {clientPortalBusy
                    ? this.t("skipline.clientSessionRestoring")
                    : clientOrderActive
                      ? this.t("skipline.clientSignedIn")
                      : this.t("skipline.emailNeeded")}
                </Typography>
              </Box>

              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: "8px",
                  bgcolor: locationOpenNow ? "rgba(46, 125, 50, 0.08)" : "rgba(211, 47, 47, 0.08)",
                  border: "1px solid",
                  borderColor: locationOpenNow ? "rgba(46, 125, 50, 0.22)" : "rgba(211, 47, 47, 0.22)",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 900,
                    color: locationOpenNow ? "success.main" : "error.main",
                  }}
                >
                  {locationOpenNow ? this.t("skipline.openNow") : this.t("skipline.closedNow")}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mt: 0.25,
                    lineHeight: 1.4,
                  }}
                >
                  {locationOpenNow
                    ? this.t(this.state.orderMode === "delivery"
                      ? "skipline.deliveryCanContinue" : "skipline.canContinue")
                    : locationReason || this.t("skipline.onlineUnavailable")}
                </Typography>
              </Box>

              {/* <TextField
                label="Nume"
                fullWidth
                autoFocus
                value={customerName}
                onChange={(e) => this.setState({ customerName: e.target.value })}
                inputProps={{ maxLength: 64 }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    bgcolor: "#fffaf3",
                  },
                }}
              /> */}

              {clientPortalBusy ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {this.t("skipline.clientSessionRestoring")}
                </Alert>
              ) : clientOrderActive ? (
                <Alert
                  severity={hasOrderEmail ? "success" : "error"}
                  sx={{ mb: 2 }}
                >
                  {this.t(
                    hasOrderEmail
                      ? "skipline.clientAccountEmailReady"
                      : "skipline.clientAccountEmailMissing"
                  )}
                </Alert>
              ) : (
                <TextField
                  label={this.t("skipline.paymentEmail")}
                  type="email"
                  required
                  autoComplete="email"
                  fullWidth
                  value={guestEmail}
                  onChange={(e) => this.setState({ guestEmail: e.target.value })}
                  error={!!guestEmail && !isValidEmail(guestEmail)}
                  helperText={
                    !!guestEmail && !isValidEmail(guestEmail)
                      ? this.t("skipline.invalidEmail")
                      : this.t("skipline.emailHelp")
                  }
                  inputProps={{ maxLength: 128, "aria-label": this.t("skipline.paymentEmail") }}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      bgcolor: "#fffaf3",
                    },
                  }}
                />
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                disabled={!ok || !locationOpenNow}
                onClick={() => this.setState({ view: "home" })}
                sx={{
                  borderRadius: "8px",
                  py: 1.25,
                  fontWeight: 900,
                  boxShadow: ok && locationOpenNow
                    ? "0 12px 26px rgba(247,181,0,0.22)"
                    : "none",
                }}
              >
                {this.t("skipline.continueMenu")}
              </Button>

              {!locationOpenNow ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    mt: 1.5,
                    lineHeight: 1.4,
                  }}
                >
                  {this.t("skipline.menuOnlyOpen")}
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    mt: 1.5,
                    lineHeight: 1.4,
                  }}
                >
                  {clientOrderActive
                    ? this.t("skipline.clientBaseOrderHelp")
                    : this.t("skipline.noAccount")}
                </Typography>
              )}
            </Box>
          </Card>
        </Container>
      </Box>
    );
  }

  renderHome() {
    const tiles = (this.state.categoriesRaw || [])
      .filter(c => c.isForSale !== false)
      .map(c => ({
        id: c.iref || c.id,
        name: c.name,
        image: ensureSmallImage(c.image),
      }));

    const { locationInfo, locationOpenNow, locationReason } = this.state;

    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: PAGE_BG,
        }}
      >
        <AppBar
          position="fixed"
          elevation={0}
          sx={appBarSx}
        >
          <Toolbar sx={{ minHeight: 64 }}>
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{ minWidth: 0, flexGrow: 1 }}
            >
              {this.renderBrandMark({ width: 40, height: 40 })}

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: 0.3,
                    lineHeight: 1.1,
                  }}
                  noWrap
                >
                  {this.getBrandNameUpper()}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.25 }}
                  noWrap
                >
                  {locationInfo?.name || this.t("skipline.selectedLocation")}
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="text"
              size="small"
              startIcon={<LocationOnRoundedIcon fontSize="small" />}
              onClick={this.changeLocation}
              disabled={this.locationChangeBlocked()}
              aria-label={this.t(this.state.orderMode === "delivery" ? "skipline.changeAddress" : "skipline.changeLocation")}
              sx={{
                ml: 1,
                minWidth: 36,
                px: 0.75,
                fontWeight: 800,
                whiteSpace: "nowrap",
                "& .MuiButton-startIcon": {
                  mr: { xs: 0, sm: 0.5 },
                },
              }}
            >
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                {this.t(this.state.orderMode === "delivery" ? "skipline.changeAddress" : "skipline.changeLocation")}
              </Box>
            </Button>

            {this.renderLanguageSwitcher()}
          </Toolbar>
        </AppBar>

        <Container
          maxWidth="lg"
          sx={{
            pt: { xs: 10, sm: 11 },
            pb: { xs: 2, sm: 3 },
          }}
        >
          <Card
            elevation={0}
            sx={{
              ...shellCardSx,
              mb: 2,
            }}
          >
            <Box
              sx={{
                p: { xs: 2.25, sm: 3 },
                bgcolor: "background.paper",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 900,
                      letterSpacing: -0.3,
                      mb: 0.5,
                    }}
                  >
                    {locationInfo?.name || this.getBrandName()}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.55,
                    }}
                  >
                    {this.t(this.state.orderMode === "delivery" ? "skipline.deliveryHomeIntro" : "skipline.homeIntro")}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: "8px",
                    bgcolor: locationOpenNow
                      ? "rgba(46, 125, 50, 0.08)"
                      : "rgba(211, 47, 47, 0.08)",
                    border: "1px solid",
                    borderColor: locationOpenNow
                      ? "rgba(46, 125, 50, 0.22)"
                      : "rgba(211, 47, 47, 0.22)",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 900,
                      color: locationOpenNow ? "success.main" : "error.main",
                    }}
                  >
                    {locationOpenNow ? this.t("skipline.openNow") : this.t("skipline.closedNow")}
                  </Typography>
                </Box>
              </Stack>

              {!locationOpenNow ? (
                <Alert
                  severity="warning"
                  sx={{
                    mt: 2,
                    borderRadius: "8px",
                  }}
                >
                  {locationReason || this.t("skipline.onlineUnavailable")}
                </Alert>
              ) : null}
            </Box>
          </Card>

          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                letterSpacing: -0.2,
              }}
            >
              {this.t("skipline.chooseCategory")}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {this.t("skipline.chooseCategoryDesc")}
            </Typography>
          </Box>

          {tiles.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: "8px" }}>
              {this.t("skipline.noCategories")}
            </Alert>
          ) : (
            <Grid container spacing={1.5}>
              {tiles.map((t) => (
                <Grid key={t.id} item xs={6} sm={4} md={3} lg={2.4}>
                  <Card
                    onClick={() => locationOpenNow && this.openCategory(t.name)}
                    onKeyDown={(event) =>
                      this.activateCardFromKeyboard(event, () => {
                        if (locationOpenNow) this.openCategory(t.name);
                      })
                    }
                    role="button"
                    tabIndex={locationOpenNow ? 0 : -1}
                    aria-disabled={!locationOpenNow}
                    aria-label={`${t.name}. ${this.t("skipline.viewProducts")}`}
                    elevation={0}
                    sx={{
                      height: "100%",
                      ...compactCardSx,
                      cursor: locationOpenNow ? "pointer" : "default",
                      opacity: locationOpenNow ? 1 : 0.55,
                      transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
                      "&:hover": locationOpenNow
                        ? {
                            transform: "translateY(-2px)",
                            borderColor: "primary.main",
                            boxShadow: "0 16px 36px rgba(33,20,12,0.12)",
                          }
                        : {},
                      "&:focus-visible": {
                        outline: `3px solid ${CTA}`,
                        outlineOffset: 3,
                        borderColor: "secondary.main",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        height: { xs: 118, sm: 132 },
                        bgcolor: "#fff",
                      }}
                    >
                      <BrandedImageFallback
                        icon={<ShoppingCart fontSize="large" />}
                        brandTheme={this.props.brandTheme}
                        sx={{ position: "absolute", inset: 0, minHeight: 0 }}
                      />
                      {t.image ? (
                        <Box
                          component="img"
                          src={t.image}
                          alt={t.name}
                          loading="lazy"
                          decoding="async"
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                          sx={{
                            position: "absolute",
                            inset: 0,
                            m: "auto",
                            maxHeight: "88%",
                            maxWidth: "88%",
                            objectFit: "contain",
                          }}
                        />
                      ) : null}
                    </Box>

                    <CardContent sx={{ p: 1.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 900,
                          lineHeight: 1.25,
                          minHeight: 38,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                        title={t.name}
                      >
                        {t.name}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="primary.main"
                        sx={{
                          display: "block",
                          mt: 0.75,
                          fontWeight: 900,
                        }}
                      >
                        {this.t("skipline.viewProducts")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}

              {this.state.combos?.length > 0 ? (
                <Grid item xs={6} sm={4} md={3} lg={2.4}>
                  <Card
                    onClick={() => this.openCategory("Combos")}
                    onKeyDown={(event) =>
                      this.activateCardFromKeyboard(event, () =>
                        this.openCategory("Combos")
                      )
                    }
                    role="button"
                    tabIndex={0}
                    aria-label={`${this.t("skipline.combo")}. ${this.t("skipline.chooseCombo")}`}
                    elevation={0}
                    sx={{
                      ...compactCardSx,
                      height: "100%",
                      borderColor: "secondary.main",
                      cursor: locationOpenNow ? "pointer" : "default",
                      opacity: locationOpenNow ? 1 : 0.55,
                      transition: "transform 140ms ease, box-shadow 140ms ease",
                      "&:hover": locationOpenNow
                        ? {
                            transform: "translateY(-2px)",
                            boxShadow: "0 16px 36px rgba(33,20,12,0.12)",
                          }
                        : {},
                      "&:focus-visible": {
                        outline: `3px solid ${CTA}`,
                        outlineOffset: 3,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: { xs: 118, sm: 132 },
                        bgcolor: "rgba(109, 76, 65, 0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Tune
                        sx={{
                          fontSize: 48,
                          color: "secondary.main",
                        }}
                      />
                    </Box>

                    <CardContent sx={{ p: 1.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 900,
                          lineHeight: 1.25,
                          minHeight: 38,
                        }}
                      >
                        Combos
                      </Typography>

                      <Typography
                        variant="caption"
                        color="secondary.main"
                        sx={{
                          display: "block",
                          mt: 0.75,
                          fontWeight: 900,
                        }}
                      >
                        {this.t("skipline.chooseCombo")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ) : null}
            </Grid>
          )}
        </Container>
      </Box>
    );
  }

  render(){
    const { loading, error, priceList, products, combos, categories, categoriesRaw, activeCategory, search,
      cartOpen, cartProducts={}, cartCombos={}, placing, view, orderLocked,
      paymentSheetOpen,
      summaryOpen, lastOrderId } = this.state;

    if (!this.state.location) {
      return (
        <ThemeProvider theme={createSkiplineTheme(this.props.brandTheme)}>
          <CssBaseline />
          <GlobalStyles styles={standaloneGlobalStyles} />
          <Box sx={{ ...brandThemeCssVars(this.props.brandTheme), minHeight: "100vh" }}>
            {this.renderLocationChangeDialog()}
            {this.renderLocationSelect()}
          </Box>
        </ThemeProvider>
      );
    }

    const cartQty =
      Object.values(cartProducts).reduce((sum, l) => sum + (l?.qty || 0), 0) +
      Object.values(cartCombos).reduce((sum, l) => sum + (l?.qty || 0), 0);

    const cartIsEmpty = cartQty === 0;
    
    const currency = priceList?.currency || "RON"; const s = (search||"").trim().toLowerCase();
    const currentCategoryId = this.getCategoryIdForName(activeCategory, categoriesRaw);

    const filteredProducts = products
      .filter((p) => {
        if (activeCategory === "Combos") return false;

        const okCat =
          activeCategory === "All" ||
          p.categoryId === currentCategoryId ||
          p.categoryName === activeCategory;

        const okText =
          !s ||
          p.name?.toLowerCase().includes(s) ||
          p.categoryName?.toLowerCase().includes(s);

        return okCat && okText;
      })
      .sort((a, b) => (a.shortName || a.name || "").localeCompare(b.shortName || b.name || ""));

    const filteredCombos = combos
      .filter((c) => {
        const okCat =
          activeCategory === "All" ||
          activeCategory === "Combos" ||
          c.categoryId === currentCategoryId ||
          c.categoryName === activeCategory;

        const okText =
          !s ||
          c.name?.toLowerCase().includes(s) ||
          c.categoryName?.toLowerCase().includes(s);

        return okCat && okText;
      })
      .sort((a, b) => (a.shortName || a.name || "").localeCompare(b.shortName || b.name || ""));

    const itemsCount = Object.values(cartProducts).reduce((a,b)=>a+(b.qty||0),0) + Object.values(cartCombos).reduce((a,b)=>a+(b.qty||0),0);
    const totals = priceList ? this.computeTotals() : { totalGross: 0, merchandiseGross: 0, deliveryFee: 0 };
    const hasLines = Object.keys(cartProducts).length || Object.keys(cartCombos).length;
    const retryPaymentAvailable =
      orderLocked &&
      Boolean(lastOrderId) &&
      this.state.paymentRegistrationFailed;
    const clientOrderActive =
      this.props.authenticatedCheckoutEnabled &&
      this.props.clientPortalAuthenticated;
    const clientAccountEmailMissing =
      clientOrderActive &&
      this.props.clientProfile?.hasOrderEmail !== true;
    const checkoutDisabled =
      (!priceList) ||
      (!hasLines) ||
      placing ||
      this.state.deliveryOrderOutcomeUnknown ||
      (orderLocked && !retryPaymentAvailable) ||
      !this.state.locationOpenNow ||
      this.state.deliveryGuestSessionExpired ||
      this.state.deliveryCheckoutConflict ||
      (this.props.authenticatedCheckoutEnabled &&
        this.isClientPortalBusy()) ||
      (clientAccountEmailMissing && !retryPaymentAvailable) ||
      (this.state.orderMode === "delivery" && totals.totalGross <= 0);
    const inFlow = paymentSheetOpen || summaryOpen; 

    return (
      <ThemeProvider theme={createSkiplineTheme(this.props.brandTheme)}>
        <CssBaseline />
        <GlobalStyles styles={standaloneGlobalStyles} />
        <Box sx={{ ...brandThemeCssVars(this.props.brandTheme), flexGrow:1 }}>
          {this.renderLocationChangeDialog()}
          {view==='name' ? this.renderNameGate() : (
            summaryOpen ? (
              <Container
                maxWidth="sm"
                sx={{
                  py: { xs: 1.25, sm: 2 },
                }}
              >
                <OrderSummary
                  id={lastOrderId}
                  currency={currency}
                  cartProducts={cartProducts}
                  cartCombos={cartCombos}
                  gross={this.state.lastOrderGross ?? (priceList ? totals.totalGross : 0)}
                  deliveryMode={this.state.orderMode === "delivery"}
                  deliveryFee={totals.deliveryFee || 0}
                  t={this.t}
                  brandTheme={this.props.brandTheme}
                  onNewOrder={() => {
                    this.pendingCreatedOrder = null;
                    this.setState({
                      cartProducts: {},
                      cartCombos: {},
                      infoProduct: null,
                      orderLocked: false,
                      view: "name",
                      activeCategory: "All",
                      search: "",
                      summaryOpen: false,
                      lastOrderId: null,
                      lastOrderContext: null,
                      lastOrderGross: null,
                      paymentRegistrationFailed: false,
                      paymentRegistering: false,
                      clientSessionRequired: false,
                      customerName: "",
                      guestEmail: "",
                    });
                  }}
                />
              </Container>
            ) : (
              view === 'home' ? this.renderHome() : (
                <Box
                  sx={{
                    minHeight: "100vh",
                    bgcolor: PAGE_BG,
                    pt: 0,
                    pb: { xs: 12, sm: 5 },
                  }}
                >
                  <Box
                    sx={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      zIndex: 1090,
                      bgcolor: "rgba(255,250,243,0.94)",
                      borderBottom: "1px solid",
                      borderColor: BORDER,
                      backdropFilter: "blur(18px)",
                      pb: 1,
                    }}
                  >
                    <Container maxWidth="lg" sx={{ pt: 0.75 }}>
                      <Card
                        elevation={0}
                        sx={{
                          ...compactCardSx,
                        }}
                      >
                        <Box sx={{ p: { xs: 1, sm: 1.25 } }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 1 }}
                          >
                            {this.renderBrandMark({
                              width: { xs: 34, sm: 38 },
                              height: { xs: 34, sm: 38 },
                            })}

                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 900,
                                  lineHeight: 1.15,
                                }}
                                noWrap
                              >
                                {activeCategory === "All" ? this.t("skipline.menu") : activeCategory}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.25 }}
                                noWrap
                              >
                                {this.state.locationInfo?.name || this.getBrandName()}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                px: 1,
                                py: 0.5,
                                borderRadius: "8px",
                                bgcolor: this.state.locationOpenNow
                                  ? "rgba(46, 125, 50, 0.08)"
                                  : "rgba(211, 47, 47, 0.08)",
                                border: "1px solid",
                                borderColor: this.state.locationOpenNow
                                  ? "rgba(46, 125, 50, 0.22)"
                                  : "rgba(211, 47, 47, 0.22)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 900,
                                  color: this.state.locationOpenNow ? "success.main" : "error.main",
                                }}
                              >
                                {this.state.locationOpenNow ? this.t("skipline.open") : this.t("skipline.closed")}
                              </Typography>
                            </Box>

                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<LocationOnRoundedIcon fontSize="small" />}
                              onClick={this.changeLocation}
                              disabled={this.locationChangeBlocked()}
                              aria-label={this.t(this.state.orderMode === "delivery" ? "skipline.changeAddress" : "skipline.changeLocation")}
                              sx={{
                                ...pillButtonSx,
                                minWidth: { xs: 40, sm: "auto" },
                                px: { xs: 1, sm: 1.25 },
                                color: BRAND,
                                borderColor: BORDER,
                                bgcolor: "background.paper",
                                whiteSpace: "nowrap",
                                "& .MuiButton-startIcon": {
                                  mr: { xs: 0, sm: 0.5 },
                                },
                                "&:hover": {
                                  borderColor: "secondary.main",
                                  bgcolor: "rgba(15,118,110,0.06)",
                                },
                              }}
                            >
                              <Box
                                component="span"
                                sx={{ display: { xs: "none", sm: "inline" } }}
                              >
                                {this.t(this.state.orderMode === "delivery" ? "skipline.changeAddress" : "skipline.changeLocation")}
                              </Box>
                            </Button>

                            {this.renderLanguageSwitcher()}
                          </Stack>

                          <TextField
                            value={search}
                            onChange={(e) => this.setState({ search: e.target.value })}
                            placeholder={this.t("skipline.searchMenu")}
                            size="small"
                            fullWidth
                            disabled={false}
                            inputProps={{
                              "aria-label": this.t("skipline.searchMenu"),
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Search fontSize="small" />
                                </InputAdornment>
                              ),
                              endAdornment: search ? (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    aria-label={this.t("skipline.clearFilter")}
                                    onClick={() => this.setState({ search: "" })}
                                  >
                                    <Close fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ) : null,
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                bgcolor: "#fffaf3",
                              },
                            }}
                          />

                          <Tabs
                            value={Math.max(0, categories.indexOf(activeCategory))}
                            onChange={(e, idx) =>
                              this.setState({ activeCategory: categories[idx] || "All" })
                            }
                            variant="scrollable"
                            scrollButtons
                            allowScrollButtonsMobile
                            sx={{
                              mt: 1,
                              minHeight: 40,
                              "& .MuiTabs-flexContainer": {
                                gap: 0.5,
                              },
                              "& .MuiTab-root": {
                                textTransform: "none",
                                fontWeight: 900,
                                minHeight: 38,
                                px: 1.5,
                                borderRadius: "8px",
                                color: "text.primary",
                                border: "1px solid",
                                borderColor: BORDER,
                                bgcolor: "background.paper",
                              },
                              "& .MuiTab-root.Mui-selected": {
                                bgcolor: "secondary.main",
                                color: "#ffffff !important",
                                borderColor: "secondary.main",
                              },
                              "& .MuiTabs-indicator": {
                                display: "none",
                              },
                            }}
                          >
                            {categories.map((c) => (
                              <Tab key={c} label={c === "All" ? this.t("skipline.allMenu") : c} />
                            ))}
                          </Tabs>

                          {!this.state.locationOpenNow ? (
                            <Alert
                              severity="warning"
                              sx={{
                                mt: 1,
                                borderRadius: "8px",
                              }}
                            >
                              {this.state.locationReason || this.t("skipline.onlineUnavailable")}
                            </Alert>
                          ) : null}
                        </Box>
                      </Card>
                    </Container>
                  </Box>

                  <Container maxWidth="lg" sx={{ pt: { xs: 18, sm: 17 } }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 1.5 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 900,
                            letterSpacing: -0.2,
                          }}
                          noWrap
                        >
                          {activeCategory === "All" ? this.t("skipline.allProducts") : activeCategory}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          {this.tr("skipline.availableProducts", {
                            count: filteredProducts.length + filteredCombos.length,
                          })}
                        </Typography>
                      </Box>
                    </Stack>

                    {loading ? (
                      <Grid container spacing={1.5}>
                        {Array.from({ length: 10 }).map((_, i) => (
                          <Grid item xs={6} sm={4} md={3} lg={2.4} key={i}>
                            <Card
                              elevation={0}
                              sx={{
                                ...compactCardSx,
                                border: "1px solid",
                                overflow: "hidden",
                              }}
                            >
                              <CardContent sx={{ p: 1.5 }}>
                                <Skeleton
                                  variant="rounded"
                                  height={132}
                                  sx={{
                                    borderRadius: "8px",
                                    bgcolor: "rgba(254,215,170,0.34)",
                                  }}
                                />
                                <Skeleton width="80%" sx={{ mt: 1 }} />
                                <Skeleton width="45%" />
                              </CardContent>
                              <CardActions sx={{ p: 1.5, pt: 0 }}>
                                <Skeleton
                                  variant="rounded"
                                  width="100%"
                                  height={38}
                                  sx={{ borderRadius: "8px" }}
                                />
                              </CardActions>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <>
                        <Grid container spacing={1.5}>
                          {filteredProducts.map((p) => (
                            <Grid item xs={6} sm={4} md={3} lg={2.4} key={p.id}>
                              {this.renderProductCard(p)}
                            </Grid>
                          ))}

                          {filteredCombos.map((c) => (
                            <Grid item xs={6} sm={4} md={3} lg={2.4} key={c.id}>
                              {this.renderComboCard(c)}
                            </Grid>
                          ))}

                          {!filteredProducts.length && !filteredCombos.length ? (
                            <Grid item xs={12}>
                              <Alert
                                severity="info"
                                sx={{
                                  borderRadius: "8px",
                                }}
                              >
                                {this.t("skipline.noProductsCategory")}
                              </Alert>
                            </Grid>
                          ) : null}
                        </Grid>
                      </>
                    )}
                  </Container>
                </Box>
              )
            )
          )}

          {view !== 'name' && !summaryOpen && !inFlow && (
            <Box
              component="aside"
              aria-label={this.t("skipline.cart")}
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1200,
                bgcolor: "rgba(255,255,255,0.96)",
                backdropFilter: "blur(18px)",
                borderTop: "1px solid",
                borderColor: BORDER,
                pt: 1,
                pb: "max(8px, env(safe-area-inset-bottom))",
                boxShadow: "0 -12px 34px rgba(33,20,12,0.12)",
              }}
            >
              <Container
                maxWidth="lg"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      fontWeight: 700,
                      lineHeight: 1.1,
                    }}
                  >
                    {this.t("skipline.totalOrder")}
                  </Typography>

                  <Typography
                    variant="h6"
                    aria-live="polite"
                    sx={{
                      fontWeight: 900,
                      lineHeight: 1.2,
                    }}
                    noWrap
                  >
                    {money(totals.totalGross || 0, currency)}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={
                    <Badge
                      badgeContent={itemsCount}
                      color="secondary"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontWeight: 900,
                        },
                      }}
                    >
                      <ShoppingCart />
                    </Badge>
                  }
                  onClick={() => !inFlow && this.setState({ cartOpen: true })}
                  aria-label={`${this.t("skipline.viewCart")}: ${itemsCount}, ${money(totals.totalGross || 0, currency)}`}
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.15,
                    ...pillButtonSx,
                    fontWeight: 900,
                    whiteSpace: "nowrap",
                    boxShadow: "0 10px 24px rgba(247, 181, 0, 0.25)",
                  }}
                >
                  {itemsCount > 0 ? this.t("skipline.viewCart") : this.t("skipline.cart")}
                </Button>
              </Container>
            </Box>
          )}

          {!inFlow && (
            <Drawer
              anchor="right"
              open={cartOpen && view !== 'name'}
              onClose={() => this.setState({ cartOpen: false })}
              PaperProps={{
                sx: {
                  ...brandThemeCssVars(this.props.brandTheme),
                  width: { xs: "100%", sm: 440 },
                  maxWidth: "100vw",
                  bgcolor: PAGE_BG,
                },
                role: "dialog",
                "aria-modal": true,
                "aria-labelledby": "skipline-cart-title",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  bgcolor: PAGE_BG,
                }}
              >
                <Box
                  component="header"
                  sx={{
                    p: 2,
                    bgcolor: SURFACE,
                    borderBottom: "1px solid",
                    borderColor: BORDER,
                    boxShadow: "0 10px 30px rgba(33,20,12,0.06)",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "8px",
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 8px 20px rgba(247, 181, 0, 0.22)",
                        flexShrink: 0,
                      }}
                    >
                      <ShoppingCart fontSize="small" />
                    </Box>

                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography
                        id="skipline-cart-title"
                        variant="h6"
                        sx={{
                          fontWeight: 900,
                          lineHeight: 1.15,
                        }}
                      >
                        {this.t("skipline.yourOrder")}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        aria-live="polite"
                        sx={{ display: "block", mt: 0.25 }}
                      >
                        {itemsCount} {itemsCount === 1 ? this.t("skipline.itemOne") : this.t("skipline.itemMany")} {this.t("skipline.inCart")}
                      </Typography>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={() => this.setState({ cartOpen: false })}
                      aria-label={this.t("skipline.closeCart")}
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: PAGE_BG,
                        borderRadius: "12px",
                        border: "1px solid",
                        borderColor: BORDER,
                        "&:hover": {
                          bgcolor: "#fff3cc",
                        },
                        ...focusRingSx,
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
                <Box
                  component="section"
                  role="region"
                  aria-label={this.t("skipline.yourOrder")}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                    p: 1.25,
                  }}
                >
                  <List dense sx={{ p: 0 }}>
                    {Object.values(cartProducts).map((it) => {
                      const unit = fromCents(it.unitPriceC);
                      const lineTotal = unit * it.qty;

                      return (
                        <ListItem
                          key={it.id}
                          disableGutters
                          sx={{
                            ...compactCardSx,
                            mb: 1,
                            p: 1.25,
                            bgcolor: "background.paper",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                              minWidth: 0,
                              gap: 1.25,
                            }}
                          >
                            <Box
                              sx={{
                                position: "relative",
                                width: 58,
                                height: 58,
                                flexShrink: 0,
                              }}
                            >
                              <Avatar
                                variant="rounded"
                                src={it.imageSmallUrl || undefined}
                                sx={{
                                  width: 58,
                                  height: 58,
                                  borderRadius: "8px",
                                  bgcolor: "#fffaf3",
                                  border: "1px solid",
                                  borderColor: BORDER,
                                }}
                              >
                                {it.name?.[0] || "?"}
                              </Avatar>

                              {it.qty > 1 ? (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: -7,
                                    right: -7,
                                    bgcolor: "primary.main",
                                    color: "primary.contrastText",
                                    fontSize: 12,
                                    fontWeight: 900,
                                    lineHeight: 1,
                                    px: 0.7,
                                    py: 0.45,
                                    borderRadius: "8px",
                                    boxShadow: "0 5px 12px rgba(247, 181, 0, 0.25)",
                                    minWidth: 22,
                                    textAlign: "center",
                                  }}
                                >
                                  ×{it.qty}
                                </Box>
                              ) : null}
                            </Box>

                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 900,
                                  lineHeight: 1.25,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                                title={it.name}
                              >
                                {it.name}
                              </Typography>

                              <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="baseline"
                                sx={{ mt: 0.5 }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 900,
                                  }}
                                >
                                  {money(lineTotal, currency)}
                                </Typography>

                                {it.qty > 1 ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {money(unit, currency)} / {this.t("skipline.piece")}
                                  </Typography>
                                ) : null}
                              </Stack>
                            </Box>

                            <Box sx={{ flexShrink: 0 }}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                sx={{
                                  bgcolor: "#fffaf3",
                                  border: "1px solid",
                                  borderColor: BORDER,
                                  borderRadius: "8px",
                                  px: 0.4,
                                  py: 0.3,
                                }}
                              >
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => this.decProduct(it.id)}
                                  disabled={orderLocked}
                                  aria-label={`${this.t("skipline.decreaseQuantity", "Decrease quantity")} ${it.name}`}
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    ...focusRingSx,
                                  }}
                                >
                                  <Remove fontSize="small" />
                                </IconButton>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    minWidth: 26,
                                    textAlign: "center",
                                    fontWeight: 900,
                                  }}
                                >
                                  {it.qty}
                                </Typography>

                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => this.incProduct(it.id)}
                                  disabled={orderLocked}
                                  aria-label={`${this.t("skipline.increaseQuantity", "Increase quantity")} ${it.name}`}
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    ...focusRingSx,
                                  }}
                                >
                                  <Add fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Box>
                          </Box>
                        </ListItem>
                      );
                    })}

                    {Object.entries(cartCombos).map(([key, it]) => {
                      const qty = it.qty || 1;

                      // Gather all items (selected + fixed)
                      const all = [
                        ...(it.selectionDetails || []),
                        ...(it.fixedItems || []),
                      ];

                      const type1 = all.filter(x => x.priceType === 1);
                      const type2 = all.filter(x => x.priceType === 2);
                      const type0 = all.filter(x => x.priceType === 0);

                      // Per‑unit parts
                      const sum1UnitRON = type1.reduce((acc, s) => acc + (this.state.effectiveProd?.[s.productId] || 0), 0);
                      const sum2UnitRON = type2.reduce((acc, s) => acc + ((s.priceValueC || 0) / 100), 0);
                      const baseUnitRON = (it.basePriceC || 0) / 100;

                      // Allocate base → type0 (RON per unit), proportional to pricelist P0 and fix ±0.01 to the largest P0
                      const P0 = type0.map(s => this.anchorForType0(s.productId));
                      const S0 = P0.reduce((a, b) => a + b, 0);
                      let alloc0UnitRON = [];
                      if (type0.length) {
                        if (S0 <= 0) {
                          const each = Math.round((baseUnitRON / type0.length) * 100) / 100;
                          alloc0UnitRON = Array(type0.length).fill(each);
                          const drift = Math.round((baseUnitRON - alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0)) * 100)/100;
                          if (Math.abs(drift) >= 0.01) alloc0UnitRON[0] = Math.round((alloc0UnitRON[0] + drift) * 100)/100;
                        } else {
                          alloc0UnitRON = P0.map(p => Math.round((baseUnitRON * (p / S0)) * 100) / 100);
                          let sum = Math.round(alloc0UnitRON.reduce((a,b)=>Math.round((a+b)*100)/100,0) * 100)/100;
                          let rem = Math.round((baseUnitRON - sum) * 100)/100;
                          if (Math.abs(rem) >= 0.01) {
                            const idx = P0.indexOf(Math.max(...P0));
                            alloc0UnitRON[idx] = Math.round((alloc0UnitRON[idx] + rem) * 100)/100;
                          }
                        }
                      }

                      // Build breakdown rows (per line, after qty)
                      const rows = [];
                      type1.forEach((s, i) => {
                        const vUnit = this.state.effectiveProd?.[s.productId] || 0;
                        rows.push({ name: s.name, value: Math.round(vUnit*qty*100)/100 });
                      });
                      type2.forEach((s, i) => {
                        const vUnit = (s.priceValueC || 0) / 100;
                        rows.push({ name: s.name, value: Math.round(vUnit*qty*100)/100 });
                      });
                      type0.forEach((s, i) => {
                        rows.push({ name: s.name, value: Math.round(alloc0UnitRON[i]*qty*100)/100 });
                      });

                      // Final line total (must equal Σ rows)
                      const unitLineRON = baseUnitRON + sum1UnitRON + sum2UnitRON;
                      const lineTotal = Math.round(unitLineRON * qty * 100) / 100;

                      return (
                        <ListItem
                          key={key}
                          disableGutters
                          sx={{
                            ...compactCardSx,
                            mb: 1,
                            p: 1.25,
                            bgcolor: "background.paper",
                            borderColor: "secondary.main",
                            boxShadow: "0 10px 26px rgba(15, 118, 110, 0.10)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              width: "100%",
                              minWidth: 0,
                              gap: 1.25,
                            }}
                          >
                            <Box
                              sx={{
                                position: "relative",
                                width: 58,
                                height: 58,
                                flexShrink: 0,
                              }}
                            >
                              <Avatar
                                variant="rounded"
                                src={it.imageSmallUrl || undefined}
                                sx={{
                                  width: 58,
                                  height: 58,
                                  borderRadius: "8px",
                                  bgcolor: "rgba(109, 76, 65, 0.08)",
                                  border: "1px solid",
                                  borderColor: BORDER,
                                }}
                              >
                                🧩
                              </Avatar>

                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: -5,
                                  left: 2,
                                  bgcolor: "secondary.main",
                                  color: "secondary.contrastText",
                                  fontSize: 10,
                                  fontWeight: 900,
                                  px: 0.65,
                                  py: 0.25,
                                  borderRadius: "8px",
                                  boxShadow: "0 5px 12px rgba(109, 76, 65, 0.22)",
                                }}
                              >
                                {this.t("skipline.combo")}
                              </Box>

                              {it.qty > 1 ? (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: -7,
                                    right: -7,
                                    bgcolor: "primary.main",
                                    color: "primary.contrastText",
                                    fontSize: 12,
                                    fontWeight: 900,
                                    lineHeight: 1,
                                    px: 0.7,
                                    py: 0.45,
                                    borderRadius: "8px",
                                    boxShadow: "0 5px 12px rgba(247, 181, 0, 0.25)",
                                    minWidth: 22,
                                    textAlign: "center",
                                  }}
                                >
                                  ×{it.qty}
                                </Box>
                              ) : null}
                            </Box>

                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                              <Typography
                                variant="subtitle2"
                                title={it.name}
                                sx={{
                                  fontWeight: 900,
                                  lineHeight: 1.25,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {it.name}
                              </Typography>

                              <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="baseline"
                                sx={{ mt: 0.5 }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 900,
                                  }}
                                >
                                  {money(lineTotal, this.state.priceList?.currency || "RON")}
                                </Typography>

                                {qty > 1 ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {money(lineTotal / qty, this.state.priceList?.currency || "RON")} / combo
                                  </Typography>
                                ) : null}
                              </Stack>

                              {rows.length > 0 ? (
                                <Box
                                  sx={{
                                    mt: 0.75,
                                    p: 1,
                                    borderRadius: "8px",
                                    bgcolor: "#fffaf3",
                                    border: "1px solid",
                                    borderColor: BORDER,
                                  }}
                                >
                                  {rows.map((r, idx) => (
                                    <Box
                                      key={idx}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        py: 0.15,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        title={r.name}
                                        sx={{
                                          minWidth: 0,
                                          pr: 1,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {r.name}
                                      </Typography>

                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 800,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {money(r.value, this.state.priceList?.currency || "RON")}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              ) : null}
                            </Box>

                            <Box sx={{ flexShrink: 0 }}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                sx={{
                                  bgcolor: "#fffaf3",
                                  border: "1px solid",
                                  borderColor: BORDER,
                                  borderRadius: "8px",
                                  px: 0.4,
                                  py: 0.3,
                                }}
                              >
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => this.decCombo(key)}
                                  disabled={orderLocked}
                                  aria-label={`${this.t("skipline.decreaseQuantity", "Decrease quantity")} ${it.name}`}
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    ...focusRingSx,
                                  }}
                                >
                                  <Remove fontSize="small" />
                                </IconButton>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    minWidth: 26,
                                    textAlign: "center",
                                    fontWeight: 900,
                                  }}
                                >
                                  {it.qty}
                                </Typography>

                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => this.incCombo(key)}
                                  disabled={orderLocked}
                                  aria-label={`${this.t("skipline.increaseQuantity", "Increase quantity")} ${it.name}`}
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    ...focusRingSx,
                                  }}
                                >
                                  <Add fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Box>
                          </Box>
                        </ListItem>
                      );
                    })}

                    {cartIsEmpty && (
                      <Box
                        sx={{
                          ...compactCardSx,
                          p: 3,
                          textAlign: "center",
                          bgcolor: "background.paper",
                        }}
                      >
                        <Box
                          sx={{
                            width: 58,
                            height: 58,
                            borderRadius: "8px",
                            bgcolor: "rgba(247, 181, 0, 0.12)",
                            color: "primary.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mx: "auto",
                            mb: 1.5,
                          }}
                        >
                          <ShoppingCart fontSize="medium" />
                        </Box>

                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 900,
                            mb: 0.5,
                          }}
                        >
                          {this.t("skipline.emptyCart")}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.5,
                            mb: 2,
                          }}
                        >
                          {this.t("skipline.emptyCartDesc")}
                        </Typography>

                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => this.setState({ cartOpen: false })}
                          sx={{
                            ...pillButtonSx,
                            fontWeight: 900,
                            py: 1,
                          }}
                        >
                          {this.t("skipline.backToMenu")}
                        </Button>
                      </Box>
                    )}
                  </List>
                </Box>

                <Box
                  component="footer"
                  sx={{
                    p: 2,
                    pb: "max(16px, env(safe-area-inset-bottom))",
                    bgcolor: SURFACE,
                    borderTop: "1px solid",
                    borderColor: BORDER,
                    boxShadow: "0 -10px 28px rgba(33,20,12,0.10)",
                    flexShrink: 0,
                    maxHeight: "62dvh",
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                  }}
                >
                  <Stack spacing={1.25}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "8px",
                        bgcolor: "#fffaf3",
                        border: "1px solid",
                        borderColor: BORDER,
                      }}
                    >
                      {this.state.orderMode === "delivery" ? (
                        <Stack spacing={0.5} sx={{ mb: 1 }}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">{this.t("skipline.merchandiseSubtotal")}</Typography>
                            <Typography variant="body2" fontWeight={800}>{money(totals.merchandiseGross || 0, currency)}</Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">{this.t("skipline.deliveryFee")}</Typography>
                            <Typography variant="body2" fontWeight={800}>
                              {totals.deliveryFee > 0 ? money(totals.deliveryFee, currency) : this.t("skipline.deliveryFree")}
                            </Typography>
                          </Stack>
                          {totals.freeDeliveryThreshold > 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              {totals.amountRemainingForFreeDelivery > 0
                                ? this.tr("skipline.deliveryRemaining", {
                                  amount: money(totals.amountRemainingForFreeDelivery, currency),
                                })
                                : this.t("skipline.deliveryThresholdReached")}
                            </Typography>
                          ) : null}
                        </Stack>
                      ) : null}
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontWeight: 700 }}
                        >
                          {this.t("skipline.totalOrder")}
                        </Typography>

                        <Typography
                          variant="h6"
                          aria-live="polite"
                          sx={{
                            fontWeight: 900,
                            lineHeight: 1.15,
                          }}
                        >
                          {money(totals.totalGross || 0, currency)}
                        </Typography>
                      </Stack>

                      {!this.state.locationOpenNow ? (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{
                            display: "block",
                            mt: 0.75,
                            lineHeight: 1.35,
                          }}
                        >
                          {this.state.locationReason || this.t("skipline.locationUnavailable")}
                        </Typography>
                      ) : null}
                    </Box>

                    {this.state.orderMode === "delivery" && totals.totalGross <= 0 ? (
                      <Alert severity="warning">{this.t("skipline.deliveryCardOnlyPositiveTotal")}</Alert>
                    ) : null}

                    {this.state.deliveryOrderOutcomeUnknown ? (
                      <Alert severity="warning">
                        {this.t(this.state.deliveryGuestSessionExpired
                          ? "skipline.deliveryGuestSessionExpired"
                          : "skipline.deliveryOrderOutcomeUnknown")}
                      </Alert>
                    ) : null}

                    {this.state.deliveryCheckoutConflict ? (
                      <Alert severity="warning">
                        <Typography variant="body2" gutterBottom>{this.t("skipline.deliveryCheckoutConflict")}</Typography>
                        <Stack spacing={1}>
                          <Button variant="outlined" onClick={this.retryDeliveryEligibility}>
                            {this.t("skipline.deliveryTryAgain")}</Button>
                          <Button variant="outlined" onClick={this.clearCartAndChooseDeliveryAddress}>
                            {this.t("skipline.deliveryClearChooseAddress")}</Button>
                          <Button variant="outlined" onClick={() => this.switchOrderMode("pickup", true)}>
                            {this.t("skipline.deliveryClearSwitchPickup")}</Button>
                        </Stack>
                      </Alert>
                    ) : null}

                    <ClientCheckoutAuthPanel
                      enabled={
                        this.props.clientPortalEnabled &&
                        this.props.authenticatedCheckoutEnabled &&
                        this.state.orderMode !== "delivery"
                      }
                      authenticated={this.props.clientPortalAuthenticated}
                      displayName={this.props.clientProfile?.displayName}
                      locked={
                        orderLocked ||
                        placing ||
                        this.isClientPortalBusy()
                      }
                      onLogin={this.props.loginClient}
                      onLogout={this.logoutClientBeforeOrder}
                      onRefresh={this.props.refreshAfterOrderOutcome}
                      t={this.t}
                      brandTheme={this.props.brandTheme}
                    />

                    {this.props.authenticatedCheckoutEnabled &&
                    this.props.clientPortalAuthenticated ? (
                      <Alert
                        severity={
                          this.props.clientProfile?.hasOrderEmail === true
                            ? "info"
                            : "error"
                        }
                        variant="outlined"
                      >
                        <Typography variant="body2" fontWeight={900}>
                          {this.t("skipline.clientCheckoutActive")}
                        </Typography>
                        <Typography variant="caption" component="p">
                          {this.t(
                            this.props.clientProfile?.hasOrderEmail === true
                              ? "skipline.clientBaseOrderHelp"
                              : "skipline.clientAccountEmailMissing"
                          )}
                        </Typography>
                      </Alert>
                    ) : null}

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={this.clearCart}
                        disabled={!hasLines || orderLocked || this.state.deliveryOrderOutcomeUnknown}
                        sx={{
                          ...pillButtonSx,
                          fontWeight: 900,
                          py: 1.1,
                          minHeight: 48,
                        }}
                      >
                        {this.t("skipline.clearCart")}
                      </Button>

                      <Button
                        variant="contained"
                        endIcon={!placing ? <Send /> : null}
                        fullWidth
                        onClick={
                          retryPaymentAvailable
                            ? this.retryPaymentRegistration
                            : this.checkout
                        }
                        disabled={checkoutDisabled}
                        aria-busy={placing}
                        sx={{
                          ...pillButtonSx,
                          fontWeight: 900,
                          py: 1.1,
                          minHeight: 50,
                          boxShadow: !checkoutDisabled
                            ? "0 10px 24px rgba(247, 181, 0, 0.25)"
                            : "none",
                        }}
                      >
                        {placing ? (
                          <CircularProgress size={20} sx={{ color: "white" }} />
                        ) : (
                          retryPaymentAvailable
                            ? this.t("skipline.retryPaymentRegistration")
                            : this.t("skipline.checkout")
                        )}
                      </Button>
                    </Stack>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        textAlign: "center",
                        lineHeight: 1.35,
                      }}
                    >
                      {this.tr("skipline.securePayment", { provider: this.state.paymentProvider })}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Drawer>
          )}

          <ComboPickerSheet
            open={this.state.pickerOpen}
            combo={this.state.pickerCombo}
            productsPool={this.state.productsAll || this.state.products}
            priceList={priceList}
            promoByProductId={this.state.promoByProductId}   // <— pass it down
            promoByComboId={this.state.promoByComboId}   // <— pass it down
            deliveryMode={this.state.orderMode === "delivery"}
            onClose={()=>this.setState({ pickerOpen:false, pickerCombo:null })}
            onConfirm={this.confirmComboFromPicker}
            t={this.t}
            brandTheme={this.props.brandTheme}
          />
          <ProductInfoModal
            open={Boolean(this.state.infoProduct)}
            onClose={() => this.setState({ infoProduct: null })}
            product={this.state.infoProduct}
            categoryName={this.state.infoProduct?.categoryName || ""}
            brandTheme={this.props.brandTheme}
            allergens={this.props.getProductAllergens?.(this.state.infoProduct) || []}
            t={this.t}
          />
          <PaymentSheet
            open={this.state.paymentSheetOpen}
            payUrl={this.state.payUrl}
            message={this.state.paymentMessage}
            checking={this.state.paymentChecking}
            iframeError={this.state.payIframeError}
            paymentProvider={this.state.paymentProvider}
            brandName={this.getBrandName()}
            registrationFailed={this.state.paymentRegistrationFailed}
            registering={this.state.paymentRegistering}
            clientSessionRequired={this.state.clientSessionRequired}
            onOpenPayU={this.openPayU}
            onManualCheck={this.checkOrderStatus}
            onCancelOrder={this.cancelCurrentOrder}
            onRetryRegistration={this.retryPaymentRegistration}
            onClientLogin={this.restoreClientSession}
            onClose={
              !this.state.lastOrderId && !this.suspendedClientUnknownOwnerId &&
                !this.state.deliveryOrderOutcomeUnknown
                ? () =>
                    this.setState({
                      paymentSheetOpen: false,
                      clientSessionRequired: false,
                    })
                : undefined
            }
            t={this.t}
            brandTheme={this.props.brandTheme}
          />

          <Snackbar open={!!error} autoHideDuration={6000} onClose={()=>this.setState({ error:"" })}><Alert onClose={()=>this.setState({ error:"" })} severity="error" variant="filled">{error}</Alert></Snackbar>
        </Box>
      </ThemeProvider>
    );
  }
}

export default function SkipTheLinePageWithStore(props) {
  const {
    logoUrl,
    bannerUrl,
    brandName,
    brandTheme,
    publicConfigReady,
    publicImagesReady,
    languages,
    selectedLanguageIref,
    selectedLanguage,
    defaultLanguage,
    languageCode,
    setSelectedLanguageIref,
    getProductAllergens,
    t,
  } = useDataStore();
  const clientPortal = useClientPortal();

  return (
    <SkipTheLinePage
      {...props}
      logoUrl={logoUrl}
      bannerUrl={bannerUrl}
      brandName={brandName}
      brandTheme={brandTheme}
      publicConfigReady={publicConfigReady}
      publicImagesReady={publicImagesReady}
      languages={languages}
      selectedLanguageIref={selectedLanguageIref}
      selectedLanguage={selectedLanguage}
      defaultLanguage={defaultLanguage}
      languageCode={languageCode}
      setSelectedLanguageIref={setSelectedLanguageIref}
      getProductAllergens={getProductAllergens}
      t={t}
      clientPortalAuthenticated={clientPortal.isAuthenticated}
      clientPortalEnabled={clientPortal.enabled}
      clientPortalRestoring={clientPortal.isRestoring}
      clientPortalBusy={
        clientPortal.status === "restoring" ||
        clientPortal.status === "authenticating"
      }
      authenticatedCheckoutEnabled={
        clientPortal.authenticatedCheckoutEnabled
      }
      clientProfile={clientPortal.profile}
      loadSkiplineDeliveryAddresses={clientPortal.loadSkiplineDeliveryAddresses}
      resolveAuthenticatedSkiplineDelivery={clientPortal.resolveAuthenticatedSkiplineDelivery}
      saveSkiplineDeliveryAddress={clientPortal.saveSkiplineDeliveryAddress}
      deleteSkiplineDeliveryAddress={clientPortal.deleteSkiplineDeliveryAddress}
      addAuthenticatedOrder={clientPortal.addAuthenticatedOrder}
      registerAuthenticatedPay={clientPortal.registerAuthenticatedPay}
      getAuthenticatedPayStatus={clientPortal.getAuthenticatedPayStatus}
      cancelAuthenticatedOrder={clientPortal.cancelAuthenticatedOrder}
      refreshAfterOrderOutcome={clientPortal.refreshAfterOrderOutcome}
      loginClient={clientPortal.login}
      logoutClient={clientPortal.logout}
    />
  );
}
